import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { toast } from "react-hot-toast";
import HttpConfigTabs from "./HttpConfigTabs";
import WeeklyScheduleDialog from "./schedule/WeeklyScheduleDialog";
import {
  triggersAPI,
  useCasesAPI,
  camerasAPI,
  notificationsAPI,
  alertRulesAPI,
} from "../../services/api";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Breadcrumbs,
  Link,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  Popover,
  Tabs,
  Tab,
  Menu,
  Chip,
  TablePagination,
  TableSortLabel,
  ListItemIcon,
  Tooltip,
  CircularProgress,
  Stack,
} from "@mui/material";
import {
  Search as SearchIcon,
  CalendarMonth as CalendarIcon,
  Info as InfoIcon,
  InfoOutlined as InfoOutlinedIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Close as CloseIcon,
  Dashboard as DashboardIcon,
  History as HistoryIcon,
  Notifications as NotificationsIcon,
  AttachFile as AttachFileIcon,
  Phone as PhoneIcon,
  FormatBold as FormatBoldIcon,
  FormatItalic as FormatItalicIcon,
  FormatUnderlined as FormatUnderlinedIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from "@mui/icons-material";

// Types

// Cooldown interval default constants (fallback if not provided via props)
const DEFAULT_COOLDOWN_MIN = 0;
const DEFAULT_COOLDOWN_MAX = 3600;

/** Shown when hovering the row or while the field has focus (including Select open). */
const ALERT_NAME_INFO_TOOLTIP =
  "Unique name for this alert rule. Use a clear label so it is easy to find in lists and history.";

const ALERT_TYPE_INFO_TOOLTIP =
  "Category of alert from your use cases. This often drives which detections or events can trigger the rule.";

const SOURCE_INFO_TOOLTIP =
  "Where events come from: AI (cameras and zones), IoT devices, or system signals. Pick the channel that matches your scenario.";

const SUB_SOURCE_INFO_TOOLTIP =
  "Narrow the rule to specific cameras and zones, devices, or system sources. Open search to select one or more.";

const SEVERITY_INFO_TOOLTIP =
  "Priority shown when an alert fires: Critical, High, Medium, or Low.";

const SCHEDULE_INFO_TOOLTIP =
  "Times and days when this rule is active. Outside the schedule, matching events will not trigger notifications.";

const COOLDOWN_INFO_TOOLTIP =
  "Minimum seconds between repeated triggers for the same condition. Prevents notification spam while staying within the allowed range.";

/** True if value looks like a use case UUID (rules API expects this as `alert_type`). */
function isProbableAlertUsecaseId(value) {
  const s = String(value ?? "").trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    s,
  );
}

/** Normalize GET /use-cases/ JSON to an array of items. */
function extractUseCasesFromAlertApi(body) {
  if (Array.isArray(body)) return body;
  if (body && typeof body === "object") {
    const arr = body.data ?? body.results ?? body.Data;
    if (Array.isArray(arr)) return arr;
  }
  return [];
}

function extractCamerasArray(body) {
  if (Array.isArray(body)) return body;
  if (body && typeof body === "object") {
    const arr = body.data ?? body.results ?? body.Data;
    if (Array.isArray(arr)) return arr;
  }
  return [];
}

function extractZonesArray(body) {
  if (Array.isArray(body)) return body;
  if (body && typeof body === "object") {
    const arr = body.data ?? body.results ?? body.Data;
    if (Array.isArray(arr)) return arr;
  }
  return [];
}

/**
 * Camera id + zone label in selectedSources. UUIDs contain `-`, so we use `@@`.
 * Legacy mock entries used `cam1-ROI 01` (first `-`); still supported when `@@` is absent.
 */
const CAMERA_ZONE_ENTRY_SEP = "@@";

/** Standard 8-4-4-4-12 hex UUID at the start of a string (camera / zone ids from API). */
const LEADING_FULL_UUID_RE =
  /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})(?:-(.+))?$/;

function splitCameraZoneEntry(entry) {
  if (entry == null || String(entry) === "") {
    return { cameraId: "", zonePart: null };
  }
  const s = String(entry).trim();
  const sepIdx = s.indexOf(CAMERA_ZONE_ENTRY_SEP);
  if (sepIdx !== -1) {
    return {
      cameraId: s.slice(0, sepIdx).trim(),
      zonePart: s.slice(sepIdx + CAMERA_ZONE_ENTRY_SEP.length).trim() || null,
    };
  }
  const uuidSplit = s.match(LEADING_FULL_UUID_RE);
  if (uuidSplit) {
    const cameraId = uuidSplit[1];
    const tail = uuidSplit[2];
    return {
      cameraId,
      zonePart: tail != null && tail !== "" ? tail : null,
    };
  }
  const dash = s.indexOf("-");
  if (dash !== -1) {
    return {
      cameraId: s.slice(0, dash),
      zonePart: s.slice(dash + 1) || null,
    };
  }
  return { cameraId: s, zonePart: null };
}

/** Canonical id for zone `Select` values (MUI toggles with `indexOf` — types must match). */
function normalizeZoneOptionId(id) {
  return String(id ?? "").trim();
}

/** Zones for Select: API `{ zones: [{ id, name }] }` or legacy `{ rois: string[] }`. */
function getCameraZonesList(source) {
  if (!source) return [];
  if (Array.isArray(source.zones) && source.zones.length > 0) {
    return source.zones
      .map((z) => {
        if (z == null || typeof z !== "object") return null;
        const id = normalizeZoneOptionId(z.id);
        const name =
          String(z.name ?? "").trim() || id || "Zone";
        if (!id) return null;
        return { id, name };
      })
      .filter(Boolean);
  }
  const rois = source.rois;
  if (Array.isArray(rois) && rois.length > 0) {
    return rois.map((r) => {
      const id = normalizeZoneOptionId(r);
      return { id, name: id };
    });
  }
  return [];
}

/** Label for the zone picker field (closed or open). */
function formatAiCameraZoneFieldLabel(source, zoneIds) {
  const ids = (zoneIds || []).map(normalizeZoneOptionId).filter(Boolean);
  if (ids.length === 0) return "";
  const opts = getCameraZonesList(source);
  return ids
    .map(
      (sid) =>
        opts.find((o) => normalizeZoneOptionId(o.id) === sid)?.name || sid,
    )
    .join(", ");
}

/** Build POST /rules/ `source_ids` and `roi_ids` from Sub Source selections. */
function buildRulesApiSourcePayload(selectedSources, sourceCategory) {
  if (sourceCategory === "AI") {
    const sourceIds = new Set();
    const roiIds = new Set();
    for (const entry of selectedSources) {
      const { cameraId, zonePart } = splitCameraZoneEntry(entry);
      if (cameraId) sourceIds.add(cameraId);
      if (zonePart) roiIds.add(zonePart);
    }
    return {
      source_ids: Array.from(sourceIds),
      roi_ids: Array.from(roiIds),
    };
  }
  return {
    source_ids: selectedSources.filter(Boolean).map(String),
    roi_ids: [],
  };
}

/** Gmail-like compose: flat rows, thin horizontal dividers, label left. */
const GMAIL_COMPOSE_OUTER_SX = {
  border: "1px solid #dadce0",
  borderRadius: "8px",
  overflow: "hidden",
  backgroundColor: "#fff",
  mb: 2,
};

const GMAIL_COMPOSE_LABEL_SX = {
  flex: "0 0 76px",
  fontSize: "14px",
  color: "#5f6368",
  fontFamily: "Roboto, Helvetica, Arial, sans-serif",
  fontWeight: 400,
  lineHeight: "20px",
  alignSelf: "center",
};

const gmailComposeRowSx = (showBottomRule) => ({
  display: "flex",
  alignItems: "stretch",
  minHeight: 44,
  px: { xs: 1.25, sm: 1.75 },
  py: 0.25,
  borderBottom: showBottomRule ? "1px solid #e8eaef" : "none",
  gap: 1.5,
});

const gmailComposeTextFieldSx = {
  flex: 1,
  minWidth: 0,
  "& .MuiInputBase-root": { marginTop: 0, marginBottom: 0 },
  "& .MuiInputBase-input": {
    py: 1.125,
    px: 0,
    fontSize: "14px",
    fontFamily: "Roboto, Helvetica, Arial, sans-serif",
    color: "#202124",
  },
  "& .MuiInput-underline:before, & .MuiInput-underline:after": {
    display: "none",
  },
};

const gmailComposeLinkSx = {
  fontSize: "13px",
  fontFamily: "Roboto, Helvetica, Arial, sans-serif",
  fontWeight: 500,
  color: "#1a73e8",
  cursor: "pointer",
  border: "none",
  background: "none",
  padding: "6px 4px",
  textDecoration: "none",
  "&:hover": { textDecoration: "underline" },
};

/** Define Rule card — row + label + white outlined controls (matches Alert Name). */
const DEFINE_RULE_ROW_SX = {
  mb: 2,
  // borderTop: "1px solid #eceff1",
  // py: 1.25,
  px: 0.5,
  borderRadius: "8px",
  transition: "background-color 0.15s ease",
  "&:hover": { bgcolor: "rgba(0, 0, 0, 0.02)" },
};

const DEFINE_RULE_LABEL_SX = {
  fontSize: "14px",
  fontWeight: 500,
  color: "#37474f",
  letterSpacing: "0.13px",
};

const DEFINE_RULE_OUTLINED_FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#ffffff",
    "& fieldset": { borderColor: "#e0e0e0" },
    "&:hover fieldset": { borderColor: "#bdbdbd" },
    "&.Mui-focused fieldset": {
      borderColor: "#2932E5",
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-input": {
    fontSize: "14px",
    color: "#212121",
    padding: "12px 16px",
    "&::placeholder": { color: "#a0a0a0", opacity: 1 },
  },
  "& .MuiSelect-select": {
    fontSize: "14px",
    color: "#212121",
    padding: "12px 16px",
  },
  "& .MuiSelect-select em": {
    color: "#a0a0a0",
    fontStyle: "normal",
  },
};

const DEFINE_RULE_PICKER_BUTTON_SX = {
  backgroundColor: "#ffffff",
  borderRadius: "10px",
  border: "1px solid #e0e0e0",
  padding: "12px 16px",
  justifyContent: "space-between",
  textTransform: "none",
  fontSize: "14px",
  fontWeight: 400,
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    borderColor: "#bdbdbd",
  },
};

/** Row wrapper: reveals `.define-rule-field-info-btn` on row hover or when any child has focus. */
const DEFINE_RULE_ROW_WITH_INFO_SX = {
  ...DEFINE_RULE_ROW_SX,
  "&:hover .define-rule-field-info-btn, &:focus-within .define-rule-field-info-btn":
    {
      opacity: 1,
      visibility: "visible",
      pointerEvents: "auto",
    },
};

const DEFINE_RULE_FIELD_INFO_ICON_BTN_SX = {
  p: 0.25,
  ml: -0.25,
  minWidth: 28,
  minHeight: 28,
  color: "#455a64",
  opacity: 0,
  visibility: "hidden",
  pointerEvents: "none",
  transition: "opacity 0.18s ease, visibility 0.18s ease, color 0.15s ease",
  borderRadius: "50%",
  "&:hover": {
    color: "#2932E5",
    bgcolor: "transparent",
  },
};

/**
 * Info icon for Define Rule fields: hidden until row hover or field focus (see DEFINE_RULE_ROW_WITH_INFO_SX).
 * @param {{ title: string; ariaLabel: string }} props
 */
function DefineRuleFieldInfo({ title, ariaLabel }) {
  return (
    <Tooltip
      title={title}
      arrow
      placement="top"
      enterTouchDelay={0}
      describeChild
    >
      <IconButton
        className="define-rule-field-info-btn"
        size="small"
        tabIndex={-1}
        aria-label={ariaLabel}
        sx={DEFINE_RULE_FIELD_INFO_ICON_BTN_SX}
      >
        <InfoOutlinedIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Tooltip>
  );
}

/** Python str.format token for a placeholder key (e.g. alert_name → {alert_name}). */
function formatMessagePlaceholderToken(key) {
  const k = String(key ?? "").trim();
  return k ? `{${k}}` : "";
}

export function AddAlertRule({
  onCancel,
  onSubmit,
  onAdd,
  onUpdate,
  editingAlert,
  /** API rule id for `PUT /rules/:id` (activate/deactivate); prefer over `editingAlert.id` when set. */
  ruleApiId = null,
  /** After a successful `PUT /rules/:id` `{ is_enabled }`, sync parent list / header state. */
  onRuleActivationUpdated,
  onScheduleChange,
  cooldownMin = DEFAULT_COOLDOWN_MIN,
  cooldownMax = DEFAULT_COOLDOWN_MAX,
  /** Read-only mode: same layout as add/edit, no saves (row menu View). */
  viewOnly = false,
  /** After loading this rule API id into the form, auto-open the Sub Source dialog once (e.g. table link). */
  pendingSubSourceBrowseRuleApiId = null,
  onPendingSubSourceBrowseConsumed,
}) {
  /** View rule: Sub Source dialog is browse-only (no toggles / apply). */
  const subSourceDialogReadOnly = Boolean(viewOnly);
  // Cooldown interval constants from props (fetched from API)
  const COOLDOWN_MIN = cooldownMin;
  const COOLDOWN_MAX = cooldownMax;
  const COOLDOWN_DEFAULT = COOLDOWN_MIN;
  // Form States
  const [alertName, setAlertName] = useState("");
  const [alertType, setAlertType] = useState("");
  /** { id, name } from GET /use-cases/ for Alert Type dropdown */
  const [useCaseOptions, setUseCaseOptions] = useState([]);
  const [useCasesLoading, setUseCasesLoading] = useState(false);
  const [useCasesError, setUseCasesError] = useState(null);
  const [selectedSources, setSelectedSources] = useState([]);
  /** AI | IoT | System — drives Sub Source UI */
  const [sourceCategory, setSourceCategory] = useState("");
  const [severity, setSeverity] = useState("");
  const [schedule, setSchedule] = useState(null);
  const [countdownInterval, setCountdownInterval] = useState(
    COOLDOWN_DEFAULT.toString(),
  );

  // Action Cards State
  const [actionCards, setActionCards] = useState([
    {
      id: "1",
      type: "",
      messageBody: "",
      notifiedTo: "",
    },
  ]);
  // Formatting state for email editors (track active formatting per editor)
  const [formattingState, setFormattingState] = useState({});
  const [editDialogFormattingState, setEditDialogFormattingState] = useState({
    bold: false,
    italic: false,
    underline: false,
  });
  const [messagePlaceholdersCatalog, setMessagePlaceholdersCatalog] =
    useState(null);
  const [messagePlaceholdersLoading, setMessagePlaceholdersLoading] =
    useState(true);

  // Function to update formatting state for a specific editor
  const updateFormattingState = (editorId) => {
    const editor = document.querySelector(
      `[data-email-editor-id="${editorId}"]`,
    );
    if (editor) {
      try {
        const bold = document.queryCommandState("bold");
        const italic = document.queryCommandState("italic");
        const underline = document.queryCommandState("underline");
        setFormattingState((prev) => ({
          ...prev,
          [editorId]: { bold, italic, underline },
        }));
      } catch (e) {
        // Fallback if queryCommandState fails
      }
    }
  };

  // Function to update formatting state for edit dialog
  const updateEditDialogFormattingState = () => {
    const editor = document.querySelector(
      `[data-email-editor-id="edit-dialog"]`,
    );
    if (editor) {
      try {
        const bold = document.queryCommandState("bold");
        const italic = document.queryCommandState("italic");
        const underline = document.queryCommandState("underline");
        setEditDialogFormattingState({ bold, italic, underline });
      } catch (e) {
        // Fallback if queryCommandState fails
      }
    }
  };
  // Email validation errors for action cards
  const [emailErrors, setEmailErrors] = useState({}); // { cardId: { email: "", cc: "", bcc: "" } }
  // SMS validation errors for action cards
  const [smsErrors, setSmsErrors] = useState({}); // { cardId: { phoneNumber: "", messageBody: "" } }
  // Phone validation errors for action cards
  const [phoneErrors, setPhoneErrors] = useState({}); // { cardId: { phoneNumber: "" } }
  // HTTP validation errors for action cards
  const [httpErrors, setHttpErrors] = useState({}); // { cardId: { url: "" } }
  const [completedActions, setCompletedActions] = useState([]);
  /** Server-side triggers for the currently edited rule (for duplicate prevention). */
  const [serverRuleTriggerKeyById, setServerRuleTriggerKeyById] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  /** 0 = Set Action composer, 1 = Action list table (matches reference tabs). */
  const [rightActionTab, setRightActionTab] = useState(0);
  const [viewActionsPage, setViewActionsPage] = useState(0);
  const [viewActionsRowsPerPage, setViewActionsRowsPerPage] = useState(10);
  const [viewActionsOrderBy, setViewActionsOrderBy] = useState("notifiedTo");
  const [viewActionsOrder, setViewActionsOrder] = useState("asc");
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedActionForMenu, setSelectedActionForMenu] = useState(null);
  const [editingTriggerId, setEditingTriggerId] = useState(null); // Track which trigger is being edited
  const [editActionDialogOpen, setEditActionDialogOpen] = useState(false); // Dialog state for editing action
  const [editingActionCard, setEditingActionCard] = useState(null); // Card data for editing in dialog
  const [editingActionId, setEditingActionId] = useState(null); // Store the action ID being edited
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false); // Confirmation dialog for delete
  const [actionToDelete, setActionToDelete] = useState(null); // Store the action to delete (persists after menu closes)

  /** Gmail-style: Cc/Bcc rows hidden until link clicked ({ [cardId]: { cc, bcc } }). */
  const [emailCcBccVisible, setEmailCcBccVisible] = useState({});
  const [editDialogEmailCcVisible, setEditDialogEmailCcVisible] =
    useState(false);
  const [editDialogEmailBccVisible, setEditDialogEmailBccVisible] =
    useState(false);

  // Dialog States
  const [sourceDialogOpen, setSourceDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);

  // Source Dialog State
  const [sourceSearchQuery, setSourceSearchQuery] = useState("");
  const [tempSelectedSources, setTempSelectedSources] = useState([]);
  const [cameraROISelections, setCameraROISelections] = useState({});
  /** Zone multi-select in source dialog: draft until Apply; Cancel/backdrop discards. */
  const [openRoiCameraId, setOpenRoiCameraId] = useState(null);
  const [roiDraftZones, setRoiDraftZones] = useState([]);
  /** Anchor for the per-camera zone `Popover` (replaces buggy `Select` multi + checkboxes). */
  const [roiZoneMenuAnchorEl, setRoiZoneMenuAnchorEl] = useState(null);
  /** Filter zone list inside the open camera zone picker `Popover`. */
  const [roiZoneMenuSearch, setRoiZoneMenuSearch] = useState("");
  /** Cameras from GET /cameras when Source = AI; entries { id, name, type: 'camera', rois } */
  const [aiCameras, setAiCameras] = useState([]);
  const [aiCamerasLoading, setAiCamerasLoading] = useState(false);
  const [aiCamerasError, setAiCamerasError] = useState(null);
  const [aiCameraZonesLoading, setAiCameraZonesLoading] = useState({});

  // Schedule Dialog State
  const [scheduleType, setScheduleType] = useState("forever"); // forever, custom, recurring
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");
  const [recurringDays, setRecurringDays] = useState([]);

  useEffect(() => {
    if (!viewOnly) return;
    setRightActionTab(1);
    setSourceDialogOpen(false);
    setScheduleDialogOpen(false);
    setActionMenuAnchor(null);
    setEditActionDialogOpen(false);
  }, [viewOnly]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setMessagePlaceholdersLoading(true);
        const res = await notificationsAPI.getMessagePlaceholders();
        if (cancelled) return;
        if (res?.success && res.data && typeof res.data === "object") {
          setMessagePlaceholdersCatalog(res.data);
        } else {
          console.warn("Unexpected message-placeholders response:", res);
          setMessagePlaceholdersCatalog(null);
        }
      } catch (err) {
        console.error("Failed to load message placeholders:", err);
        if (!cancelled) {
          setMessagePlaceholdersCatalog(null);
          toast.error(
            err?.message || "Could not load message placeholders. Try again.",
          );
        }
      } finally {
        if (!cancelled) setMessagePlaceholdersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Mock source data - camera sources show 5 ROIs when Source is selected
  const sourceData = [
    {
      id: "cam1",
      name: "Camera 01",
      type: "camera",
      rois: ["ROI 01", "ROI 02", "ROI 03", "ROI 04", "ROI 05"],
    },
    {
      id: "cam2",
      name: "Camera 02",
      type: "camera",
      rois: ["ROI 01", "ROI 02", "ROI 03", "ROI 04", "ROI 05"],
    },
    {
      id: "cam3",
      name: "Camera 03",
      type: "camera",
      rois: ["ROI 01", "ROI 02", "ROI 03", "ROI 04", "ROI 05"],
    },
    {
      id: "iot1",
      name: "IoT Device 01",
      type: "iot",
    },
    {
      id: "iot2",
      name: "IoT Device 02",
      type: "iot",
    },
    {
      id: "iot3",
      name: "Environmental Sensor A",
      type: "iot",
    },
    {
      id: "sys1",
      name: "System Monitor",
      type: "system",
    },
    {
      id: "sys2",
      name: "Server Health",
      type: "system",
    },
  ];

  /** Map mock source row `type` to Source dropdown: AI | IoT | System */
  const mapTypeToSourceCategory = (type) => {
    if (type === "camera") return "AI";
    if (type === "iot") return "IoT";
    if (type === "system") return "System";
    return "";
  };

  const inferSourceCategoryFromIds = (selectedIds) => {
    if (!selectedIds?.length) return "";
    for (const rawId of selectedIds) {
      const { cameraId: baseId } = splitCameraZoneEntry(rawId);
      const src =
        aiCameras.find((s) => s.id === baseId) ||
        sourceData.find((s) => s.id === baseId);
      if (src) return mapTypeToSourceCategory(src.type);
    }
    return "";
  };

  const rebuildCameraRoiSelectionsFromIds = (selectedIds) => {
    const next = {};
    if (!selectedIds?.length) return next;
    for (const id of selectedIds) {
      const { cameraId: camId, zonePart: roi } = splitCameraZoneEntry(id);
      if (!roi) continue;
      const cam =
        aiCameras.find((s) => s.id === camId && s.type === "camera") ||
        sourceData.find((s) => s.id === camId && s.type === "camera");
      if (!cam || !roi) continue;
      if (!next[camId]) next[camId] = [];
      if (!next[camId].includes(roi)) next[camId].push(roi);
    }
    return next;
  };

  const handleAddActionCard = () => {
    const newCard = {
      id: Date.now().toString(),
      type: "",
      messageBody: "",
      notifiedTo: "",
    };
    setActionCards([...actionCards, newCard]);
  };

  /**
   * Optional comma-separated names (Notified to) as top-level `notified_to` on the trigger payload.
   */
  const applyNotifiedToToTriggerPayload = (triggerData, card) => {
    if (!triggerData || !card) return;
    const t = card.notifiedTo;
    if (t != null && String(t).trim() !== "") {
      triggerData.notified_to = String(t).trim();
    }
  };
  const handleRemoveActionCard = (id) => {
    if (actionCards.length > 1) {
      setActionCards(actionCards.filter((card) => card.id !== id));
    }
  };

  /** Plain multiline default for Telegram, HTTP body, Phone, etc. */
  const getDefaultPlainActionMessageBody = () =>
    `Alert: {alert_name}
Severity: {severity}
Use case: {alert_type_name}
Count: {count}
Camera: {camera_name}
Zone: {zone_name}
Time: {timestamp}`;

  /** Email uses contentEditable (HTML); block tags so each line shows like SMS. */
  const getDefaultEmailMessageBody = () =>
    `<div>Alert: {alert_name}</div><div>Severity: {severity}</div><div>Use case: {alert_type_name}</div><div>Count: {count}</div><div>Camera: {camera_name}</div><div>Zone: {zone_name}</div><div>Time: {timestamp}</div>`;

  /** Shorter default for SMS (160-char limit); no Detections line. */
  const getDefaultSmsMessageBody = () =>
    `Alert: {alert_name}
Severity: {severity}
Use case: {alert_type_name}
Count: {count}
Camera: {camera_name}
Zone: {zone_name}
Time: {timestamp}`;

  /**
   * Empty composer row after Add Action or when action type is cleared.
   * @param {boolean} keepNotifiedTo - keep "Notified to" when only clearing type (optional).
   */
  const buildEmptyComposerCard = (prevCard, keepNotifiedTo = false) => ({
    id: prevCard.id,
    type: "",
    messageBody: "",
    notifiedTo: keepNotifiedTo ? (prevCard.notifiedTo ?? "") : "",
    email: "",
    subject: "",
    cc: "",
    bcc: "",
    chatId: "",
    countryCode: "+1",
    httpMethod: "POST",
    httpTab: 0,
    httpParamKey: "",
    httpParamValue: "",
    authType: "No Auth",
    authAccount: "",
    authPassword: "",
    authToken: "",
    httpHeaderKey: "",
    httpHeaderValue: "",
  });

  /**
   * When notification type changes, replace channel-specific fields in one update
   * so the template matches Email / SMS / Telegram / HTTP / Phone immediately.
   */
  const buildCardStateForActionType = (prevCard, newType) => ({
    id: prevCard.id,
    type: newType,
    notifiedTo: prevCard.notifiedTo ?? "",
    messageBody:
      newType === "SMS"
        ? getDefaultSmsMessageBody()
        : newType === "Email"
          ? getDefaultEmailMessageBody()
          : getDefaultPlainActionMessageBody(),
    email: "",
    subject: "",
    cc: "",
    bcc: "",
    chatId: "",
    countryCode: "+1",
    httpMethod: newType === "HTTP" ? "POST" : "GET",
    httpTab: 0,
    httpParamKey: "",
    httpParamValue: "",
    authType: "No Auth",
    authAccount: "",
    authPassword: "",
    authToken: "",
    httpHeaderKey: "",
    httpHeaderValue: "",
  });

  const clearActionCardErrors = (cardId) => {
    setEmailErrors((p) => {
      const n = { ...p };
      delete n[cardId];
      return n;
    });
    setSmsErrors((p) => {
      const n = { ...p };
      delete n[cardId];
      return n;
    });
    setPhoneErrors((p) => {
      const n = { ...p };
      delete n[cardId];
      return n;
    });
    setHttpErrors((p) => {
      const n = { ...p };
      delete n[cardId];
      return n;
    });
    setEmailCcBccVisible((p) => {
      const n = { ...p };
      delete n[cardId];
      return n;
    });
  };

  const plainMessageTextFromHtml = (html) => {
    if (!html || typeof html !== "string") return "";
    const d = document.createElement("div");
    d.innerHTML = html;
    return d.innerText || d.textContent || "";
  };

  const plainTextLengthFromMessageHtml = (html) =>
    plainMessageTextFromHtml(html).length;

  /** Plain text / newlines → HTML for SMS/Telegram contentEditable (same as Email). */
  const messageBodyToEditorHtml = (raw) => {
    if (raw == null || raw === "") return "";
    const str = String(raw);
    const trimmed = str.trim();
    if (trimmed && /<[a-z][\s\S]*?>/i.test(str)) return str;
    const esc = (t) =>
      t
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    return str.split("\n").map((line) => `<div>${esc(line)}</div>`).join("");
  };

  const handleActionCardChange = (id, field, value) => {
    if (field === "type") {
      setActionCards((prev) =>
        prev.map((c) => {
          if (c.id !== id) return c;
          if (!value) return buildEmptyComposerCard(c, true);
          return buildCardStateForActionType(c, value);
        }),
      );
      clearActionCardErrors(id);
      return;
    }

    // Find the card to check its type (for validation side-effects)
    const card = actionCards.find((c) => c.id === id);

    setActionCards((prev) =>
      prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)),
    );

    // Clear error when user starts typing in email fields
    if (field === "email" || field === "cc" || field === "bcc") {
      setEmailErrors((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          [field]: "",
        },
      }));
    }

    if (field === "messageBody" && card?.type === "SMS") {
      const len = plainTextLengthFromMessageHtml(String(value));
      if (len > 160) {
        setSmsErrors((prev) => ({
          ...prev,
          [id]: {
            ...prev[id],
            messageBody: "Message body must be 160 characters or less",
          },
        }));
      } else {
        setSmsErrors((prev) => ({
          ...prev,
          [id]: {
            ...prev[id],
            messageBody: "",
          },
        }));
      }
    }

    if (field === "chatId" && card && card.type === "SMS") {
      setSmsErrors((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          phoneNumber: "",
        },
      }));
    }

    // Clear error when user starts typing in Phone fields
    if (field === "chatId" && card && card.type === "Phone") {
      setPhoneErrors((prev) => ({
        ...prev,
        [id]: {
          phoneNumber: "",
        },
      }));
    }

    // Clear error when user starts typing in HTTP fields
    if (field === "chatId" && card && card.type === "HTTP") {
      setHttpErrors((prev) => ({
        ...prev,
        [id]: {
          url: "",
        },
      }));
    }
  };

  // Handle editing action card in dialog - clear HTTP errors
  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const validateEmailAddresses = (emailString) => {
    if (!emailString || !emailString.trim()) {
      return { isValid: false, error: "Email address is required" };
    }

    const emails = emailString
      .split(",")
      .map((email) => email.trim())
      .filter((email) => email.length > 0);

    if (emails.length === 0) {
      return {
        isValid: false,
        error: "Please enter at least one valid email address",
      };
    }

    const invalidEmails = emails.filter((email) => !validateEmail(email));
    if (invalidEmails.length > 0) {
      return {
        isValid: false,
        error: `Invalid email address(es): ${invalidEmails.join(", ")}`,
      };
    }

    return { isValid: true, emails };
  };

  // Phone number validation function
  const validatePhoneNumber = (phone) => {
    if (!phone || !phone.trim()) {
      return false;
    }
    // Remove spaces, dashes, parentheses, and dots for validation
    const cleaned = phone.replace(/[\s\-\(\)\.]/g, "");
    // Check if it contains only digits (and optional + at start)
    // Allow 7-15 digits after optional + sign
    return /^\+?\d{7,15}$/.test(cleaned);
  };

  const validatePhoneNumbers = (phoneString, countryCode = "+1") => {
    if (!phoneString || !phoneString.trim()) {
      return { isValid: false, error: "Phone number is required" };
    }

    const phones = phoneString
      .split(",")
      .map((phone) => phone.trim())
      .filter((phone) => phone.length > 0);

    if (phones.length === 0) {
      return {
        isValid: false,
        error: "Please enter at least one phone number",
      };
    }

    const invalidPhones = phones.filter((phone) => !validatePhoneNumber(phone));
    if (invalidPhones.length > 0) {
      return {
        isValid: false,
        error: `Invalid phone number(s): ${invalidPhones.join(", ")}`,
      };
    }

    // Return cleaned phone numbers (remove formatting)
    const cleanedPhones = phones.map((phone) => {
      return phone.replace(/[\s\-\(\)\.]/g, "");
    });

    return { isValid: true, phones: cleanedPhones };
  };

  /** SMS: count visible characters (HTML stripped). */
  const validateSmsMessageBodyRich = (messageBodyHtml, maxLength = 160) => {
    const text = plainMessageTextFromHtml(messageBodyHtml);
    if (!text || !String(text).trim()) {
      return { isValid: false, error: "Message body is required" };
    }
    if (plainTextLengthFromMessageHtml(messageBodyHtml) > maxLength) {
      return {
        isValid: false,
        error: `Message body must be ${maxLength} characters or less`,
      };
    }
    return { isValid: true };
  };

  // -----------------------------
  // Client-side duplicate prevention (all triggers)
  // -----------------------------
  const normalizeCsvTokens = (value) =>
    String(value ?? "")
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);

  const normalizeEmailList = (value) =>
    normalizeCsvTokens(value)
      .map((x) => x.toLowerCase())
      .sort();

  const normalizePhoneNumbersForAction = (phoneString, countryCode = "+1") => {
    const cc = String(countryCode ?? "+1").trim() || "+1";
    const tokens = normalizeCsvTokens(phoneString);
    const normalized = tokens
      .map((t) => t.replace(/[\s\-\(\)\.]/g, "")) // match validatePhoneNumbers cleaning
      .filter(Boolean)
      .map((p) => (p.startsWith("+") ? p : `${cc}${p}`));
    normalized.sort();
    return normalized;
  };

  const stableJson = (obj) => {
    if (obj == null) return "";
    if (typeof obj !== "object") return String(obj);
    try {
      if (Array.isArray(obj)) return JSON.stringify(obj.map(stableJson));
      const keys = Object.keys(obj).sort();
      const out = {};
      keys.forEach((k) => {
        out[k] = obj[k];
      });
      return JSON.stringify(out);
    } catch {
      return "";
    }
  };

  const buildTriggerDedupKeyFromCard = (card) => {
    const type = String(card?.type ?? "").trim();
    if (!type) return "";
    const t = type.toLowerCase();
    const notifiedTo = String(card?.notifiedTo ?? "").trim().toLowerCase();

    if (t === "email") {
      const to = normalizeEmailList(card.email);
      const cc = normalizeEmailList(card.cc);
      const bcc = normalizeEmailList(card.bcc);
      const subject = String(card.subject ?? "").trim().toLowerCase();
      const body = String(card.messageBody ?? "").trim();
      return [
        "email",
        notifiedTo,
        to.join("|"),
        cc.join("|"),
        bcc.join("|"),
        subject,
        body,
      ].join("::");
    }

    if (t === "telegram") {
      const ids = normalizeCsvTokens(card.chatId)
        .map((x) => {
          const n = parseInt(x, 10);
          return Number.isFinite(n) ? String(n) : "";
        })
        .filter(Boolean)
        .sort();
      const body = String(card.messageBody ?? "").trim();
      return ["telegram", notifiedTo, ids.join("|"), body].join("::");
    }

    if (t === "sms") {
      const cc = String(card.countryCode ?? "+1").trim() || "+1";
      const phones = normalizePhoneNumbersForAction(card.chatId, cc);
      const body = String(card.messageBody ?? "").trim();
      return ["sms", notifiedTo, cc, phones.join("|"), body].join("::");
    }

    if (t === "http") {
      const method = String(card.httpMethod ?? "POST").trim().toUpperCase();
      const url = String(card.chatId ?? "").trim();
      const body = String(card.messageBody ?? "").trim();
      const headers =
        Array.isArray(card.httpHeaders)
          ? card.httpHeaders
              .filter((h) => h && h.key && h.value)
              .map((h) => [String(h.key).trim(), String(h.value).trim()])
              .sort((a, b) => a[0].localeCompare(b[0]))
          : card.httpHeaderKey && card.httpHeaderValue
            ? [[String(card.httpHeaderKey).trim(), String(card.httpHeaderValue).trim()]]
            : [];
      const params =
        Array.isArray(card.httpParams)
          ? card.httpParams
              .filter((p) => p && p.key && p.value)
              .map((p) => [String(p.key).trim(), String(p.value).trim()])
              .sort((a, b) => a[0].localeCompare(b[0]))
          : card.httpParamKey && card.httpParamValue
            ? [[String(card.httpParamKey).trim(), String(card.httpParamValue).trim()]]
            : [];
      const authType = String(card.authType ?? "").trim();
      const authAccount = String(card.authAccount ?? "").trim();
      // Do NOT include authPassword/token in key to avoid accidentally deduping based on secrets.
      return [
        "http",
        notifiedTo,
        method,
        url,
        body,
        stableJson(headers),
        stableJson(params),
        authType,
        authAccount,
      ].join("::");
    }

    if (t === "phone") {
      const cc = String(card.countryCode ?? "+1").trim() || "+1";
      const phones = normalizePhoneNumbersForAction(card.chatId, cc);
      return ["phone", notifiedTo, cc, phones.join("|")].join("::");
    }

    return `${t}::${stableJson(card)}`;
  };

  const isDuplicateTriggerCard = (card, { excludeActionId = null } = {}) => {
    const key = buildTriggerDedupKeyFromCard(card);
    if (!key) return false;
    return completedActions.some((a) => {
      if (excludeActionId != null && a?.id === excludeActionId) return false;
      const existingCard = a?.details ? { ...a.details, type: a.type } : a;
      return buildTriggerDedupKeyFromCard(existingCard) === key;
    });
  };

  const isDuplicateAgainstServerRuleTriggers = (
    card,
    { excludeTriggerId = null } = {},
  ) => {
    const key = buildTriggerDedupKeyFromCard(card);
    if (!key) return false;
    const entries = Object.entries(serverRuleTriggerKeyById || {});
    return entries.some(([id, k]) => {
      if (excludeTriggerId != null && String(id) === String(excludeTriggerId)) {
        return false;
      }
      return k === key;
    });
  };

  const mapServerTriggerToDedupCard = (trigger) => {
    if (!trigger || typeof trigger !== "object") return null;
    const action = String(trigger.action ?? trigger.action_type ?? "")
      .toUpperCase()
      .trim();
    const cfg =
      trigger.config && typeof trigger.config === "object"
        ? trigger.config
        : trigger;

    const notifiedTo =
      trigger.notified_to != null && String(trigger.notified_to).trim() !== ""
        ? String(trigger.notified_to).trim()
        : cfg.notified_to != null && String(cfg.notified_to).trim() !== ""
          ? String(cfg.notified_to).trim()
          : "";

    const messageBody = String(
      cfg.message_body ?? cfg.body ?? cfg.message ?? "",
    );

    if (action === "EMAIL") {
      const recipients = Array.isArray(cfg.recipients)
        ? cfg.recipients.join(", ")
        : cfg.recipients ?? cfg.mail_ids ?? cfg.email ?? "";
      const cc = Array.isArray(cfg.cc) ? cfg.cc.join(", ") : cfg.cc ?? "";
      const bcc = Array.isArray(cfg.bcc)
        ? cfg.bcc.join(", ")
        : cfg.bcc ?? "";
      return {
        type: "Email",
        notifiedTo,
        email: String(recipients ?? ""),
        cc: String(cc ?? ""),
        bcc: String(bcc ?? ""),
        subject: String(cfg.subject ?? trigger.name ?? ""),
        messageBody,
      };
    }

    if (action === "TELEGRAM") {
      const ids = Array.isArray(cfg.chat_ids)
        ? cfg.chat_ids.join(", ")
        : cfg.chat_ids ?? cfg.chat_id ?? "";
      return {
        type: "Telegram",
        notifiedTo,
        chatId: String(ids ?? ""),
        messageBody,
      };
    }

    if (action === "SMS") {
      const raw =
        cfg.phone_numbers ??
        cfg.phone_number ??
        cfg.phoneNumbers ??
        cfg.phone ??
        [];
      const phonesStr = Array.isArray(raw) ? raw.join(", ") : String(raw ?? "");
      const first = phonesStr.split(",")[0]?.trim() || "";
      const knownCodes = [
        "+1",
        "+91",
        "+44",
        "+86",
        "+81",
        "+49",
        "+33",
        "+39",
        "+34",
        "+61",
        "+55",
        "+7",
        "+82",
        "+65",
        "+971",
      ];
      const cc =
        String(cfg.country_code ?? cfg.countryCode ?? "").trim() ||
        knownCodes.find((c) => first.startsWith(c)) ||
        (first.match(/^(\+\d{1,3})/)?.[1] ?? "+1");

      const phoneNoCc = phonesStr
        .split(",")
        .map((p) => {
          const t = p.trim();
          return t.startsWith(cc)
            ? t.slice(cc.length).trim()
            : t.replace(/^\+\d{1,3}/, "").trim();
        })
        .filter(Boolean)
        .join(", ");

      return {
        type: "SMS",
        notifiedTo,
        countryCode: cc,
        chatId: phoneNoCc,
        messageBody,
      };
    }

    if (action === "HTTP") {
      const headers =
        cfg.headers && typeof cfg.headers === "object"
          ? Object.entries(cfg.headers).map(([key, value]) => ({
              key,
              value,
            }))
          : [];
      const params =
        cfg.params && typeof cfg.params === "object"
          ? Object.entries(cfg.params).map(([key, value]) => ({
              key,
              value,
            }))
          : [];
      return {
        type: "HTTP",
        notifiedTo,
        httpMethod: String(cfg.method ?? "POST"),
        chatId: String(cfg.url ?? ""),
        httpHeaders: headers,
        httpParams: params,
        authType: String(cfg.authorization ?? ""),
        authAccount: String(cfg.authorization ?? ""),
        messageBody,
      };
    }

    return null;
  };

  // URL validation function
  const validateURL = (url) => {
    if (!url || !url.trim()) {
      return { isValid: false, error: "URL is required" };
    }

    try {
      // Try to create a URL object to validate
      const urlObj = new URL(url);
      // Check if it's http or https
      if (urlObj.protocol !== "http:" && urlObj.protocol !== "https:") {
        return {
          isValid: false,
          error: "URL must start with http:// or https://",
        };
      }
      return { isValid: true };
    } catch (error) {
      return {
        isValid: false,
        error: "Please enter a valid URL (e.g., https://example.com/webhook)",
      };
    }
  };

  const handleAddActionToList = async (card) => {
    const toastOpts = { position: "bottom-center", duration: 4000 };
    if (!card.type) {
      toast.error("Please select an action type", toastOpts);
      return;
    }

    // Prevent duplicates on the client (same destination + same config/body).
    if (isDuplicateTriggerCard(card)) {
      toast.error("This action is already added (duplicate trigger).", toastOpts);
      return;
    }
    if (editingAlert && isDuplicateAgainstServerRuleTriggers(card)) {
      toast.error(
        "This action already exists on the server for this rule (duplicate trigger).",
        toastOpts,
      );
      return;
    }

    let payload = "";
    let isValid = false;
    let validatedPhoneNumbers = null; // Store validated phone numbers for SMS
    if (card.type === "Email") {
      if (!card.email || !card.subject || !card.messageBody) {
        toast.error(
          "Please fill in all required email fields (To, Subject, and Message Body)",
          toastOpts,
        );
        return;
      }

      // Validate email addresses before proceeding
      const emailValidation = validateEmailAddresses(card.email);
      if (!emailValidation.isValid) {
        setEmailErrors((prev) => ({
          ...prev,
          [card.id]: {
            ...prev[card.id],
            email: emailValidation.error,
          },
        }));
        return;
      }

      // Validate CC emails if provided
      if (card.cc) {
        const ccValidation = validateEmailAddresses(card.cc);
        if (!ccValidation.isValid) {
          setEmailErrors((prev) => ({
            ...prev,
            [card.id]: {
              ...prev[card.id],
              cc: ccValidation.error,
            },
          }));
          return;
        }
      }

      // Validate BCC emails if provided
      if (card.bcc) {
        const bccValidation = validateEmailAddresses(card.bcc);
        if (!bccValidation.isValid) {
          setEmailErrors((prev) => ({
            ...prev,
            [card.id]: {
              ...prev[card.id],
              bcc: bccValidation.error,
            },
          }));
          return;
        }
      }

      // Clear errors if validation passes
      setEmailErrors((prev) => ({
        ...prev,
        [card.id]: {
          email: "",
          cc: "",
          bcc: "",
        },
      }));

      payload = card.email;
      isValid = true;
    } else if (card.type === "Telegram") {
      if (
        !card.chatId ||
        !plainMessageTextFromHtml(card.messageBody || "").trim()
      ) {
        toast.error("Please fill in Chat ID and Message Body", toastOpts);
        return;
      }
      payload = card.chatId;
      isValid = true;
    } else if (card.type === "SMS") {
      if (
        !card.chatId ||
        !plainMessageTextFromHtml(card.messageBody || "").trim()
      ) {
        toast.error("Please fill in Phone Number and Message Body", toastOpts);
        return;
      }

      // Validate phone numbers before proceeding
      const phoneValidation = validatePhoneNumbers(
        card.chatId,
        card.countryCode,
      );
      if (!phoneValidation.isValid) {
        setSmsErrors((prev) => ({
          ...prev,
          [card.id]: {
            ...prev[card.id],
            phoneNumber: phoneValidation.error,
          },
        }));
        return;
      }

      const messageValidation = validateSmsMessageBodyRich(card.messageBody);
      if (!messageValidation.isValid) {
        setSmsErrors((prev) => ({
          ...prev,
          [card.id]: {
            ...prev[card.id],
            messageBody: messageValidation.error,
          },
        }));
        return;
      }

      setSmsErrors((prev) => ({
        ...prev,
        [card.id]: {
          phoneNumber: "",
          messageBody: "",
        },
      }));

      // Store validated phone numbers for use in trigger creation
      validatedPhoneNumbers = phoneValidation.phones;
      payload = card.chatId;
      isValid = true;
    } else if (card.type === "Phone") {
      if (!card.chatId) {
        toast.error("Please fill in Phone Number", toastOpts);
        return;
      }

      // Validate phone numbers before proceeding
      const phoneValidation = validatePhoneNumbers(
        card.chatId,
        card.countryCode,
      );
      if (!phoneValidation.isValid) {
        setPhoneErrors((prev) => ({
          ...prev,
          [card.id]: {
            phoneNumber: phoneValidation.error,
          },
        }));
        return;
      }

      // Clear errors if validation passes
      setPhoneErrors((prev) => ({
        ...prev,
        [card.id]: {
          phoneNumber: "",
        },
      }));

      // Store validated phone numbers for use in trigger creation
      validatedPhoneNumbers = phoneValidation.phones;
      payload = card.chatId;
      isValid = true;
    } else if (card.type === "HTTP") {
      if (!card.chatId) {
        toast.error("Please fill in Endpoint URL", toastOpts);
        return;
      }

      // Validate URL before proceeding
      const urlValidation = validateURL(card.chatId);
      if (!urlValidation.isValid) {
        setHttpErrors((prev) => ({
          ...prev,
          [card.id]: {
            url: urlValidation.error,
          },
        }));
        return;
      }

      // Clear errors if validation passes
      setHttpErrors((prev) => ({
        ...prev,
        [card.id]: {
          url: "",
        },
      }));

      payload = card.chatId;
      isValid = true;
    }
    if (!isValid) return;

    // Check if we're editing an existing trigger
    const isEditing = editingTriggerId !== null;
    let triggerId = editingTriggerId || null;

    // Create or update trigger via API for EMAIL action
    if (card.type === "Email") {
      try {
        // Prepare trigger data for EMAIL action
        // Parse email addresses - handle comma-separated values
        const emailAddresses = card.email
          ? card.email
              .split(",")
              .map((email) => email.trim())
              .filter((email) => email.length > 0)
          : [];

        const triggerData = {
          action: "EMAIL",
          name: card.subject || "Security Alert",
          config: {
            message_body: card.messageBody || "",
            recipients: emailAddresses,
            subject: card.subject || "Security Alert",
          },
        };

        // Add CC if provided
        if (card.cc) {
          const ccAddresses = card.cc
            .split(",")
            .map((email) => email.trim())
            .filter((email) => email.length > 0);
          if (ccAddresses.length > 0) {
            triggerData.config.cc = ccAddresses;
          }
        }

        // Add BCC if provided
        if (card.bcc) {
          const bccAddresses = card.bcc
            .split(",")
            .map((email) => email.trim())
            .filter((email) => email.length > 0);
          if (bccAddresses.length > 0) {
            triggerData.config.bcc = bccAddresses;
          }
        }

        applyNotifiedToToTriggerPayload(triggerData, card);

        console.log("Creating trigger:", triggerData);
        const response = await triggersAPI.create(triggerData);
        console.log("Trigger created successfully:", response);

        // Extract trigger ID from response
        triggerId = response.id || response.trigger_id || response.data?.id;
      } catch (error) {
        console.error("Error creating trigger:", error);
        toast.error(
          `Failed to create trigger: ${error.message || "Please try again"}`,
          toastOpts,
        );
        return; // Don't add action if trigger creation fails
      }
    } else if (card.type === "Telegram") {
      try {
        // Prepare trigger data for TELEGRAM action
        // Parse chat IDs - handle comma-separated values and convert to numbers
        const chatIds = card.chatId
          ? card.chatId
              .split(",")
              .map((id) => {
                const trimmed = id.trim();
                const numId = parseInt(trimmed, 10);
                return isNaN(numId) ? null : numId;
              })
              .filter((id) => id !== null)
          : [];

        if (chatIds.length === 0) {
          toast.error(
            "Please enter valid Telegram chat IDs (numbers)",
            toastOpts,
          );
          return;
        }

        const triggerData = {
          name: card.messageBody
            ? card.messageBody.substring(0, 50) + "..."
            : "Security Alert Telegram",
          event: "SECURITY_ALERT", // Default event type
          action: "TELEGRAM",
          config: {
            chat_ids: chatIds, // Array of numbers
            message_body: card.messageBody || "",
          },
        };

        applyNotifiedToToTriggerPayload(triggerData, card);

        console.log(
          isEditing ? "Updating trigger:" : "Creating trigger:",
          triggerData,
        );
        const response = isEditing
          ? await triggersAPI.update(editingTriggerId, triggerData)
          : await triggersAPI.create(triggerData);
        console.log(
          isEditing
            ? "Trigger updated successfully:"
            : "Trigger created successfully:",
          response,
        );

        // Extract trigger ID from response (for new triggers)
        if (!isEditing) {
          triggerId = response.id || response.trigger_id || response.data?.id;
        }
      } catch (error) {
        console.error(
          isEditing ? "Error updating trigger:" : "Error creating trigger:",
          error,
        );
        toast.error(
          `Failed to ${isEditing ? "update" : "create"} trigger: ${
            error.message || "Please try again"
          }`,
          toastOpts,
        );
        return; // Don't add action if trigger creation/update fails
      }
    } else if (card.type === "SMS") {
      try {
        console.log(
          "Creating SMS trigger, validatedPhoneNumbers:",
          validatedPhoneNumbers,
        );
        // Prepare trigger data for SMS action
        // Use validated phone numbers from validation step
        const countryCode = card.countryCode || "+1";
        let phoneNumbers = [];

        if (validatedPhoneNumbers && validatedPhoneNumbers.length > 0) {
          console.log("Using validated phone numbers:", validatedPhoneNumbers);
          // Use validated phone numbers and combine with country code
          phoneNumbers = validatedPhoneNumbers.map((phone) => {
            const trimmed = phone.trim();
            // Remove any formatting characters before checking
            const cleaned = trimmed.replace(/[\s\-\(\)\.]/g, "");
            // If phone number already starts with +, use as is, otherwise prepend country code
            if (cleaned.startsWith("+")) {
              return cleaned;
            }
            return countryCode + cleaned;
          });
          console.log(
            "Formatted phone numbers with country code:",
            phoneNumbers,
          );
        } else {
          console.log("No validated phone numbers, falling back to parsing");
          // Fallback: parse phone numbers (shouldn't happen if validation passed)
          phoneNumbers = card.chatId
            ? card.chatId
                .split(",")
                .map((phone) => {
                  const trimmed = phone.trim();
                  const cleaned = trimmed.replace(/[\s\-\(\)\.]/g, "");
                  if (cleaned.startsWith("+")) {
                    return cleaned;
                  }
                  return countryCode + cleaned;
                })
                .filter((phone) => phone.length > 0)
            : [];
        }

        if (phoneNumbers.length === 0) {
          console.error("No phone numbers after processing");
          toast.error("Please enter at least one phone number", toastOpts);
          return;
        }

        const triggerData = {
          name: card.messageBody
            ? card.messageBody.substring(0, 50) + "..."
            : "Security SMS Alert",
          event: "SECURITY_ALERT", // Default event type
          action: "SMS",
          config: {
            phone_numbers: phoneNumbers, // Array of phone number strings with country code
            message_body: card.messageBody || "",
          },
        };

        applyNotifiedToToTriggerPayload(triggerData, card);

        console.log(
          isEditing ? "Updating trigger:" : "Creating trigger:",
          triggerData,
        );
        const response = isEditing
          ? await triggersAPI.update(editingTriggerId, triggerData)
          : await triggersAPI.create(triggerData);
        console.log(
          isEditing
            ? "Trigger updated successfully:"
            : "Trigger created successfully:",
          response,
        );

        // Extract trigger ID from response (for new triggers)
        if (!isEditing) {
          triggerId = response.id || response.trigger_id || response.data?.id;
        }
      } catch (error) {
        console.error(
          isEditing ? "Error updating trigger:" : "Error creating trigger:",
          error,
        );
        toast.error(
          `Failed to ${isEditing ? "update" : "create"} trigger: ${
            error.message || "Please try again"
          }`,
          toastOpts,
        );
        return; // Don't add action if trigger creation/update fails
      }
    } else if (card.type === "HTTP") {
      try {
        // Prepare trigger data for HTTP action
        if (!card.chatId) {
          toast.error("Please fill in Endpoint URL", toastOpts);
          return;
        }

        // Validate URL before proceeding
        const urlValidation = validateURL(card.chatId);
        if (!urlValidation.isValid) {
          setHttpErrors((prev) => ({
            ...prev,
            [card.id]: {
              url: urlValidation.error,
            },
          }));
          return;
        }

        // Clear errors if validation passes
        setHttpErrors((prev) => ({
          ...prev,
          [card.id]: {
            url: "",
          },
        }));

        // Build headers object from httpHeaders array or key-value pairs
        let headers = {};
        if (card.httpHeaders && Array.isArray(card.httpHeaders)) {
          card.httpHeaders.forEach((header) => {
            if (header.key && header.value) {
              headers[header.key] = header.value;
            }
          });
        } else if (card.httpHeaderKey && card.httpHeaderValue) {
          // Single header from key-value fields
          headers[card.httpHeaderKey] = card.httpHeaderValue;
        }

        // Build params object from httpParams array or key-value pairs
        let params = {};
        if (card.httpParams && Array.isArray(card.httpParams)) {
          card.httpParams.forEach((param) => {
            if (param.key && param.value) {
              params[param.key] = param.value;
            }
          });
        } else if (card.httpParamKey && card.httpParamValue) {
          // Single param from key-value fields
          params[card.httpParamKey] = card.httpParamValue;
        }

        // Build authorization string
        let authorization = null;
        if (card.authType && card.authType !== "No Auth") {
          if (card.authType === "Bearer") {
            // For Bearer token, use authToken or authAccount field
            const token = card.authToken || card.authAccount || "";
            if (token) {
              authorization = `Bearer ${token}`;
            }
          } else if (
            card.authType === "Basic" &&
            card.authAccount &&
            card.authPassword
          ) {
            // Basic auth: base64 encode username:password
            const credentials = btoa(
              `${card.authAccount}:${card.authPassword}`,
            );
            authorization = `Basic ${credentials}`;
          } else if (card.authAccount) {
            // For Digest or other types, or if Bearer token is in authAccount
            // Check if authAccount looks like a Bearer token (starts with Bearer or is a token)
            if (card.authAccount.startsWith("Bearer ")) {
              authorization = card.authAccount;
            } else {
              // For other auth types, use account as authorization value
              authorization = card.authAccount;
            }
          }
        }

        const triggerData = {
          name: card.messageBody
            ? card.messageBody.substring(0, 50) + "..."
            : "Webhook Alert with Message",
          event: "DETECTION_ALERT", // Default event type for HTTP
          action: "HTTP",
          config: {
            method: card.httpMethod || "POST",
            url: card.chatId || "",
            ...(authorization && { authorization: authorization }),
            headers: headers,
            params: params,
            message_body: card.messageBody || "",
          },
        };

        applyNotifiedToToTriggerPayload(triggerData, card);

        console.log(
          isEditing ? "Updating trigger:" : "Creating trigger:",
          triggerData,
        );
        const response = isEditing
          ? await triggersAPI.update(editingTriggerId, triggerData)
          : await triggersAPI.create(triggerData);
        console.log(
          isEditing
            ? "Trigger updated successfully:"
            : "Trigger created successfully:",
          response,
        );

        // Extract trigger ID from response (for new triggers)
        if (!isEditing) {
          triggerId = response.id || response.trigger_id || response.data?.id;
        }
      } catch (error) {
        console.error(
          isEditing ? "Error updating trigger:" : "Error creating trigger:",
          error,
        );
        toast.error(
          `Failed to ${isEditing ? "update" : "create"} trigger: ${
            error.message || "Please try again"
          }`,
          toastOpts,
        );
        return; // Don't add action if trigger creation/update fails
      }
    }

    const newAction = {
      id: Date.now().toString(),
      type: card.type,
      payload: payload,
      triggerId: triggerId, // Store trigger ID for use in rule creation
      isActive: true,
      details: {
        ...card,
      },
    };
    setCompletedActions((prev) => [...prev, newAction]);

    toast.success(
      isEditing
        ? "Action updated. You can review it in View Action."
        : "Action saved. It has been added to View Action.",
      toastOpts,
    );
    setRightActionTab(1);

    // Clear editing state after successful update
    if (isEditing) {
      setEditingTriggerId(null);
    }

    // Reset composer in one update so the next entry gets a fresh template (avoids stale batched state)
    setActionCards((prev) =>
      prev.map((c) => (c.id === card.id ? buildEmptyComposerCard(c) : c)),
    );
    clearActionCardErrors(card.id);
  };

  const loadZonesForCamera = useCallback(async (cameraId) => {
    if (!cameraId) return;
    try {
      setAiCameraZonesLoading((prev) => ({ ...prev, [cameraId]: true }));
      const body = await camerasAPI.listZones(cameraId);
      const raw = extractZonesArray(body);
      const zones = raw
        .filter((z) => z != null && typeof z === "object")
        .map((z) => {
          const id = String(z.id ?? "").trim();
          if (!id) return null;
          const name =
            String(z.name ?? "").trim() || id || "Zone";
          return { id, name };
        })
        .filter(Boolean);
      setAiCameras((prev) =>
        prev.map((c) =>
          c.id === cameraId ? { ...c, zones, rois: undefined } : c,
        ),
      );
    } catch (err) {
      console.error("Failed to load zones for camera:", cameraId, err);
      toast.error(err?.message || "Failed to load zones", {
        position: "bottom-center",
        duration: 4000,
      });
    } finally {
      setAiCameraZonesLoading((prev) => {
        const next = { ...prev };
        delete next[cameraId];
        return next;
      });
    }
  }, []);

  const handleSourceDialogOpen = () => {
    if (sourceCategory === "AI") {
      const aiRelated = selectedSources.filter((id) => {
        const { cameraId: baseId } = splitCameraZoneEntry(id);
        const src =
          aiCameras.find((s) => s.id === baseId) ||
          sourceData.find((s) => s.id === baseId);
        return src?.type === "camera";
      });
      setTempSelectedSources(aiRelated);
      setCameraROISelections(rebuildCameraRoiSelectionsFromIds(aiRelated));
      const cameraOnlyIds = new Set();
      aiRelated.forEach((entry) => {
        const { cameraId } = splitCameraZoneEntry(entry);
        if (cameraId) cameraOnlyIds.add(cameraId);
      });
      cameraOnlyIds.forEach((camId) => {
        const cam = aiCameras.find((c) => c.id === camId);
        const zl = getCameraZonesList(cam);
        if (cam && zl.length === 0) {
          void loadZonesForCamera(camId);
        }
      });
    } else if (sourceCategory === "IoT") {
      const iotRelated = selectedSources.filter((id) => {
        const src = sourceData.find((s) => s.id === id);
        return src?.type === "iot";
      });
      setTempSelectedSources(iotRelated);
    } else if (sourceCategory === "System") {
      const systemRelated = selectedSources.filter((id) => {
        const src = sourceData.find((s) => s.id === id);
        return src?.type === "system";
      });
      setTempSelectedSources(systemRelated);
    } else {
      return;
    }
    setSourceDialogOpen(true);
  };

  const handleSourceCategoryChange = (event) => {
    if (viewOnly) return;
    setSourceCategory(event.target.value);
    setSelectedSources([]);
    setCameraROISelections({});
    setTempSelectedSources([]);
    setSourceDialogOpen(false);
  };

  const getAiSubSourceButtonLabel = () => {
    const aiIds = selectedSources.filter((id) => {
      const { cameraId: baseId } = splitCameraZoneEntry(id);
      const src =
        aiCameras.find((s) => s.id === baseId) ||
        sourceData.find((s) => s.id === baseId);
      return src?.type === "camera";
    });
    if (aiIds.length === 0) return "Select cameras & zones";
    return `${aiIds.length} selection(s) — click to edit`;
  };

  const getIotSubSourceButtonLabel = () => {
    const n = selectedSources.filter((id) => {
      const src = sourceData.find((s) => s.id === id);
      return src?.type === "iot";
    }).length;
    if (n === 0) return "Select IoT devices";
    return `${n} selection(s) — click to edit`;
  };

  const getSystemSubSourceButtonLabel = () => {
    const n = selectedSources.filter((id) => {
      const src = sourceData.find((s) => s.id === id);
      return src?.type === "system";
    }).length;
    if (n === 0) return "Select system sources";
    return `${n} selection(s) — click to edit`;
  };

  /** IoT / System rows inside the sub-source dialog (simple id list, no ROIs). */
  const handleIotSystemTempToggle = (sourceId) => {
    if (subSourceDialogReadOnly) return;
    setTempSelectedSources((prev) =>
      prev.includes(sourceId)
        ? prev.filter((id) => id !== sourceId)
        : [...prev, sourceId],
    );
  };
  const handleSourceDialogClose = () => {
    setSourceDialogOpen(false);
    setSourceSearchQuery("");
  };
  const handleSourceDialogSave = () => {
    if (subSourceDialogReadOnly) {
      handleSourceDialogClose();
      return;
    }
    setSelectedSources([...tempSelectedSources]);
    setSourceDialogOpen(false);
    setSourceSearchQuery("");
  };
  const handleSourceToggle = (sourceId) => {
    if (subSourceDialogReadOnly) return;
    if (tempSelectedSources.includes(sourceId)) {
      setTempSelectedSources(
        tempSelectedSources.filter((id) => id !== sourceId),
      );
      const roiIds = tempSelectedSources.filter(
        (id) =>
          id.startsWith(`${sourceId}${CAMERA_ZONE_ENTRY_SEP}`) ||
          id.startsWith(`${sourceId}-`),
      );
      roiIds.forEach((roiId) => {
        setTempSelectedSources((prev) => prev.filter((id) => id !== roiId));
      });
      const newSelections = {
        ...cameraROISelections,
      };
      delete newSelections[sourceId];
      setCameraROISelections(newSelections);
    } else {
      setTempSelectedSources([...tempSelectedSources, sourceId]);
    }
  };

  const handleAiCameraToggle = (cameraId) => {
    if (subSourceDialogReadOnly) return;
    if (tempSelectedSources.includes(cameraId)) {
      handleSourceToggle(cameraId);
      return;
    }
    handleSourceToggle(cameraId);
    void loadZonesForCamera(cameraId);
  };

  /** Select or clear every camera in the current filtered list (respects search). */
  const handleAiCamerasSelectAllToggle = () => {
    if (subSourceDialogReadOnly) return;
    const ids = filteredAiCameras.map((c) => c.id);
    if (ids.length === 0) return;

    if (allFilteredAiCamerasSelected) {
      setCameraROISelections((rois) => {
        const copy = { ...rois };
        ids.forEach((cameraId) => {
          delete copy[cameraId];
        });
        return copy;
      });
      setTempSelectedSources((prev) => {
        let next = [...prev];
        ids.forEach((cameraId) => {
          next = next.filter((id) => id !== cameraId);
          const sep = `${cameraId}${CAMERA_ZONE_ENTRY_SEP}`;
          next = next.filter(
            (id) =>
              !String(id).startsWith(sep) &&
              !String(id).startsWith(`${cameraId}-`),
          );
        });
        return next;
      });
      return;
    }

    const idsToAdd = ids.filter((id) => !tempSelectedSources.includes(id));
    setTempSelectedSources((prev) => {
      const next = [...prev];
      ids.forEach((cameraId) => {
        if (!next.includes(cameraId)) next.push(cameraId);
      });
      return next;
    });
    idsToAdd.forEach((id) => {
      void loadZonesForCamera(id);
    });
  };

  const handleCameraROIChange = (cameraId, selectedROIs) => {
    if (subSourceDialogReadOnly) return;
    const roisNorm = (selectedROIs || []).map(normalizeZoneOptionId);
    setCameraROISelections((prev) => ({
      ...prev,
      [cameraId]: roisNorm,
    }));

    setTempSelectedSources((prev) => {
      const otherSources = prev.filter((id) => {
        if (id === cameraId) return false;
        if (id.startsWith(`${cameraId}${CAMERA_ZONE_ENTRY_SEP}`)) return false;
        if (id.startsWith(`${cameraId}-`)) return false;
        return true;
      });
      const roiIds = roisNorm.map(
        (roi) => `${cameraId}${CAMERA_ZONE_ENTRY_SEP}${roi}`,
      );

      if (roiIds.length > 0) {
        return [...otherSources, cameraId, ...roiIds];
      }
      return [...otherSources, cameraId];
    });
  };
  // Convert API schedule format to WeeklyScheduleDialog format
  const convertApiScheduleToDialogFormat = (apiSchedule) => {
    if (!apiSchedule) return null;

    const DAYS_MAP = {
      sunday: "Sunday",
      monday: "Monday",
      tuesday: "Tuesday",
      wednesday: "Wednesday",
      thursday: "Thursday",
      friday: "Friday",
      saturday: "Saturday",
    };

    const schedule = {};
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];

    // Initialize all days with all hours set to false
    days.forEach((day) => {
      schedule[day] = {};
      for (let hour = 0; hour < 24; hour++) {
        schedule[day][hour] = false;
      }
    });

    // Check if it's the new format (schedule.schedule object with day keys)
    let scheduleData = null;
    if (apiSchedule.schedule && typeof apiSchedule.schedule === "object") {
      scheduleData = apiSchedule.schedule;
    } else if (
      apiSchedule &&
      typeof apiSchedule === "object" &&
      !apiSchedule.type &&
      !apiSchedule.startTime
    ) {
      // Might be the new format directly
      scheduleData = apiSchedule;
    }

    // Helper to parse time from either "HH:mm" or "YYYY-MM-DDTHH:mm:ss" format
    const parseTimeToHours = (timeStr) => {
      if (!timeStr) return { hour: 0, minute: 0 };
      if (timeStr.includes("T")) {
        const d = new Date(timeStr);
        return { hour: d.getHours(), minute: d.getMinutes() };
      }
      const [h, m] = timeStr.split(":").map(Number);
      return { hour: h ?? 0, minute: m ?? 0 };
    };

    if (scheduleData) {
      // New format: { "monday": [{ "start": "09:00", "end": "17:00" }], ... }
      Object.keys(scheduleData).forEach((dayKey) => {
        const dayName = DAYS_MAP[dayKey.toLowerCase()];
        if (dayName && Array.isArray(scheduleData[dayKey])) {
          scheduleData[dayKey].forEach((timeSlot) => {
            if (timeSlot.start && timeSlot.end) {
              const start = parseTimeToHours(timeSlot.start);
              const end = parseTimeToHours(timeSlot.end);
              const startHour = start.hour;
              const endHour = end.hour;
              const endMinute = end.minute;

              // Mark hours as selected
              for (let hour = startHour; hour < endHour; hour++) {
                schedule[dayName][hour] = true;
              }
              if (endMinute > 0 && endHour < 24) {
                schedule[dayName][endHour] = true;
              }
            }
          });
        }
      });
    } else {
      // Old format: handle legacy format with startTime/endTime
      const DAYS_MAP_OLD = {
        sun: "Sunday",
        mon: "Monday",
        tue: "Tuesday",
        wed: "Wednesday",
        thu: "Thursday",
        fri: "Friday",
        sat: "Saturday",
      };

      // Parse start_time and end_time
      const startTime =
        apiSchedule.startTime || apiSchedule.start_time || "00:00";
      const endTime = apiSchedule.endTime || apiSchedule.end_time || "23:59";
      const [startHour, startMinute] = startTime.split(":").map(Number);
      let [endHour, endMinute] = endTime.split(":").map(Number);

      // If end_time is 23:59, treat it as 24:00 (end of day)
      if (endHour === 23 && endMinute === 59) {
        endHour = 24;
        endMinute = 0;
      }

      // Get the days from API (could be in recurringDays or days array)
      const apiDays = apiSchedule.recurringDays || apiSchedule.days || [];
      const mappedDays = apiDays.map((day) => {
        const dayLower = day.toLowerCase();
        return DAYS_MAP_OLD[dayLower] || DAYS_MAP[dayLower] || day;
      });

      // If no days specified, assume all days
      const selectedDays = mappedDays.length === 0 ? days : mappedDays;

      // Initialize all days
      days.forEach((day) => {
        const isDaySelected = selectedDays.includes(day);

        // Initialize all hours
        for (let hour = 0; hour < 24; hour++) {
          if (!isDaySelected) {
            schedule[day][hour] = false;
          } else {
            // Check if hour is within the time range
            if (startHour < endHour) {
              // Normal range (e.g., 9:00 to 17:00)
              if (hour === startHour) {
                // Start hour: include if startMinute is 0, otherwise exclude
                schedule[day][hour] = startMinute === 0;
              } else if (hour === endHour) {
                // End hour: exclude (end is exclusive)
                schedule[day][hour] = false;
              } else {
                // Hours in between
                schedule[day][hour] = hour > startHour && hour < endHour;
              }
            } else if (startHour === endHour) {
              // Same hour (e.g., 9:00 to 9:00 means no time selected, or all day if 00:00 to 23:59)
              if (startHour === 0 && endHour === 24) {
                schedule[day][hour] = true; // All day
              } else {
                schedule[day][hour] = false; // No time selected
              }
            } else {
              // Overnight range (e.g., 22:00 to 6:00) - not common but handle it
              schedule[day][hour] = hour >= startHour || hour < endHour;
            }
          }
        }
      });
    }

    return schedule;
  };

  // Convert old schedule format to new format
  const convertOldScheduleToNewFormat = (oldSchedule) => {
    if (!oldSchedule || typeof oldSchedule !== "object") {
      return {};
    }

    // Check if it's already in new format
    if (oldSchedule.schedule && typeof oldSchedule.schedule === "object") {
      return oldSchedule.schedule;
    }
    if (
      typeof oldSchedule === "object" &&
      !oldSchedule.type &&
      !oldSchedule.days &&
      !oldSchedule.start_time
    ) {
      // Might already be in new format (object with day keys)
      return oldSchedule;
    }

    // It's in old format, convert it to API format (HH:mm)
    const DAYS_MAP = {
      sun: "sunday",
      mon: "monday",
      tue: "tuesday",
      wed: "wednesday",
      thu: "thursday",
      fri: "friday",
      sat: "saturday",
    };

    const schedule = {};
    const days = oldSchedule.days || oldSchedule.recurringDays || [];
    const startTimeRaw =
      oldSchedule.startTime || oldSchedule.start_time || "00:00";
    let endTime = oldSchedule.endTime || oldSchedule.end_time || "23:59";
    let startTime = startTimeRaw;

    // Rules API accepts hours 00–23 only; keep end-of-day as 23:59 (not 24:00)
    if (!startTime.includes(":")) startTime = `${startTime}:00`;
    if (!endTime.includes(":")) endTime = `${endTime}:00`;
    if (endTime === "24:00" || endTime === "24:00:00") endTime = "23:59";

    const DAY_KEYS = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];

    // Initialize all days with empty arrays
    DAY_KEYS.forEach((d) => {
      schedule[d] = [];
    });

    // Convert each selected day
    days.forEach((dayAbbr) => {
      const dayName = DAYS_MAP[dayAbbr.toLowerCase()];
      if (dayName) {
        schedule[dayName] = [
          {
            start: startTime,
            end: endTime,
          },
        ];
      }
    });

    return schedule;
  };

  const handleScheduleDialogOpen = () => {
    if (viewOnly) return;
    setScheduleDialogOpen(true);
  };
  const handleScheduleDialogClose = () => {
    setScheduleDialogOpen(false);
  };
  // Convert WeeklyScheduleDialog format to API format
  const convertDialogScheduleToApiFormat = (dialogSchedule) => {
    if (!dialogSchedule || typeof dialogSchedule !== "object") {
      return null;
    }

    const DAYS_MAP = {
      Sunday: "sunday",
      Monday: "monday",
      Tuesday: "tuesday",
      Wednesday: "wednesday",
      Thursday: "thursday",
      Friday: "friday",
      Saturday: "saturday",
    };

    // Helper function to find consecutive time ranges in an array of hours
    const findConsecutiveRanges = (hours) => {
      const ranges = [];
      let rangeStart = null;
      let rangeEnd = null;

      for (let hour = 0; hour < 24; hour++) {
        if (hours[hour] === true) {
          if (rangeStart === null) {
            rangeStart = hour;
            rangeEnd = hour;
          } else {
            rangeEnd = hour;
          }
        } else {
          if (rangeStart !== null) {
            ranges.push({ start: rangeStart, end: rangeEnd + 1 }); // End is exclusive
            rangeStart = null;
            rangeEnd = null;
          }
        }
      }

      // Handle case where range extends to end of day
      if (rangeStart !== null) {
        ranges.push({ start: rangeStart, end: rangeEnd + 1 });
      }

      return ranges;
    };

    // Helper: API requires hours 00–23; end-of-day exclusive index 24 → 23:59
    const formatTime = (hour) => {
      if (hour >= 24) return "23:59";
      const hourStr = String(hour).padStart(2, "0");
      return `${hourStr}:00`;
    };

    const DAY_KEYS = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];
    const schedule = {};
    let hasAnySelection = false;

    // Process each day - include all days (empty array for days with no selection)
    Object.keys(DAYS_MAP).forEach((dayName) => {
      const lowercaseDay = DAYS_MAP[dayName];
      if (dialogSchedule[dayName]) {
        const dayHours = dialogSchedule[dayName];
        const ranges = findConsecutiveRanges(dayHours);

        if (ranges.length > 0) {
          hasAnySelection = true;
          schedule[lowercaseDay] = ranges.map((range) => ({
            start: formatTime(range.start),
            end: formatTime(range.end),
          }));
        } else {
          schedule[lowercaseDay] = [];
        }
      } else {
        schedule[lowercaseDay] = [];
      }
    });

    // If no selection, return empty schedule
    if (!hasAnySelection) {
      return {
        schedule: Object.fromEntries(DAY_KEYS.map((d) => [d, []])),
      };
    }

    return {
      schedule,
    };
  };

  const handleScheduleDialogSave = (scheduleData) => {
    // Store both formats: dialog format for the dialog, API format for submission
    const apiFormat = convertDialogScheduleToApiFormat(scheduleData);
    const newSchedule = {
      ...apiFormat,
      _dialogFormat: scheduleData, // Store original dialog format for reloading
    };
    setSchedule(newSchedule);
    // Sync to parent so UI stays in sync (e.g. if parent re-renders)
    onScheduleChange?.(newSchedule);
    setScheduleDialogOpen(false);
  };
  const getScheduleDisplay = () => {
    if (!schedule) return "Select Schedule";

    // Handle new format: schedule.schedule object
    const scheduleData = schedule.schedule || schedule;

    if (schedule.type === "forever") {
      return "Forever";
    } else if (schedule.type === "custom") {
      // For custom, show only dates without times
      return `${schedule.startDate} - ${schedule.endDate}`;
    } else if (schedule.type === "recurring") {
      // Show only weekday names, not timing
      const dayNameMap = {
        sun: "Sun",
        mon: "Mon",
        tue: "Tue",
        wed: "Wed",
        thu: "Thu",
        fri: "Fri",
        sat: "Sat",
      };
      const days = (schedule.recurringDays || schedule.days || []).map(
        (day) => dayNameMap[day.toLowerCase()] || day,
      );
      return days.join(", ");
    } else if (
      scheduleData &&
      typeof scheduleData === "object" &&
      Object.keys(scheduleData).length > 0
    ) {
      // New format: show only days that have time slots selected
      const dayNameMap = {
        sunday: "Sun",
        monday: "Mon",
        tuesday: "Tue",
        wednesday: "Wed",
        thursday: "Thu",
        friday: "Fri",
        saturday: "Sat",
      };
      const daysWithSlots = Object.keys(scheduleData).filter(
        (day) =>
          Array.isArray(scheduleData[day]) && scheduleData[day].length > 0,
      );
      if (daysWithSlots.length === 0) return "Select Schedule";
      if (daysWithSlots.length === 7) return "Forever";
      const days = daysWithSlots.map(
        (day) => dayNameMap[day.toLowerCase()] || day,
      );
      return days.join(", ");
    }
    return "Select Schedule";
  };
  // Debug: Log when editingActionCard changes
  useEffect(() => {
    if (editingActionCard) {
      console.log(
        "editingActionCard state updated:",
        JSON.stringify(editingActionCard, null, 2),
      );
      console.log("editingActionCard.type:", editingActionCard.type);
    }
  }, [editingActionCard]);

  useEffect(() => {
    if (editActionDialogOpen && editingActionCard?.type === "Email") {
      const cc = editingActionCard.cc;
      const bcc = editingActionCard.bcc;
      setEditDialogEmailCcVisible(cc != null && String(cc).trim() !== "");
      setEditDialogEmailBccVisible(bcc != null && String(bcc).trim() !== "");
    }
    if (!editActionDialogOpen) {
      setEditDialogEmailCcVisible(false);
      setEditDialogEmailBccVisible(false);
    }
  }, [
    editActionDialogOpen,
    editingActionCard?.id,
    editingActionCard?.type,
    editingActionCard?.cc,
    editingActionCard?.bcc,
  ]);

  useEffect(() => {
    setViewActionsPage(0);
  }, [searchQuery]);

  // Populate form fields when editingAlert is provided
  useEffect(() => {
    if (editingAlert) {
      console.log("Populating form with editingAlert data:", editingAlert);
      setAlertName(editingAlert.alertName || editingAlert.name || "");
      setSelectedSources(editingAlert.selectedSources || []);
      setSourceCategory(
        editingAlert.sourceCategory ||
          inferSourceCategoryFromIds(editingAlert.selectedSources || []) ||
          "",
      );
      {
        const raw = (editingAlert.severity || "").trim();
        const sl = raw.toLowerCase();
        const normalized =
          sl === "warning"
            ? "Medium"
            : sl === "critical"
              ? "Critical"
              : sl === "high"
                ? "High"
                : sl === "medium"
                  ? "Medium"
                  : sl === "low"
                    ? "Low"
                    : raw;
        setSeverity(normalized);
      }
      setSchedule(editingAlert.schedule || null);
      // Ensure countdown interval is within bounds
      const intervalValue =
        editingAlert.countdownInterval || COOLDOWN_DEFAULT.toString();
      const numValue = parseInt(intervalValue, 10);
      if (!isNaN(numValue)) {
        const clampedValue = Math.max(
          COOLDOWN_MIN,
          Math.min(COOLDOWN_MAX, numValue),
        );
        setCountdownInterval(clampedValue.toString());
      } else {
        setCountdownInterval(COOLDOWN_DEFAULT.toString());
      }
      const normalizeLoadedAction = (a) => {
        if (!a || typeof a !== "object") return a;
        const type = String(a.type ?? "").trim();
        const detailsRaw =
          a.details && typeof a.details === "object" && !Array.isArray(a.details)
            ? a.details
            : {};

        // Ensure details exist so downstream (dedupe + edit dialog) behave consistently.
        const details = { ...detailsRaw };

        // Some loaders store destination in `payload` only.
        if (details.chatId == null && a.payload != null) {
          details.chatId = String(a.payload);
        }

        // Normalize SMS/Phone into `{ countryCode, chatId(without cc) }` when possible.
        if (type === "SMS" || type === "Phone") {
          const phoneNumbers = String(details.chatId ?? "").trim();
          if (phoneNumbers) {
            const first = phoneNumbers.split(",")[0]?.trim() || "";
            const knownCodes = [
              "+1",
              "+91",
              "+44",
              "+86",
              "+81",
              "+49",
              "+33",
              "+39",
              "+34",
              "+61",
              "+55",
              "+7",
              "+82",
              "+65",
              "+971",
            ];
            const inferredCc =
              details.countryCode ||
              knownCodes.find((c) => first.startsWith(c)) ||
              (first.match(/^(\+\d{1,3})/)?.[1] ?? "");
            if (inferredCc) {
              details.countryCode = String(inferredCc);
              // Remove cc prefix from each phone for UI
              details.chatId = phoneNumbers
                .split(",")
                .map((p) => {
                  const t = p.trim();
                  return t.startsWith(String(inferredCc))
                    ? t.slice(String(inferredCc).length).trim()
                    : t.replace(/^\+\d{1,3}/, "").trim();
                })
                .filter(Boolean)
                .join(", ");
            }
          }
        }

        return {
          ...a,
          type,
          details,
        };
      };

      setCompletedActions((editingAlert.actions || []).map(normalizeLoadedAction));
    } else {
      // Reset form when not editing
      setAlertName("");
      setAlertType("");
      setSourceCategory("");
      setSelectedSources([]);
      setSeverity("");
      setSchedule(null);
      setCountdownInterval(COOLDOWN_DEFAULT.toString());
      setCompletedActions([]);
    }
  }, [editingAlert]);

  /** Alert Rules table: open Sub Source dialog after view-only rule load (cameras & zones). */
  useEffect(() => {
    if (pendingSubSourceBrowseRuleApiId == null || pendingSubSourceBrowseRuleApiId === "") {
      return;
    }
    if (!viewOnly || !editingAlert) return;
    const loaded = String(
      ruleApiId ?? editingAlert.id ?? editingAlert.apiId ?? "",
    ).trim();
    const want = String(pendingSubSourceBrowseRuleApiId).trim();
    if (!loaded || loaded !== want) return;
    const tid = window.setTimeout(() => {
      handleSourceDialogOpen();
      onPendingSubSourceBrowseConsumed?.();
    }, 0);
    return () => window.clearTimeout(tid);
    // handleSourceDialogOpen is stable enough for this one-shot browse flow
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pendingSubSourceBrowseRuleApiId,
    editingAlert,
    ruleApiId,
    viewOnly,
  ]);

  // When editing a rule, fetch triggers for that rule and cache their dedupe keys.
  useEffect(() => {
    if (!editingAlert) {
      setServerRuleTriggerKeyById({});
      return;
    }
    const rid = ruleApiId ?? editingAlert?.id ?? null;
    if (!rid) {
      setServerRuleTriggerKeyById({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await triggersAPI.getByRuleId(rid);
        const listRaw =
          res?.data?.triggers && Array.isArray(res.data.triggers)
            ? res.data.triggers
            : Array.isArray(res?.data)
              ? res.data
              : Array.isArray(res?.triggers)
                ? res.triggers
                : Array.isArray(res)
                  ? res
                  : [];

        const keyById = {};
        for (const t of listRaw) {
          const id = t?.id ?? t?.trigger_id;
          if (id == null) continue;
          const card = mapServerTriggerToDedupCard(t);
          if (!card) continue;
          const key = buildTriggerDedupKeyFromCard(card);
          if (!key) continue;
          keyById[String(id)] = key;
        }
        if (!cancelled) setServerRuleTriggerKeyById(keyById);
      } catch (e) {
        console.warn("Failed to load server triggers for rule:", rid, e);
        if (!cancelled) setServerRuleTriggerKeyById({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editingAlert, ruleApiId]);

  useEffect(() => {
    let cancelled = false;
    setUseCasesLoading(true);
    setUseCasesError(null);
    (async () => {
      try {
        const body = await useCasesAPI.list();
        if (cancelled) return;
        const raw = extractUseCasesFromAlertApi(body);
        const options = raw
          .filter(
            (item) =>
              item != null &&
              typeof item === "object" &&
              item.id != null &&
              String(item.id).trim() !== "",
          )
          .map((item) => ({
            id: String(item.id),
            name: String(item.name ?? "").trim() || "Untitled",
          }))
          .filter((o) => o.name.length > 0);
        setUseCaseOptions(options);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load use cases for alert type:", err);
        setUseCaseOptions([]);
        const msg =
          err?.message ||
          (typeof err === "string" ? err : "Failed to load alert types");
        setUseCasesError(msg);
        toast.error(msg, { position: "bottom-center", duration: 4000 });
      } finally {
        if (!cancelled) setUseCasesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** Resolve Alert Type Select value to use case id (dropdown uses id; API `alert_type` must be id). */
  useEffect(() => {
    if (!editingAlert) return;
    const idFromRule =
      editingAlert.usecaseId != null
        ? String(editingAlert.usecaseId).trim()
        : "";
    if (idFromRule && isProbableAlertUsecaseId(idFromRule)) {
      setAlertType(idFromRule);
      return;
    }
    const display = String(editingAlert.alertType || "").trim();
    if (!display) {
      setAlertType("");
      return;
    }
    const byName = useCaseOptions.find((u) => u.name === display);
    if (byName) {
      setAlertType(byName.id);
      return;
    }
    if (isProbableAlertUsecaseId(display)) {
      setAlertType(display);
      return;
    }
    setAlertType(display);
  }, [editingAlert, useCaseOptions]);

  useEffect(() => {
    if (sourceCategory !== "AI") {
      setAiCameras([]);
      setAiCamerasError(null);
      setAiCamerasLoading(false);
      setAiCameraZonesLoading({});
      return;
    }
    let cancelled = false;
    setAiCamerasLoading(true);
    setAiCamerasError(null);
    (async () => {
      try {
        const body = await camerasAPI.list();
        if (cancelled) return;
        const raw = extractCamerasArray(body);
        const list = raw
          .filter(
            (c) =>
              c != null &&
              typeof c === "object" &&
              (c.id != null || c.camera_id != null),
          )
          .map((c) => {
            const id = String(c.id ?? c.camera_id ?? "").trim();
            const name = String(c.name ?? "").trim() || id || "Camera";
            return { id, name, type: "camera", zones: [] };
          })
          .filter((c) => c.id.length > 0);
        setAiCameras(list);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load cameras for alert rule:", err);
        setAiCameras([]);
        const msg = err?.message || "Failed to load cameras";
        setAiCamerasError(msg);
        toast.error(msg, { position: "bottom-center", duration: 4000 });
      } finally {
        if (!cancelled) setAiCamerasLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sourceCategory]);

  const handleSubmit = () => {
    if (viewOnly) return;
    // Validation
    if (!alertName.trim()) {
      toast.error("Please enter an Alert Name", {
        position: "bottom-center",
        duration: 4000,
      });
      return;
    }
    if (!alertType) {
      toast.error("Please select an Alert Type", {
        position: "bottom-center",
        duration: 4000,
      });
      return;
    }
    if (!sourceCategory) {
      toast.error("Please select a Source (AI, IoT, or System)", {
        position: "bottom-center",
        duration: 4000,
      });
      return;
    }
    if (selectedSources.length === 0) {
      toast.error("Please select at least one Sub Source", {
        position: "bottom-center",
        duration: 4000,
      });
      return;
    }
    if (!severity) {
      toast.error("Please select a Severity level", {
        position: "bottom-center",
        duration: 4000,
      });
      return;
    }
    if (!schedule) {
      toast.error("Please configure a Schedule", {
        position: "bottom-center",
        duration: 4000,
      });
      return;
    }
    const intervalNum = parseInt(countdownInterval || "", 10);
    if (
      isNaN(intervalNum) ||
      intervalNum < COOLDOWN_MIN ||
      intervalNum > COOLDOWN_MAX
    ) {
      toast.error(
        `Cooldown interval must be between ${COOLDOWN_MIN} and ${COOLDOWN_MAX} seconds`,
        { position: "bottom-center", duration: 4000 },
      );
      return;
    }
    if (completedActions.length === 0) {
      toast.error("Please add at least one Action to the action list", {
        position: "bottom-center",
        duration: 4000,
      });
      return;
    }
    // Extract schedule in the correct format for API
    let scheduleForApi = null;

    if (schedule) {
      // Check if schedule is in the new format (has schedule property with day keys)
      if (schedule.schedule && typeof schedule.schedule === "object") {
        // New format: { schedule: { monday: [...], ... } }
        scheduleForApi = schedule.schedule;
      } else if (schedule._dialogFormat) {
        // Has dialog format stored, convert it to new format
        const converted = convertDialogScheduleToApiFormat(
          schedule._dialogFormat,
        );
        scheduleForApi = converted?.schedule || {};
      } else if (
        schedule.type === "forever" ||
        schedule.type === "recurring" ||
        schedule.days ||
        schedule.start_time
      ) {
        // Old format detected - convert to new format
        scheduleForApi = convertOldScheduleToNewFormat(schedule);
      } else if (
        typeof schedule === "object" &&
        !schedule.type &&
        !schedule.days &&
        !schedule.start_time
      ) {
        // Might be new format directly (object with day keys like monday, tuesday)
        scheduleForApi = schedule;
      } else {
        // Unknown format, use empty
        scheduleForApi = {};
      }
    }

    // Ensure scheduleForApi is always an object
    if (!scheduleForApi || typeof scheduleForApi !== "object") {
      scheduleForApi = {};
    }

    // Debug: Log the schedule format being sent
    console.log(
      "Schedule being sent to API:",
      JSON.stringify(scheduleForApi, null, 2),
    );

    const { source_ids, roi_ids } = buildRulesApiSourcePayload(
      selectedSources,
      sourceCategory,
    );

    const selectedUseCase = useCaseOptions.find((u) => u.id === alertType);
    const data = {
      id: editingAlert?.id, // Include ID if editing
      alertName,
      alertType,
      /** Human-readable label for list UI after save (Select value is use case id). */
      alertTypeDisplayName:
        selectedUseCase?.name ??
        (!isProbableAlertUsecaseId(alertType) ? alertType : null),
      sourceCategory,
      selectedSources,
      source_ids,
      roi_ids,
      severity,
      schedule: scheduleForApi,
      countdownInterval: countdownInterval || COOLDOWN_DEFAULT.toString(),
      actions: completedActions,
    };

    // If editing, call onUpdate (which should call handleUpdateAlert)
    // Otherwise, call onAdd (which should call handleAddNewAlert)
    if (editingAlert && onUpdate) {
      onUpdate(data);
    } else if (onAdd) {
      onAdd(data);
    } else if (onSubmit) {
      onSubmit(data);
    }
  };
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const getNotifiedToLabel = (action) => {
    const d = action.details || {};
    if (d.notifiedTo != null && String(d.notifiedTo).trim() !== "") {
      return String(d.notifiedTo).trim();
    }
    if (action.type === "Email") {
      return d.email || action.payload || "—";
    }
    if (action.type === "SMS" || action.type === "Phone") {
      const cc = d.countryCode ? `${d.countryCode} ` : "";
      const phone = d.chatId || action.payload;
      return phone ? `${cc}${phone}`.trim() : "—";
    }
    if (action.type === "Telegram" || action.type === "HTTP") {
      return d.chatId || action.payload || "—";
    }
    return String(action.payload || "—");
  };

  const getMessagePreview = (action) => {
    const raw = action.details?.messageBody || "";
    if (!raw) {
      return String(action.payload || "").slice(0, 120) || "—";
    }
    const text = raw
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (!text) return "—";
    return text.length > 120 ? `${text.slice(0, 117)}...` : text;
  };

  const filteredActions = completedActions.filter((action) => {
    const q = searchQuery.toLowerCase();
    if (!q) return true;
    return (
      action.type.toLowerCase().includes(q) ||
      String(action.payload || "")
        .toLowerCase()
        .includes(q) ||
      getNotifiedToLabel(action).toLowerCase().includes(q) ||
      getMessagePreview(action).toLowerCase().includes(q)
    );
  });

  const viewActionsSorted = [...filteredActions].sort((a, b) => {
    if (viewActionsOrderBy !== "notifiedTo") return 0;
    const ca = getNotifiedToLabel(a).toLowerCase();
    const cb = getNotifiedToLabel(b).toLowerCase();
    return viewActionsOrder === "asc"
      ? ca.localeCompare(cb)
      : cb.localeCompare(ca);
  });

  const viewActionsMaxPage = Math.max(
    0,
    Math.ceil(viewActionsSorted.length / viewActionsRowsPerPage) - 1,
  );
  const viewActionsPageSafe = Math.min(viewActionsPage, viewActionsMaxPage);
  const viewActionsPaginated = viewActionsSorted.slice(
    viewActionsPageSafe * viewActionsRowsPerPage,
    viewActionsPageSafe * viewActionsRowsPerPage + viewActionsRowsPerPage,
  );

  // Handle action menu
  const handleActionMenuOpen = (event, action) => {
    if (viewOnly) return;
    setActionMenuAnchor(event.currentTarget);
    setSelectedActionForMenu(action);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setSelectedActionForMenu(null);
  };

  const handleEditAction = async () => {
    if (viewOnly) return;
    if (!selectedActionForMenu) return;

    // Find the action in completedActions to get full details
    const actionToEdit = completedActions.find(
      (a) => a.id === selectedActionForMenu.id,
    );

    console.log(
      "handleEditAction - actionToEdit:",
      JSON.stringify(actionToEdit, null, 2),
    );
    console.log("handleEditAction - actionToEdit.type:", actionToEdit?.type);
    console.log(
      "handleEditAction - actionToEdit.details:",
      actionToEdit?.details,
    );

    if (!actionToEdit) {
      handleActionMenuClose();
      return;
    }

    // If action has a triggerId, fetch the trigger data from API
    if (actionToEdit.triggerId) {
      try {
        console.log(
          "Fetching trigger via GET /triggers/:trigger_id:",
          actionToEdit.triggerId,
        );
        const response = await triggersAPI.get(actionToEdit.triggerId);
        console.log(
          "Trigger API response (full):",
          JSON.stringify(response, null, 2),
        );

        // Extract trigger data from response (handle nested data structure)
        // Try multiple possible response structures
        let triggerData = {};
        if (response?.data) {
          // Check if data contains triggers array or is the trigger object itself
          if (
            response.data.triggers &&
            Array.isArray(response.data.triggers) &&
            response.data.triggers.length > 0
          ) {
            // Response structure: { data: { triggers: [...] } }
            triggerData = response.data.triggers[0];
            console.log("Extracted triggerData from response.data.triggers[0]");
          } else if (Array.isArray(response.data) && response.data.length > 0) {
            // Response structure: { data: [...] }
            triggerData = response.data[0];
            console.log(
              "Extracted triggerData from response.data array (first item)",
            );
          } else if (
            typeof response.data === "object" &&
            response.data !== null
          ) {
            // Response structure: { data: { ...triggerData } }
            triggerData = response.data;
            console.log(
              "Extracted triggerData from response.data object",
              triggerData,
            );
          }
        } else if (response?.trigger) {
          triggerData = response.trigger;
          console.log("Extracted triggerData from response.trigger");
        } else if (response && typeof response === "object") {
          triggerData = response;
          console.log("Extracted triggerData from response directly");
        }

        console.log(
          "Trigger data extracted:",
          JSON.stringify(triggerData, null, 2),
        );
        console.log("Action type from API (raw):", triggerData.action);
        console.log(
          "Action type from API (typeof):",
          typeof triggerData.action,
        );
        console.log("Config from API:", triggerData.config);
        console.log("Full triggerData keys:", Object.keys(triggerData));

        // Store the trigger ID for update
        setEditingTriggerId(actionToEdit.triggerId);

        // Initialize cardData - start fresh to ensure API data takes precedence
        const cardData = {};

        // Normalize action type (handle case variations)
        // Try to get action from multiple possible locations
        const rawAction = triggerData.action || triggerData.action_type || "";
        const actionType = rawAction
          ? String(rawAction).toUpperCase().trim()
          : "";
        console.log("Raw action from API:", rawAction);
        console.log("Raw action type:", typeof rawAction);
        console.log("Normalized action type:", actionType);
        console.log("actionToEdit.type (from action list):", actionToEdit.type);

        // Debug: Check if action type is being extracted correctly
        if (!actionType && triggerData) {
          console.warn(
            "No action type found in triggerData. Available keys:",
            Object.keys(triggerData),
          );
          console.warn("triggerData.action:", triggerData.action);
          console.warn("triggerData.action_type:", triggerData.action_type);
        }

        // PRIORITY: Use API action type FIRST (it's the source of truth from backend)
        // The API response shows the actual action type (e.g., "TELEGRAM"), so use that
        let finalActionType = "";
        if (actionType) {
          // Use the API action type (source of truth from backend)
          finalActionType = actionType;
          console.log(
            "Using API action type as primary source:",
            finalActionType,
          );
        } else if (actionToEdit.type) {
          // Fallback to action list type if API doesn't provide action type
          finalActionType = (actionToEdit.type || "").toUpperCase();
          console.log("Using actionToEdit.type as fallback:", finalActionType);
        }
        console.log("Final action type to use:", finalActionType);

        // Get config - try multiple locations
        const config = triggerData.config || triggerData;
        console.log("Config extracted:", config);

        // Set action type based on API response FIRST (critical for dialog to show correct form)
        // Always set type, even if config is missing
        console.log("Checking finalActionType:", finalActionType);
        console.log(
          "finalActionType === 'EMAIL':",
          finalActionType === "EMAIL",
        );
        console.log(
          "finalActionType === 'TELEGRAM':",
          finalActionType === "TELEGRAM",
        );

        if (finalActionType === "EMAIL") {
          // Map EMAIL trigger data to form
          cardData.type = "Email";
          console.log("✓ Mapped to Email type");
          // Handle recipients - could be array or string, check multiple possible locations
          const recipients =
            config.recipients || config.mail_ids || config.email || [];
          cardData.email = Array.isArray(recipients)
            ? recipients.join(", ")
            : recipients || "";
          cardData.subject = config.subject || triggerData.name || "";
          let messageBody =
            config.message_body || config.body || config.message || "";
          // Remove "🚨 SECURITY ALERT 🚨" from message body if present
          if (messageBody) {
            messageBody = messageBody
              .replace(/🚨\s*SECURITY\s*ALERT\s*🚨/gi, "")
              .trim();
          }
          cardData.messageBody = messageBody;
          // Handle CC - could be array or string
          const cc = config.cc || [];
          cardData.cc = Array.isArray(cc) ? cc.join(", ") : cc || "";
          // Handle BCC - could be array or string
          const bcc = config.bcc || [];
          cardData.bcc = Array.isArray(bcc) ? bcc.join(", ") : bcc || "";

          console.log("Email trigger data mapped:", {
            type: cardData.type,
            email: cardData.email,
            subject: cardData.subject,
            messageBody: cardData.messageBody,
            cc: cardData.cc,
            bcc: cardData.bcc,
          });
        } else if (finalActionType === "TELEGRAM") {
          // Map TELEGRAM trigger data to form
          cardData.type = "Telegram";
          console.log("Mapped to Telegram type");
          const chatIds = config.chat_ids || config.chat_id || [];
          cardData.chatId = Array.isArray(chatIds)
            ? chatIds.join(", ")
            : chatIds || "";
          let messageBody =
            config.message_body || config.body || config.message || "";
          // Remove "🚨 SECURITY ALERT 🚨" from message body if present
          if (messageBody) {
            messageBody = messageBody
              .replace(/🚨\s*SECURITY\s*ALERT\s*🚨/gi, "")
              .trim();
          }
          cardData.messageBody = messageBody;
          console.log("Telegram trigger data mapped:", {
            type: cardData.type,
            chatId: cardData.chatId,
            messageBody: cardData.messageBody,
          });
        } else if (finalActionType === "SMS") {
          // Map SMS trigger data to form
          cardData.type = "SMS";
          // Extract country code and phone numbers (API may send either:
          // - phone_numbers: ["+918233240231", ...]
          // - phone_number: ["..."] or "..."
          // - country_code: "+91" and phone_number: "8233240231" (or "8233, 240231")
          const normalizePhonesToArray = (raw) => {
            if (Array.isArray(raw)) return raw.map(String);
            if (raw == null) return [];
            const s = String(raw).trim();
            if (!s) return [];
            return s
              .split(",")
              .map((x) => x.trim())
              .filter(Boolean);
          };

          let phoneNumbers = normalizePhonesToArray(
            config.phone_numbers ??
              config.phone_number ??
              config.phone ??
              config.phoneNumbers,
          );

          const rawCc =
            config.country_code ??
            config.countryCode ??
            config.country ??
            config.cc ??
            "";
          const ccFromConfig = rawCc ? String(rawCc).trim() : "";
          const normalizedCc = ccFromConfig
            ? ccFromConfig.startsWith("+")
              ? ccFromConfig
              : `+${ccFromConfig.replace(/^\+/, "")}`
            : "";

          const stripLeadingCountryCode = (phone, cc) => {
            const p = String(phone ?? "").trim();
            if (!p) return "";
            if (cc && p.startsWith(cc)) return p.slice(cc.length).trim();
            return p.replace(/^\+/, "").trim(); // last-resort: drop '+' only
          };

          if (phoneNumbers.length > 0) {
            const firstPhone = String(phoneNumbers[0] ?? "").trim();

            // Prefer explicit country_code from backend; else infer from known dropdown values.
            const knownCodes = [
              "+1",
              "+91",
              "+44",
              "+86",
              "+81",
              "+49",
              "+33",
              "+39",
              "+34",
              "+61",
              "+55",
              "+7",
              "+82",
              "+65",
              "+971",
            ];
            const inferredKnown = knownCodes.find((c) => firstPhone.startsWith(c));

            let ccToUse = normalizedCc || inferredKnown || "";
            if (!ccToUse) {
              // Fallback inference (limit to 1-3 digits to avoid "+9182..." issue)
              const m = firstPhone.match(/^(\+\d{1,3})/);
              if (m) ccToUse = m[1];
            }

            if (ccToUse) {
              cardData.countryCode = ccToUse;
              cardData.chatId = phoneNumbers
                .map((p) => stripLeadingCountryCode(p, ccToUse))
                .filter(Boolean)
                .join(", ");
            } else {
              cardData.chatId = phoneNumbers.join(", ");
            }
          } else if (normalizedCc) {
            // Sometimes backend sends only country_code without phone_numbers array
            cardData.countryCode = normalizedCc;
          }
          let messageBody =
            config.message_body || config.body || config.message || "";
          // Remove "🚨 SECURITY ALERT 🚨" from message body if present
          if (messageBody) {
            messageBody = messageBody
              .replace(/🚨\s*SECURITY\s*ALERT\s*🚨/gi, "")
              .trim();
          }
          cardData.messageBody = messageBody;
        } else if (finalActionType === "HTTP") {
          // Map HTTP trigger data to form
          cardData.type = "HTTP";
          cardData.chatId = config.url || "";
          cardData.httpMethod = config.method || "POST";
          let messageBody =
            config.message_body || config.body || config.message || "";
          // Remove "🚨 SECURITY ALERT 🚨" from message body if present
          if (messageBody) {
            messageBody = messageBody
              .replace(/🚨\s*SECURITY\s*ALERT\s*🚨/gi, "")
              .trim();
          }
          cardData.messageBody = messageBody;

          // Map headers
          if (config.headers) {
            const headerEntries = Object.entries(config.headers);
            if (headerEntries.length > 0) {
              cardData.httpHeaderKey = headerEntries[0][0];
              cardData.httpHeaderValue = headerEntries[0][1];
            }
          }

          // Map params
          if (config.params) {
            const paramEntries = Object.entries(config.params);
            if (paramEntries.length > 0) {
              cardData.httpParamKey = paramEntries[0][0];
              cardData.httpParamValue = paramEntries[0][1];
            }
          }

          // Map authorization
          if (config.authorization) {
            if (config.authorization.startsWith("Bearer ")) {
              cardData.authType = "Bearer";
              cardData.authAccount = config.authorization.replace(
                "Bearer ",
                "",
              );
            } else if (config.authorization.startsWith("Basic ")) {
              cardData.authType = "Basic";
              // Decode Basic auth (base64)
              try {
                const decoded = atob(
                  config.authorization.replace("Basic ", ""),
                );
                const [account, password] = decoded.split(":");
                cardData.authAccount = account;
                cardData.authPassword = password;
              } catch (e) {
                console.warn("Failed to decode Basic auth:", e);
              }
            }
          }
        } else {
          // If action type is not recognized, use actionToEdit.type as fallback
          console.warn(
            "Unknown action type from API:",
            finalActionType,
            "Using actionToEdit.type as fallback",
          );
          if (actionToEdit.type) {
            cardData.type = actionToEdit.type;
            console.log("Set type from actionToEdit.type:", cardData.type);
            // Try to preserve existing details
            if (actionToEdit.details) {
              Object.assign(cardData, actionToEdit.details);
            }
          } else {
            // Last resort: default to Email
            cardData.type = "Email";
            console.warn("No type found, defaulting to Email");
          }
        }

        // Ensure type is ALWAYS set (critical for dialog to show correct form)
        if (!cardData.type) {
          console.error(
            "Action type not set after API fetch. Trigger data:",
            triggerData,
          );
          console.error("Action type from API was:", triggerData.action);
          console.error("Normalized action type was:", finalActionType);
          console.error("actionToEdit:", actionToEdit);

          // Try multiple fallback strategies
          if (actionToEdit.type) {
            cardData.type = actionToEdit.type;
            console.warn("Using actionToEdit.type as fallback:", cardData.type);
          } else if (actionToEdit.details?.type) {
            cardData.type = actionToEdit.details.type;
            console.warn(
              "Using actionToEdit.details.type as fallback:",
              cardData.type,
            );
          } else {
            // Last resort: try to infer from action string or default to Email
            const actionLower = (
              triggerData.action ||
              finalActionType ||
              ""
            ).toLowerCase();
            if (actionLower.includes("email") || actionLower.includes("mail")) {
              cardData.type = "Email";
            } else if (actionLower.includes("telegram")) {
              cardData.type = "Telegram";
            } else if (actionLower.includes("sms")) {
              cardData.type = "SMS";
            } else if (
              actionLower.includes("http") ||
              actionLower.includes("webhook")
            ) {
              cardData.type = "HTTP";
            } else {
              cardData.type = "Email"; // Default fallback
            }
            console.warn(
              "Using inferred/default type as fallback:",
              cardData.type,
            );
          }
        }

        // Final safety check - if type is still not set, force it
        // BUT: Only use Email as last resort if we truly can't determine the type
        if (!cardData.type) {
          console.error("CRITICAL: Type still not set after all checks!");
          console.error("finalActionType was:", finalActionType);
          console.error("actionToEdit.type was:", actionToEdit.type);
          console.error("triggerData.action was:", triggerData.action);

          // Try one more time to get type from finalActionType
          if (finalActionType) {
            // Map finalActionType directly to cardData.type
            if (finalActionType === "TELEGRAM") {
              cardData.type = "Telegram";
            } else if (finalActionType === "EMAIL") {
              cardData.type = "Email";
            } else if (finalActionType === "SMS") {
              cardData.type = "SMS";
            } else if (finalActionType === "HTTP") {
              cardData.type = "HTTP";
            } else {
              // Only default to Email if we truly don't know
              cardData.type = actionToEdit.type || "Email";
            }
            console.error(
              "Set cardData.type from finalActionType:",
              cardData.type,
            );
          } else {
            // Last resort: use actionToEdit.type or default to Email
            cardData.type = actionToEdit.type || "Email";
            console.error("Forced cardData.type to:", cardData.type);
          }
        }

        console.log(
          "Final cardData for edit dialog:",
          JSON.stringify(cardData, null, 2),
        );
        console.log("Action type set to:", cardData.type);
        console.log("Will set editingActionCard with type:", cardData.type);

        const cfgNotify = triggerData.config || triggerData;
        const rootNotifiedTo =
          triggerData.notified_to != null &&
          String(triggerData.notified_to).trim() !== ""
            ? String(triggerData.notified_to).trim()
            : "";
        cardData.notifiedTo =
          cardData.notifiedTo ||
          rootNotifiedTo ||
          cfgNotify?.notified_to ||
          cfgNotify?.notifiedTo ||
          actionToEdit.details?.notifiedTo ||
          "";

        // Set the card data for editing in dialog
        const finalCardData = {
          id: actionToEdit.id, // Keep the original action ID
          ...cardData,
        };

        // CRITICAL: Ensure type is in finalCardData before setting state
        if (!finalCardData.type) {
          console.error(
            "CRITICAL: finalCardData.type is missing before setting state!",
          );
          console.error("cardData:", cardData);
          console.error("finalCardData before fix:", finalCardData);
          // Try to get type from actionToEdit
          if (actionToEdit.type) {
            finalCardData.type = actionToEdit.type;
            console.log(
              "Set finalCardData.type from actionToEdit.type:",
              finalCardData.type,
            );
          } else if (actionToEdit.details?.type) {
            finalCardData.type = actionToEdit.details.type;
            console.log(
              "Set finalCardData.type from actionToEdit.details.type:",
              finalCardData.type,
            );
          } else {
            // Check completedActions
            const actionFromList = completedActions.find(
              (a) => a.id === actionToEdit.id,
            );
            if (actionFromList?.type) {
              finalCardData.type = actionFromList.type;
              console.log(
                "Set finalCardData.type from completedActions:",
                finalCardData.type,
              );
            } else {
              finalCardData.type = "Email"; // Absolute last resort
              console.error(
                "Forced finalCardData.type to Email as last resort",
              );
            }
          }
        }

        console.log(
          "Setting editingActionCard:",
          JSON.stringify(finalCardData, null, 2),
        );
        console.log("finalCardData.type:", finalCardData.type);
        setEditingActionCard(finalCardData);
        setEditingActionId(actionToEdit.id); // Store action ID for update

        // Open the edit dialog - React will batch the state updates
        setEditActionDialogOpen(true);
      } catch (error) {
        console.error("Error fetching trigger:", error);
        alert(
          `Failed to fetch trigger: ${error.message || "Please try again"}`,
        );
      }
    } else {
      // No triggerId, use existing details
      if (actionToEdit.details) {
        setEditingActionCard({
          id: actionToEdit.id,
          ...actionToEdit.details,
        });
        setEditingActionId(actionToEdit.id);
        setEditActionDialogOpen(true);
      }
    }

    handleActionMenuClose();
  };

  const handleDeleteAction = () => {
    if (viewOnly) return;
    if (!selectedActionForMenu) return;

    // Find the action in completedActions to get full details
    const action = completedActions.find(
      (a) => a.id === selectedActionForMenu.id,
    );

    if (!action) {
      handleActionMenuClose();
      return;
    }

    // Store the action to delete (so it persists after menu closes)
    setActionToDelete(action);

    // Open confirmation dialog
    setDeleteConfirmDialogOpen(true);
    handleActionMenuClose(); // Close the menu
  };

  const handleConfirmDeleteAction = async () => {
    console.log(
      "handleConfirmDeleteAction called, actionToDelete:",
      actionToDelete,
    );

    if (!actionToDelete) {
      console.log("No action to delete, closing dialog");
      setDeleteConfirmDialogOpen(false);
      setActionToDelete(null);
      return;
    }

    // If action has a triggerId, delete the trigger via API
    if (actionToDelete.triggerId) {
      try {
        console.log("Deleting trigger with ID:", actionToDelete.triggerId);
        const response = await triggersAPI.delete(actionToDelete.triggerId);
        console.log("Trigger deleted successfully, response:", response);

        // Remove the action from completedActions
        setCompletedActions(
          completedActions.filter((a) => a.id !== actionToDelete.id),
        );

        setDeleteConfirmDialogOpen(false);
        setActionToDelete(null);
      } catch (error) {
        console.error("Error deleting trigger:", error);
        alert(
          `Failed to delete trigger: ${error.message || "Please try again"}`,
        );
        setDeleteConfirmDialogOpen(false);
        setActionToDelete(null);
      }
    } else {
      // No triggerId, just remove from local state
      setCompletedActions(
        completedActions.filter((a) => a.id !== actionToDelete.id),
      );
      setDeleteConfirmDialogOpen(false);
      setActionToDelete(null);
    }
  };

  // Handle editing action card in dialog
  const handleEditActionCardChange = (field, value) => {
    if (editingActionCard) {
      setEditingActionCard({
        ...editingActionCard,
        [field]: value,
      });
    }

    // Clear error when user starts typing in email fields
    if (field === "email" || field === "cc" || field === "bcc") {
      setEmailErrors((prev) => ({
        ...prev,
        editDialog: {
          ...prev.editDialog,
          [field]: "",
        },
      }));
    }

    if (field === "messageBody" && editingActionCard?.type === "SMS") {
      const len = plainTextLengthFromMessageHtml(String(value));
      if (len > 160) {
        setSmsErrors((prev) => ({
          ...prev,
          editDialog: {
            ...prev.editDialog,
            messageBody: "Message body must be 160 characters or less",
          },
        }));
      } else {
        setSmsErrors((prev) => ({
          ...prev,
          editDialog: {
            ...prev.editDialog,
            messageBody: "",
          },
        }));
      }
    }

    if (field === "chatId" && editingActionCard?.type === "SMS") {
      setSmsErrors((prev) => ({
        ...prev,
        editDialog: {
          ...prev.editDialog,
          phoneNumber: "",
        },
      }));
    }

    // Clear error when user starts typing in HTTP fields
    if (
      field === "chatId" &&
      editingActionCard &&
      editingActionCard.type === "HTTP"
    ) {
      setHttpErrors((prev) => ({
        ...prev,
        editDialog: {
          ...prev.editDialog,
          url: "",
        },
      }));
    }
  };

  // Save edited action (update trigger via API)
  const handleSaveEditedAction = async () => {
    if (!editingActionCard || !editingTriggerId) return;

    const card = editingActionCard;

    // Prevent duplicates when saving edits (exclude the current action itself).
    if (isDuplicateTriggerCard(card, { excludeActionId: editingActionId })) {
      alert("Duplicate action detected. Please change destination or message before saving.");
      return;
    }
    if (
      editingAlert &&
      isDuplicateAgainstServerRuleTriggers(card, { excludeTriggerId: editingTriggerId })
    ) {
      alert(
        "This action already exists on the server for this rule (duplicate trigger).",
      );
      return;
    }

    // Validate based on action type
    if (card.type === "Email") {
      if (!card.email || !card.subject || !card.messageBody) {
        alert(
          "Please fill in all required email fields (To, Subject, and Message Body)",
        );
        return;
      }

      // Validate email addresses before proceeding
      const emailValidation = validateEmailAddresses(card.email);
      if (!emailValidation.isValid) {
        alert(emailValidation.error);
        return;
      }

      // Validate CC emails if provided
      if (card.cc) {
        const ccValidation = validateEmailAddresses(card.cc);
        if (!ccValidation.isValid) {
          alert(`CC: ${ccValidation.error}`);
          return;
        }
      }

      // Validate BCC emails if provided
      if (card.bcc) {
        const bccValidation = validateEmailAddresses(card.bcc);
        if (!bccValidation.isValid) {
          alert(`BCC: ${bccValidation.error}`);
          return;
        }
      }
    } else if (card.type === "Telegram") {
      if (
        !card.chatId ||
        !plainMessageTextFromHtml(card.messageBody || "").trim()
      ) {
        alert("Please fill in Chat ID and Message Body");
        return;
      }
    } else if (card.type === "SMS") {
      if (
        !card.chatId ||
        !plainMessageTextFromHtml(card.messageBody || "").trim()
      ) {
        alert("Please fill in Phone Number and Message Body");
        return;
      }

      // Validate phone numbers before proceeding
      const phoneValidation = validatePhoneNumbers(
        card.chatId,
        card.countryCode,
      );
      if (!phoneValidation.isValid) {
        setSmsErrors((prev) => ({
          ...prev,
          editDialog: {
            ...prev.editDialog,
            phoneNumber: phoneValidation.error,
          },
        }));
        return;
      }

      const messageValidation = validateSmsMessageBodyRich(card.messageBody);
      if (!messageValidation.isValid) {
        setSmsErrors((prev) => ({
          ...prev,
          editDialog: {
            ...prev.editDialog,
            messageBody: messageValidation.error,
          },
        }));
        return;
      }

      setSmsErrors((prev) => ({
        ...prev,
        editDialog: {
          phoneNumber: "",
          messageBody: "",
        },
      }));
    } else if (card.type === "HTTP") {
      if (!card.chatId) {
        alert("Please fill in Endpoint URL");
        return;
      }

      // Validate URL before proceeding
      const urlValidation = validateURL(card.chatId);
      if (!urlValidation.isValid) {
        setHttpErrors((prev) => ({
          ...prev,
          editDialog: {
            ...prev.editDialog,
            url: urlValidation.error,
          },
        }));
        return;
      }

      // Clear errors if validation passes
      setHttpErrors((prev) => ({
        ...prev,
        editDialog: {
          url: "",
        },
      }));
    }

    try {
      // Prepare trigger data based on action type (same as handleAddActionToList)
      let triggerData = {};
      let payload = "";

      if (card.type === "Email") {
        // Use validated emails (validation already done above)
        const emailValidation = validateEmailAddresses(card.email);
        const emailAddresses = emailValidation.emails;

        triggerData = {
          action: "EMAIL",
          name: card.subject || "Security Alert",
          config: {
            message_body: card.messageBody || "",
            recipients: emailAddresses,
            subject: card.subject || "Security Alert",
          },
        };

        if (card.cc) {
          const ccValidation = validateEmailAddresses(card.cc);
          if (ccValidation.isValid && ccValidation.emails.length > 0) {
            triggerData.config.cc = ccValidation.emails;
          }
        }

        if (card.bcc) {
          const bccValidation = validateEmailAddresses(card.bcc);
          if (bccValidation.isValid && bccValidation.emails.length > 0) {
            triggerData.config.bcc = bccValidation.emails;
          }
        }

        applyNotifiedToToTriggerPayload(triggerData, card);

        payload = card.email;
      } else if (card.type === "Telegram") {
        const chatIds = card.chatId
          ? card.chatId
              .split(",")
              .map((id) => {
                const trimmed = id.trim();
                const numId = parseInt(trimmed, 10);
                return isNaN(numId) ? null : numId;
              })
              .filter((id) => id !== null)
          : [];

        if (chatIds.length === 0) {
          alert("Please enter valid Telegram chat IDs (numbers)");
          return;
        }

        triggerData = {
          name: card.messageBody
            ? card.messageBody.substring(0, 50) + "..."
            : "Security Alert Telegram",
          event: "SECURITY_ALERT",
          action: "TELEGRAM",
          config: {
            chat_ids: chatIds,
            message_body: card.messageBody || "",
          },
        };

        applyNotifiedToToTriggerPayload(triggerData, card);

        payload = card.chatId;
      } else if (card.type === "SMS") {
        const countryCode = card.countryCode || "+1";
        const phoneNumbers = card.chatId
          ? card.chatId
              .split(",")
              .map((phone) => {
                const trimmed = phone.trim();
                if (trimmed.startsWith("+")) {
                  return trimmed;
                }
                return countryCode + trimmed;
              })
              .filter((phone) => phone.length > 0)
          : [];

        if (phoneNumbers.length === 0) {
          alert("Please enter at least one phone number");
          return;
        }

        triggerData = {
          name: card.messageBody
            ? card.messageBody.substring(0, 50) + "..."
            : "Security SMS Alert",
          event: "SECURITY_ALERT",
          action: "SMS",
          config: {
            phone_numbers: phoneNumbers,
            message_body: card.messageBody || "",
          },
        };

        applyNotifiedToToTriggerPayload(triggerData, card);

        payload = card.chatId;
      } else if (card.type === "HTTP") {
        let headers = {};
        if (card.httpHeaderKey && card.httpHeaderValue) {
          headers[card.httpHeaderKey] = card.httpHeaderValue;
        }

        let params = {};
        if (card.httpParamKey && card.httpParamValue) {
          params[card.httpParamKey] = card.httpParamValue;
        }

        let authorization = null;
        if (card.authType && card.authType !== "No Auth") {
          if (card.authType === "Bearer") {
            const token = card.authToken || card.authAccount || "";
            if (token) {
              authorization = `Bearer ${token}`;
            }
          } else if (
            card.authType === "Basic" &&
            card.authAccount &&
            card.authPassword
          ) {
            const credentials = btoa(
              `${card.authAccount}:${card.authPassword}`,
            );
            authorization = `Basic ${credentials}`;
          }
        }

        triggerData = {
          name: card.messageBody
            ? card.messageBody.substring(0, 50) + "..."
            : "Webhook Alert with Message",
          event: "DETECTION_ALERT",
          action: "HTTP",
          config: {
            method: card.httpMethod || "POST",
            url: card.chatId || "",
            ...(authorization && { authorization: authorization }),
            headers: headers,
            params: params,
            message_body: card.messageBody || "",
          },
        };

        applyNotifiedToToTriggerPayload(triggerData, card);

        payload = card.chatId;
      }

      // Update trigger via API using PUT method to /triggers/:trigger_id
      console.log(
        "Updating trigger via PUT /triggers/:trigger_id:",
        editingTriggerId,
        "with data:",
        triggerData,
      );
      const response = await triggersAPI.update(editingTriggerId, triggerData);
      console.log("Trigger updated successfully:", response);

      // Update the action in completedActions
      const updatedAction = {
        id: editingActionId,
        type: card.type,
        payload: payload,
        triggerId: editingTriggerId,
        details: { ...card },
      };

      setCompletedActions(
        completedActions.map((action) =>
          action.id === editingActionId ? updatedAction : action,
        ),
      );

      // Close dialog and reset state
      setEditActionDialogOpen(false);
      setEditingActionCard(null);
      setEditingTriggerId(null);
      setEditingActionId(null);
    } catch (error) {
      console.error("Error updating trigger:", error);
      alert(`Failed to update trigger: ${error.message || "Please try again"}`);
    }
  };

  const filteredSources = sourceData.filter((source) =>
    source.name.toLowerCase().includes(sourceSearchQuery.toLowerCase()),
  );

  const filteredAiCameras = useMemo(
    () =>
      aiCameras.filter((c) =>
        c.name.toLowerCase().includes(sourceSearchQuery.toLowerCase()),
      ),
    [aiCameras, sourceSearchQuery],
  );

  const allFilteredAiCamerasSelected = useMemo(() => {
    const list = filteredAiCameras;
    if (list.length === 0) return false;
    return list.every((c) => tempSelectedSources.includes(c.id));
  }, [filteredAiCameras, tempSelectedSources]);

  const insertEmailEditorToken = (editorDataId, token, syncHtml) => {
    const editor = document.querySelector(
      `[data-email-editor-id="${editorDataId}"]`,
    );
    if (!editor) return;
    editor.focus();
    try {
      const sel = window.getSelection();
      if (sel?.rangeCount) {
        const range = sel.getRangeAt(0);
        range.deleteContents();
        const textNode = document.createTextNode(token);
        range.insertNode(textNode);
        range.setStartAfter(textNode);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      } else {
        document.execCommand("insertText", false, token);
      }
    } catch {
      document.execCommand("insertText", false, token);
    }
    syncHtml(editor.innerHTML);
  };

  const insertEmailShortcode = (cardId, token) => {
    insertEmailEditorToken(cardId, token, (html) => {
      handleActionCardChange(cardId, "messageBody", html);
      updateFormattingState(cardId);
    });
  };

  const insertEditDialogEmailShortcode = (token) => {
    insertEmailEditorToken("edit-dialog", token, (html) => {
      handleEditActionCardChange("messageBody", html);
      updateEditDialogFormattingState();
    });
  };

  const messagePlaceholderPickerPanelSx = {
    border: "1px solid #e8eaef",
    borderRadius: "10px",
    backgroundColor: "#fafafa",
    p: 1.5,
    display: "flex",
    flexDirection: "column",
  };

  /** md: one row so message body column height matches placeholder column */
  const actionMessagePlaceholderGridSx = {
    display: { xs: "flex", md: "grid" },
    flexDirection: { xs: "column" },
    gridTemplateColumns: { md: "minmax(0, 1fr) minmax(168px, 260px)" },
    gridTemplateRows: { md: "minmax(300px, max-content)" },
    gap: 2,
    alignItems: "stretch",
  };

  const actionMessageBodyColumnSx = {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    height: { md: "100%" },
    minHeight: { md: 0 },
  };

  const actionPlaceholderColumnSx = {
    minWidth: { md: 168 },
    maxWidth: { md: 260 },
    border: "1px solid #e8eaef",
    borderRadius: "10px",
    backgroundColor: "#fafafa",
    p: 1.5,
    display: "flex",
    flexDirection: "column",
    height: { md: "100%" },
    minHeight: { md: 0 },
    overflow: "hidden",
  };

  const actionPlaceholderColumnEditSx = {
    ...actionPlaceholderColumnSx,
    maxWidth: { md: 280 },
  };

  const actionMessagePlaceholderGridEditSx = {
    ...actionMessagePlaceholderGridSx,
    gridTemplateColumns: { md: "minmax(0, 1fr) minmax(168px, 280px)" },
  };

  const renderMessagePlaceholderPicker = ({
    onPickToken,
    title = "Click a link below to insert a placeholder into your message:",
    maxHeight = { md: 360 },
    scroll = true,
    wrapInPanel = false,
    /** When true (md), list scroll area flexes so panel height matches sibling message column */
    stretchList = false,
  }) => {
    const list = messagePlaceholdersCatalog?.placeholders;
    const syntaxHint = messagePlaceholdersCatalog?.placeholder_syntax;

    const listScrollSx = scroll
      ? stretchList
        ? {
            flex: { md: 1 },
            minHeight: { md: 0 },
            overflow: "auto",
            maxHeight: { xs: maxHeight, md: "none" },
          }
        : { overflow: "auto", maxHeight }
      : {};

    const innerBody = (
      <>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: "#4A5565",
            mb: syntaxHint ? 0.75 : 1.5,
            lineHeight: 1.4,
            fontSize: "12px",
            flexShrink: 0,
          }}
        >
          {title}
        </Typography>
        {syntaxHint ? (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "#6b7280",
              mb: 1.25,
              lineHeight: 1.4,
              fontSize: "11px",
              flexShrink: 0,
            }}
          >
            {syntaxHint}
          </Typography>
        ) : null}
        {messagePlaceholdersLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 2,
              flex: stretchList ? { md: 1 } : undefined,
              minHeight: stretchList ? { md: 0 } : undefined,
            }}
          >
            <CircularProgress size={22} />
          </Box>
        ) : Array.isArray(list) && list.length > 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
              ...listScrollSx,
            }}
          >
            {list.map((p) => {
              const token = formatMessagePlaceholderToken(p.key);
              if (!token) return null;
              return (
                <Link
                  key={p.key}
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => onPickToken(token)}
                  sx={{
                    textAlign: "left",
                    fontSize: "13px",
                    cursor: "pointer",
                    border: "none",
                    background: "none",
                    p: 0,
                    color: "#2932E5",
                    fontFamily: "Roboto, sans-serif",
                    "&:hover": { color: "#1f28c7" },
                  }}
                >
                  {p.label || p.key}
                </Link>
              );
            })}
          </Box>
        ) : (
          <Typography
            variant="caption"
            sx={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.4 }}
          >
            Placeholders could not be loaded. Refresh the page or try again
            later.
          </Typography>
        )}
      </>
    );

    const inner = stretchList ? (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: { md: "100%" },
          minHeight: { md: 0 },
          flex: { md: 1 },
        }}
      >
        {innerBody}
      </Box>
    ) : (
      innerBody
    );

    if (wrapInPanel) {
      return <Box sx={messagePlaceholderPickerPanelSx}>{inner}</Box>;
    }
    return inner;
  };

  const renderSharedNotifiedToField = (card) => (
    <Box sx={{ mb: 2 }}>
      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 500,
          color: "#212121",
          mb: 1,
          letterSpacing: "0.15px",
        }}
      >
        Notified to
      </Typography>
      <TextField
        fullWidth
        size="small"
        placeholder="Names or teams (comma-separated)"
        value={card.notifiedTo ?? ""}
        onChange={(e) =>
          handleActionCardChange(card.id, "notifiedTo", e.target.value)
        }
        helperText="Who is notified for this action (display and reporting)"
        FormHelperTextProps={{ sx: { mt: 0.5 } }}
        sx={{
          "& .MuiOutlinedInput-root": {
            backgroundColor: "white",
            borderRadius: "10px",
            "& fieldset": { borderColor: "#e0e0e0" },
          },
        }}
      />
    </Box>
  );

  const renderActionCardFields = (card) => {
    if (card.type === "Email") {
      const showCcRow =
        !!emailCcBccVisible[card.id]?.cc ||
        (card.cc != null && String(card.cc).trim() !== "") ||
        !!emailErrors[card.id]?.cc;
      const showBccRow =
        !!emailCcBccVisible[card.id]?.bcc ||
        (card.bcc != null && String(card.bcc).trim() !== "") ||
        !!emailErrors[card.id]?.bcc;

      const focusEmailField = (elementId) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            document.getElementById(elementId)?.focus?.();
          });
        });
      };

      const revealEmailCcRow = () => {
        setEmailCcBccVisible((prev) => ({
          ...prev,
          [card.id]: { ...(prev[card.id] || {}), cc: true },
        }));
        focusEmailField(`email-cc-${card.id}`);
      };

      const revealEmailBccRow = () => {
        setEmailCcBccVisible((prev) => ({
          ...prev,
          [card.id]: { ...(prev[card.id] || {}), bcc: true },
        }));
        focusEmailField(`email-bcc-${card.id}`);
      };

      return (
        <>
          <Box sx={GMAIL_COMPOSE_OUTER_SX}>
            <Box sx={gmailComposeRowSx(true)}>
              <Typography component="span" sx={GMAIL_COMPOSE_LABEL_SX}>
                Notified to
              </Typography>
              <TextField
                variant="standard"
                hiddenLabel
                fullWidth
                placeholder="Names or teams, comma-separated"
                value={card.notifiedTo ?? ""}
                onChange={(e) =>
                  handleActionCardChange(card.id, "notifiedTo", e.target.value)
                }
                InputProps={{ disableUnderline: true }}
                sx={gmailComposeTextFieldSx}
              />
            </Box>
            <Box sx={gmailComposeRowSx(true)}>
              <Typography component="span" sx={GMAIL_COMPOSE_LABEL_SX}>
                To
              </Typography>
              <TextField
                variant="standard"
                hiddenLabel
                fullWidth
                placeholder="Recipients"
                value={card.email || ""}
                onChange={(e) =>
                  handleActionCardChange(card.id, "email", e.target.value)
                }
                error={!!emailErrors[card.id]?.email}
                InputProps={{ disableUnderline: true }}
                sx={gmailComposeTextFieldSx}
                inputProps={{
                  "aria-invalid": !!emailErrors[card.id]?.email,
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  flexShrink: 0,
                }}
              >
                {!showCcRow && (
                  <Link
                    component="button"
                    type="button"
                    underline="none"
                    onClick={(e) => {
                      e.preventDefault();
                      revealEmailCcRow();
                    }}
                    sx={gmailComposeLinkSx}
                  >
                    Cc
                  </Link>
                )}
                {!showBccRow && (
                  <Link
                    component="button"
                    type="button"
                    underline="none"
                    onClick={(e) => {
                      e.preventDefault();
                      revealEmailBccRow();
                    }}
                    sx={gmailComposeLinkSx}
                  >
                    Bcc
                  </Link>
                )}
              </Box>
            </Box>
            {showCcRow && (
              <Box sx={gmailComposeRowSx(true)}>
                <Typography component="span" sx={GMAIL_COMPOSE_LABEL_SX}>
                  Cc
                </Typography>
                <TextField
                  id={`email-cc-${card.id}`}
                  variant="standard"
                  hiddenLabel
                  fullWidth
                  placeholder="Carbon copy"
                  value={card.cc || ""}
                  onChange={(e) =>
                    handleActionCardChange(card.id, "cc", e.target.value)
                  }
                  error={!!emailErrors[card.id]?.cc}
                  InputProps={{ disableUnderline: true }}
                  sx={gmailComposeTextFieldSx}
                />
              </Box>
            )}
            {showBccRow && (
              <Box sx={gmailComposeRowSx(true)}>
                <Typography component="span" sx={GMAIL_COMPOSE_LABEL_SX}>
                  Bcc
                </Typography>
                <TextField
                  id={`email-bcc-${card.id}`}
                  variant="standard"
                  hiddenLabel
                  fullWidth
                  placeholder="Blind carbon copy"
                  value={card.bcc || ""}
                  onChange={(e) =>
                    handleActionCardChange(card.id, "bcc", e.target.value)
                  }
                  error={!!emailErrors[card.id]?.bcc}
                  InputProps={{ disableUnderline: true }}
                  sx={gmailComposeTextFieldSx}
                />
              </Box>
            )}
            <Box sx={gmailComposeRowSx(false)}>
              <Typography component="span" sx={GMAIL_COMPOSE_LABEL_SX}>
                Subject
              </Typography>
              <TextField
                variant="standard"
                hiddenLabel
                fullWidth
                placeholder="Subject"
                value={card.subject || ""}
                onChange={(e) =>
                  handleActionCardChange(card.id, "subject", e.target.value)
                }
                InputProps={{ disableUnderline: true }}
                sx={gmailComposeTextFieldSx}
              />
            </Box>
          </Box>
          {(emailErrors[card.id]?.email ||
            emailErrors[card.id]?.cc ||
            emailErrors[card.id]?.bcc) && (
            <Box sx={{ mb: 1.5, pl: 0.5 }}>
              {emailErrors[card.id]?.email && (
                <Typography
                  component="div"
                  variant="caption"
                  color="error"
                  sx={{ display: "block" }}
                >
                  To: {emailErrors[card.id].email}
                </Typography>
              )}
              {emailErrors[card.id]?.cc && (
                <Typography
                  component="div"
                  variant="caption"
                  color="error"
                  sx={{ display: "block" }}
                >
                  Cc: {emailErrors[card.id].cc}
                </Typography>
              )}
              {emailErrors[card.id]?.bcc && (
                <Typography
                  component="div"
                  variant="caption"
                  color="error"
                  sx={{ display: "block" }}
                >
                  Bcc: {emailErrors[card.id].bcc}
                </Typography>
              )}
            </Box>
          )}

          <Box sx={actionMessagePlaceholderGridSx}>
            <Box sx={actionMessageBodyColumnSx}>
              <Box
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "10px",
                  backgroundColor: "white",
                  overflow: "hidden",
                  flex: 1,
                  minHeight: { md: 0 },
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 0.5,
                    p: 1,
                    borderBottom: "1px solid #e0e0e0",
                    backgroundColor: "#fafafa",
                  }}
                >
                  <IconButton
                    size="small"
                    title="Attach files"
                    sx={{
                      color: "#5f6368",
                      padding: "6px",
                      "&:hover": {
                        backgroundColor: "rgba(95, 99, 104, 0.1)",
                      },
                    }}
                    onClick={() => {
                      const input = document.createElement("input");
                      input.type = "file";
                      input.multiple = true;
                      input.onchange = (e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length) console.log("Files selected:", files);
                      };
                      input.click();
                    }}
                  >
                    <AttachFileIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    title="Bold"
                    sx={{
                      color: formattingState[card.id]?.bold
                        ? "#2932E5"
                        : "#5f6368",
                      padding: "6px",
                      backgroundColor: formattingState[card.id]?.bold
                        ? "rgba(41, 50, 229, 0.08)"
                        : "transparent",
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      const editor = document.querySelector(
                        `[data-email-editor-id="${card.id}"]`,
                      );
                      if (editor) {
                        editor.focus();
                        document.execCommand("bold", false, null);
                        setTimeout(() => {
                          handleActionCardChange(
                            card.id,
                            "messageBody",
                            editor.innerHTML,
                          );
                          updateFormattingState(card.id);
                        }, 0);
                      }
                    }}
                  >
                    <FormatBoldIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    title="Italic"
                    sx={{
                      color: formattingState[card.id]?.italic
                        ? "#2932E5"
                        : "#5f6368",
                      padding: "6px",
                      backgroundColor: formattingState[card.id]?.italic
                        ? "rgba(41, 50, 229, 0.08)"
                        : "transparent",
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      const editor = document.querySelector(
                        `[data-email-editor-id="${card.id}"]`,
                      );
                      if (editor) {
                        editor.focus();
                        document.execCommand("italic", false, null);
                        setTimeout(() => {
                          handleActionCardChange(
                            card.id,
                            "messageBody",
                            editor.innerHTML,
                          );
                          updateFormattingState(card.id);
                        }, 0);
                      }
                    }}
                  >
                    <FormatItalicIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    title="Underline"
                    sx={{
                      color: formattingState[card.id]?.underline
                        ? "#2932E5"
                        : "#5f6368",
                      padding: "6px",
                      backgroundColor: formattingState[card.id]?.underline
                        ? "rgba(41, 50, 229, 0.08)"
                        : "transparent",
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      const editor = document.querySelector(
                        `[data-email-editor-id="${card.id}"]`,
                      );
                      if (editor) {
                        editor.focus();
                        document.execCommand("underline", false, null);
                        setTimeout(() => {
                          handleActionCardChange(
                            card.id,
                            "messageBody",
                            editor.innerHTML,
                          );
                          updateFormattingState(card.id);
                        }, 0);
                      }
                    }}
                  >
                    <FormatUnderlinedIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box
                  contentEditable
                  suppressContentEditableWarning
                  data-email-editor-id={card.id}
                  ref={(el) => {
                    if (!el) return;
                    const next = messageBodyToEditorHtml(card.messageBody);
                    if (el.innerHTML !== next) el.innerHTML = next;
                  }}
                  onInput={(e) => {
                    const htmlContent = e.target.innerHTML;
                    handleActionCardChange(card.id, "messageBody", htmlContent);
                    updateFormattingState(card.id);
                  }}
                  onBlur={(e) => {
                    handleActionCardChange(
                      card.id,
                      "messageBody",
                      e.target.innerHTML,
                    );
                  }}
                  onKeyUp={() => updateFormattingState(card.id)}
                  onMouseUp={() => updateFormattingState(card.id)}
                  sx={{
                    flex: 1,
                    minHeight: { xs: 200, md: 0 },
                    fontSize: "14px",
                    color: "#202124",
                    fontFamily: "Roboto, sans-serif",
                    lineHeight: 1.5,
                    outline: "none",
                    p: 2,
                    "&:empty:before": {
                      content: '"Compose your message."',
                      color: "#5f6368",
                      opacity: 1,
                    },
                  }}
                />
              </Box>
            </Box>

            <Box sx={actionPlaceholderColumnSx}>
              {renderMessagePlaceholderPicker({
                title:
                  "Click a link below to insert a placeholder into your email:",
                onPickToken: (token) => insertEmailShortcode(card.id, token),
                maxHeight: { md: 360 },
                scroll: true,
                stretchList: true,
              })}
            </Box>
          </Box>
        </>
      );
    } else if (card.type === "Telegram") {
      return (
        <>
          {renderSharedNotifiedToField(card)}
          {/* Get Chat ID Link Section */}
          <Box
            sx={{
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#212121",
                  letterSpacing: "0.15px",
                }}
              >
                Get Chat ID
              </Typography>
              <InfoIcon
                sx={{
                  fontSize: 16,
                  color: "#212121",
                }}
              />
            </Box>
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: "10px",
                border: "1px solid #e0e0e0",
                p: 2,
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#4a5565",
                  m: 0,
                  lineHeight: 1.5,
                }}
              >
                Click the link to get your Chat ID from the Telegram bot:
              </Typography>
              <Link
                href="https://t.me/allvision_alert_bot?start=register"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontSize: "14px",
                  color: "#2932E5",
                  textDecoration: "none",
                  fontWeight: 500,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  wordBreak: "break-all",
                  "&:hover": {
                    textDecoration: "underline",
                    color: "#1f28c7",
                  },
                }}
              >
                https://t.me/allvision_alert_bot?start=register
              </Link>
            </Box>
          </Box>

          <Box
            sx={{
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#212121",
                  letterSpacing: "0.15px",
                }}
              >
                Chat ID
              </Typography>
              <InfoIcon
                sx={{
                  fontSize: 16,
                  color: "#212121",
                }}
              />
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter Telegram chat ID"
              value={card.chatId || ""}
              onChange={(e) =>
                handleActionCardChange(card.id, "chatId", e.target.value)
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                  borderRadius: "10px",
                  "& fieldset": {
                    borderColor: "#e0e0e0",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  color: "#212121",
                  padding: "12px 16px",
                  "&::placeholder": {
                    color: "#a0a0a0",
                    opacity: 1,
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ ...actionMessagePlaceholderGridSx, mb: 2 }}>
            <Box sx={actionMessageBodyColumnSx}>
              <Box
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "10px",
                  backgroundColor: "white",
                  overflow: "hidden",
                  flex: 1,
                  minHeight: { md: 0 },
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 0.5,
                    p: 1,
                    borderBottom: "1px solid #e0e0e0",
                    backgroundColor: "#fafafa",
                  }}
                >
                  <IconButton
                    size="small"
                    title="Attach files"
                    sx={{
                      color: "#5f6368",
                      padding: "6px",
                      "&:hover": {
                        backgroundColor: "rgba(95, 99, 104, 0.1)",
                      },
                    }}
                    onClick={() => {
                      const input = document.createElement("input");
                      input.type = "file";
                      input.multiple = true;
                      input.onchange = (e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length) console.log("Files selected:", files);
                      };
                      input.click();
                    }}
                  >
                    <AttachFileIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    title="Bold"
                    sx={{
                      color: formattingState[card.id]?.bold
                        ? "#2932E5"
                        : "#5f6368",
                      padding: "6px",
                      backgroundColor: formattingState[card.id]?.bold
                        ? "rgba(41, 50, 229, 0.08)"
                        : "transparent",
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      const editor = document.querySelector(
                        `[data-email-editor-id="${card.id}"]`,
                      );
                      if (editor) {
                        editor.focus();
                        document.execCommand("bold", false, null);
                        setTimeout(() => {
                          handleActionCardChange(
                            card.id,
                            "messageBody",
                            editor.innerHTML,
                          );
                          updateFormattingState(card.id);
                        }, 0);
                      }
                    }}
                  >
                    <FormatBoldIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    title="Italic"
                    sx={{
                      color: formattingState[card.id]?.italic
                        ? "#2932E5"
                        : "#5f6368",
                      padding: "6px",
                      backgroundColor: formattingState[card.id]?.italic
                        ? "rgba(41, 50, 229, 0.08)"
                        : "transparent",
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      const editor = document.querySelector(
                        `[data-email-editor-id="${card.id}"]`,
                      );
                      if (editor) {
                        editor.focus();
                        document.execCommand("italic", false, null);
                        setTimeout(() => {
                          handleActionCardChange(
                            card.id,
                            "messageBody",
                            editor.innerHTML,
                          );
                          updateFormattingState(card.id);
                        }, 0);
                      }
                    }}
                  >
                    <FormatItalicIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    title="Underline"
                    sx={{
                      color: formattingState[card.id]?.underline
                        ? "#2932E5"
                        : "#5f6368",
                      padding: "6px",
                      backgroundColor: formattingState[card.id]?.underline
                        ? "rgba(41, 50, 229, 0.08)"
                        : "transparent",
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      const editor = document.querySelector(
                        `[data-email-editor-id="${card.id}"]`,
                      );
                      if (editor) {
                        editor.focus();
                        document.execCommand("underline", false, null);
                        setTimeout(() => {
                          handleActionCardChange(
                            card.id,
                            "messageBody",
                            editor.innerHTML,
                          );
                          updateFormattingState(card.id);
                        }, 0);
                      }
                    }}
                  >
                    <FormatUnderlinedIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box
                  contentEditable
                  suppressContentEditableWarning
                  data-email-editor-id={card.id}
                  ref={(el) => {
                    if (!el) return;
                    const next = messageBodyToEditorHtml(card.messageBody);
                    if (el.innerHTML !== next) el.innerHTML = next;
                  }}
                  onInput={(e) => {
                    const htmlContent = e.target.innerHTML;
                    handleActionCardChange(
                      card.id,
                      "messageBody",
                      htmlContent,
                    );
                    updateFormattingState(card.id);
                  }}
                  onBlur={(e) => {
                    handleActionCardChange(
                      card.id,
                      "messageBody",
                      e.target.innerHTML,
                    );
                  }}
                  onKeyUp={() => updateFormattingState(card.id)}
                  onMouseUp={() => updateFormattingState(card.id)}
                  sx={{
                    flex: 1,
                    minHeight: { xs: 200, md: 0 },
                    fontSize: "14px",
                    color: "#202124",
                    fontFamily: "Roboto, sans-serif",
                    lineHeight: 1.5,
                    outline: "none",
                    p: 2,
                    "&:empty:before": {
                      content: '"Enter message content."',
                      color: "#5f6368",
                      opacity: 1,
                    },
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.5,
                  color: "rgba(0, 0, 0, 0.6)",
                  fontSize: "0.75rem",
                }}
              >
                Message to be sent via Telegram
              </Typography>
            </Box>
            <Box sx={actionPlaceholderColumnSx}>
              {renderMessagePlaceholderPicker({
                title:
                  "Click a link below to insert a placeholder into your message:",
                onPickToken: (token) => insertEmailShortcode(card.id, token),
                maxHeight: { md: 360 },
                scroll: true,
                stretchList: true,
              })}
            </Box>
          </Box>
        </>
      );
    } else if (card.type === "SMS") {
      return (
        <>
          {renderSharedNotifiedToField(card)}
          {/* Phone Number Field with Country Code */}
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#212121",
                mb: 1,
                letterSpacing: "0.15px",
              }}
            >
              Phone Number
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "flex-start",
              }}
            >
              {/* Country Code Dropdown */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={card.countryCode || "+1"}
                  onChange={(e) =>
                    handleActionCardChange(
                      card.id,
                      "countryCode",
                      e.target.value,
                    )
                  }
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "10px",
                    fontSize: "14px",
                    "& fieldset": {
                      borderColor: "#e0e0e0",
                    },
                    "& .MuiSelect-select": {
                      padding: "12px 16px",
                    },
                  }}
                >
                  <MenuItem value="+1">+1 (US/Canada)</MenuItem>
                  <MenuItem value="+91">+91 (India)</MenuItem>
                  <MenuItem value="+44">+44 (UK)</MenuItem>
                  <MenuItem value="+86">+86 (China)</MenuItem>
                  <MenuItem value="+81">+81 (Japan)</MenuItem>
                  <MenuItem value="+49">+49 (Germany)</MenuItem>
                  <MenuItem value="+33">+33 (France)</MenuItem>
                  <MenuItem value="+39">+39 (Italy)</MenuItem>
                  <MenuItem value="+34">+34 (Spain)</MenuItem>
                  <MenuItem value="+61">+61 (Australia)</MenuItem>
                  <MenuItem value="+55">+55 (Brazil)</MenuItem>
                  <MenuItem value="+7">+7 (Russia)</MenuItem>
                  <MenuItem value="+82">+82 (South Korea)</MenuItem>
                  <MenuItem value="+65">+65 (Singapore)</MenuItem>
                  <MenuItem value="+971">+971 (UAE)</MenuItem>
                </Select>
              </FormControl>
              {/* Phone Number Input */}
              <TextField
                fullWidth
                size="small"
                placeholder="Enter phone number"
                value={card.chatId || ""}
                onChange={(e) =>
                  handleActionCardChange(card.id, "chatId", e.target.value)
                }
                error={!!smsErrors[card.id]?.phoneNumber}
                helperText={
                  smsErrors[card.id]?.phoneNumber ||
                  "Enter phone numbers separated by commas for multiple recipients (e.g., 1234567890, 9876543210)."
                }
                FormHelperTextProps={{
                  sx: {
                    color: smsErrors[card.id]?.phoneNumber
                      ? "#d32f2f"
                      : "rgba(0, 0, 0, 0.6)",
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                    borderRadius: "10px",
                    "& fieldset": {
                      borderColor: smsErrors[card.id]?.phoneNumber
                        ? "#d32f2f"
                        : "#e0e0e0",
                    },
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "14px",
                    color: "#212121",
                    padding: "12px 16px",
                    "&::placeholder": {
                      color: "#a0a0a0",
                      opacity: 1,
                    },
                  },
                }}
              />
            </Box>
          </Box>

          {/* Message Body + placeholders (same rich editor as Email) */}
          <Box sx={{ ...actionMessagePlaceholderGridSx, mb: 2 }}>
            <Box sx={actionMessageBodyColumnSx}>
              <Box
                sx={{
                  border:
                    smsErrors[card.id]?.messageBody ||
                    plainTextLengthFromMessageHtml(card.messageBody || "") > 160
                      ? "1px solid #d32f2f"
                      : "1px solid #e0e0e0",
                  borderRadius: "10px",
                  backgroundColor: "white",
                  overflow: "hidden",
                  flex: 1,
                  minHeight: { md: 0 },
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 0.5,
                    p: 1,
                    borderBottom: "1px solid #e0e0e0",
                    backgroundColor: "#fafafa",
                  }}
                >
                  <IconButton
                    size="small"
                    title="Attach files"
                    sx={{
                      color: "#5f6368",
                      padding: "6px",
                      "&:hover": {
                        backgroundColor: "rgba(95, 99, 104, 0.1)",
                      },
                    }}
                    onClick={() => {
                      const input = document.createElement("input");
                      input.type = "file";
                      input.multiple = true;
                      input.onchange = (e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length) console.log("Files selected:", files);
                      };
                      input.click();
                    }}
                  >
                    <AttachFileIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    title="Bold"
                    sx={{
                      color: formattingState[card.id]?.bold
                        ? "#2932E5"
                        : "#5f6368",
                      padding: "6px",
                      backgroundColor: formattingState[card.id]?.bold
                        ? "rgba(41, 50, 229, 0.08)"
                        : "transparent",
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      const editor = document.querySelector(
                        `[data-email-editor-id="${card.id}"]`,
                      );
                      if (editor) {
                        editor.focus();
                        document.execCommand("bold", false, null);
                        setTimeout(() => {
                          handleActionCardChange(
                            card.id,
                            "messageBody",
                            editor.innerHTML,
                          );
                          updateFormattingState(card.id);
                        }, 0);
                      }
                    }}
                  >
                    <FormatBoldIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    title="Italic"
                    sx={{
                      color: formattingState[card.id]?.italic
                        ? "#2932E5"
                        : "#5f6368",
                      padding: "6px",
                      backgroundColor: formattingState[card.id]?.italic
                        ? "rgba(41, 50, 229, 0.08)"
                        : "transparent",
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      const editor = document.querySelector(
                        `[data-email-editor-id="${card.id}"]`,
                      );
                      if (editor) {
                        editor.focus();
                        document.execCommand("italic", false, null);
                        setTimeout(() => {
                          handleActionCardChange(
                            card.id,
                            "messageBody",
                            editor.innerHTML,
                          );
                          updateFormattingState(card.id);
                        }, 0);
                      }
                    }}
                  >
                    <FormatItalicIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    title="Underline"
                    sx={{
                      color: formattingState[card.id]?.underline
                        ? "#2932E5"
                        : "#5f6368",
                      padding: "6px",
                      backgroundColor: formattingState[card.id]?.underline
                        ? "rgba(41, 50, 229, 0.08)"
                        : "transparent",
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      const editor = document.querySelector(
                        `[data-email-editor-id="${card.id}"]`,
                      );
                      if (editor) {
                        editor.focus();
                        document.execCommand("underline", false, null);
                        setTimeout(() => {
                          handleActionCardChange(
                            card.id,
                            "messageBody",
                            editor.innerHTML,
                          );
                          updateFormattingState(card.id);
                        }, 0);
                      }
                    }}
                  >
                    <FormatUnderlinedIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box
                  contentEditable
                  suppressContentEditableWarning
                  data-email-editor-id={card.id}
                  ref={(el) => {
                    if (!el) return;
                    const next = messageBodyToEditorHtml(card.messageBody);
                    if (el.innerHTML !== next) el.innerHTML = next;
                  }}
                  onInput={(e) => {
                    const htmlContent = e.target.innerHTML;
                    handleActionCardChange(
                      card.id,
                      "messageBody",
                      htmlContent,
                    );
                    updateFormattingState(card.id);
                  }}
                  onBlur={(e) => {
                    handleActionCardChange(
                      card.id,
                      "messageBody",
                      e.target.innerHTML,
                    );
                  }}
                  onKeyUp={() => updateFormattingState(card.id)}
                  onMouseUp={() => updateFormattingState(card.id)}
                  sx={{
                    flex: 1,
                    minHeight: { xs: 200, md: 0 },
                    fontSize: "14px",
                    color: "#202124",
                    fontFamily: "Roboto, sans-serif",
                    lineHeight: 1.5,
                    outline: "none",
                    p: 2,
                    "&:empty:before": {
                      content: '"Enter SMS message."',
                      color: "#5f6368",
                      opacity: 1,
                    },
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.5,
                  color:
                    smsErrors[card.id]?.messageBody ||
                    plainTextLengthFromMessageHtml(card.messageBody || "") > 160
                      ? "#d32f2f"
                      : "rgba(0, 0, 0, 0.6)",
                }}
              >
                {smsErrors[card.id]?.messageBody ||
                  (plainTextLengthFromMessageHtml(card.messageBody || "") > 160
                    ? "Message body must be 160 characters or less"
                    : `${plainTextLengthFromMessageHtml(card.messageBody || "")}/160 characters`)}
              </Typography>
            </Box>
            <Box sx={actionPlaceholderColumnSx}>
              {renderMessagePlaceholderPicker({
                title:
                  "Click a link below to insert a placeholder into your message:",
                onPickToken: (token) => insertEmailShortcode(card.id, token),
                maxHeight: { md: 360 },
                scroll: true,
                stretchList: true,
              })}
            </Box>
          </Box>
        </>
      );
    } else if (card.type === "Phone") {
      return (
        <>
          {renderSharedNotifiedToField(card)}
          {/* Phone Number Field with Country Code */}
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#212121",
                mb: 1,
                letterSpacing: "0.15px",
              }}
            >
              Phone Number
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "flex-start",
              }}
            >
              {/* Country Code Dropdown */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={card.countryCode || "+1"}
                  onChange={(e) =>
                    handleActionCardChange(
                      card.id,
                      "countryCode",
                      e.target.value,
                    )
                  }
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "10px",
                    fontSize: "14px",
                    "& fieldset": {
                      borderColor: "#e0e0e0",
                    },
                    "& .MuiSelect-select": {
                      padding: "12px 16px",
                    },
                  }}
                >
                  <MenuItem value="+1">+1 (US/Canada)</MenuItem>
                  <MenuItem value="+91">+91 (India)</MenuItem>
                  <MenuItem value="+44">+44 (UK)</MenuItem>
                  <MenuItem value="+86">+86 (China)</MenuItem>
                  <MenuItem value="+81">+81 (Japan)</MenuItem>
                  <MenuItem value="+49">+49 (Germany)</MenuItem>
                  <MenuItem value="+33">+33 (France)</MenuItem>
                  <MenuItem value="+39">+39 (Italy)</MenuItem>
                  <MenuItem value="+34">+34 (Spain)</MenuItem>
                  <MenuItem value="+61">+61 (Australia)</MenuItem>
                  <MenuItem value="+55">+55 (Brazil)</MenuItem>
                  <MenuItem value="+7">+7 (Russia)</MenuItem>
                  <MenuItem value="+82">+82 (South Korea)</MenuItem>
                  <MenuItem value="+65">+65 (Singapore)</MenuItem>
                  <MenuItem value="+971">+971 (UAE)</MenuItem>
                </Select>
              </FormControl>
              {/* Phone Number Input */}
              <TextField
                fullWidth
                size="small"
                placeholder="Enter phone number"
                value={card.chatId || ""}
                onChange={(e) =>
                  handleActionCardChange(card.id, "chatId", e.target.value)
                }
                error={!!phoneErrors[card.id]?.phoneNumber}
                helperText={
                  phoneErrors[card.id]?.phoneNumber ||
                  "Enter phone numbers separated by commas for multiple recipients (e.g., 1234567890, 9876543210)."
                }
                FormHelperTextProps={{
                  sx: {
                    color: phoneErrors[card.id]?.phoneNumber
                      ? "#d32f2f"
                      : "rgba(0, 0, 0, 0.6)",
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                    borderRadius: "10px",
                    "& fieldset": {
                      borderColor: phoneErrors[card.id]?.phoneNumber
                        ? "#d32f2f"
                        : "#e0e0e0",
                    },
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "14px",
                    color: "#212121",
                    padding: "12px 16px",
                    "&::placeholder": {
                      color: "#a0a0a0",
                      opacity: 1,
                    },
                  },
                }}
              />
            </Box>
          </Box>

          {/* Info Box */}
          <Box
            sx={{
              backgroundColor: "#e3f2fd",
              border: "1px solid #2196f3",
              borderRadius: "10px",
              p: 2,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <InfoIcon
              sx={{
                fontSize: 20,
                color: "#2196f3",
              }}
            />
            <Typography
              sx={{
                fontSize: "14px",
                color: "#1976d2",
              }}
            >
              A voice call will be made to this number when the alert is
              triggered
            </Typography>
          </Box>
        </>
      );
    } else if (card.type === "HTTP") {
      return (
        <>
          {renderSharedNotifiedToField(card)}
          <Box
            sx={{
              mb: 2,
              display: "flex",
              gap: 1,
              alignItems: "flex-start",
            }}
          >
            <FormControl
              size="small"
              sx={{
                minWidth: "140px",
              }}
            >
              <Typography
                component="label"
                htmlFor={`http-method-${card.id}`}
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "rgba(0, 0, 0, 0.6)",
                  mb: 0.5,
                  display: "block",
                }}
              >
                Method
              </Typography>
              <Select
                id={`http-method-${card.id}`}
                value={card.httpMethod || "POST"}
                onChange={(e) =>
                  handleActionCardChange(card.id, "httpMethod", e.target.value)
                }
                sx={{
                  backgroundColor: "white",
                  borderRadius: "10px",
                  fontSize: "14px",
                  "& fieldset": {
                    borderColor: "#e5e7eb",
                  },
                  "& .MuiSelect-select": {
                    padding: "16px",
                  },
                }}
              >
                <MenuItem value="GET">GET</MenuItem>
                <MenuItem value="POST">POST</MenuItem>
                <MenuItem value="PUT">PUT</MenuItem>
                <MenuItem value="PATCH">PATCH</MenuItem>
                <MenuItem value="DELETE">DELETE</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              size="small"
              placeholder="Enter endpoint URL"
              value={card.chatId || ""}
              onChange={(e) =>
                handleActionCardChange(card.id, "chatId", e.target.value)
              }
              error={!!httpErrors[card.id]?.url}
              helperText={
                httpErrors[card.id]?.url ||
                "Enter a valid webhook URL (e.g., https://example.com/webhook)"
              }
              FormHelperTextProps={{
                sx: {
                  color: httpErrors[card.id]?.url
                    ? "#d32f2f"
                    : "rgba(0, 0, 0, 0.6)",
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                  borderRadius: "10px",
                  "& fieldset": {
                    borderColor: httpErrors[card.id]?.url
                      ? "#d32f2f"
                      : "#e5e7eb",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  color: "#212121",
                  padding: "16px",
                  "&::placeholder": {
                    color: "#a0a0a0",
                    opacity: 1,
                  },
                },
              }}
            />
          </Box>

          <Box
            sx={{
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#212121",
                  letterSpacing: "0.15px",
                }}
              >
                HTTP Configuration
              </Typography>
            </Box>
          </Box>

          <HttpConfigTabs
            card={card}
            handleActionCardChange={handleActionCardChange}
          />

          <Box
            sx={{
              display: "none",
            }}
          >
            <TextField
              style={{
                display: "none",
              }}
              fullWidth
              multiline
              rows={5}
              placeholder='{"alert": "message", "severity": "high"}'
              value={card.messageBody || ""}
              onChange={(e) =>
                handleActionCardChange(card.id, "messageBody", e.target.value)
              }
              helperText="JSON payload to send in HTTP request"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                  borderRadius: "10px",
                  "& fieldset": {
                    borderColor: "#e0e0e0",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  color: "#212121",
                  padding: "16px",
                  fontFamily: "monospace",
                  "&::placeholder": {
                    color: "#a0a0a0",
                    opacity: 1,
                  },
                },
              }}
            />
          </Box>
        </>
      );
    }
    return null;
  };
  return (
    <Box
      sx={{
        backgroundColor: "#F2F2F2",
        minHeight: "105vh",
        pt: "6px",
        pl: "6px",
        pr: "20px",
        /* Fixed footer (Cancel / Save) covers ~56px; extra space so “Add Action” scrolls above it — tall Telegram layout */
        pb: "calc(88px + env(safe-area-inset-bottom, 0px))",
      }}
    >
      {/* Tabs Card */}
      <Box
        sx={{
          backgroundColor: "white",
          mb: "6px",
        }}
      >
        <Tabs
          value={2}
          sx={{
            minHeight: "48px",
            px: 0,
            backgroundColor: "#f2f2f2",
            "& .MuiTabs-indicator": {
              height: "3px",
              backgroundColor: "#2932E5",
            },
            "& .MuiTab-root": {
              textTransform: "none",
              fontFamily: "Roboto, sans-serif",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "17.5px",
              color: "#4A5565",
              minHeight: "48px",
              px: "16px",
              py: "12px",
              flexDirection: "row",
              gap: "8px",
              "& .MuiTab-iconWrapper": {
                marginBottom: 0,
                marginRight: 0,
              },
              "&.Mui-selected": {
                color: "#2932E5",
                "& .MuiSvgIcon-root": {
                  color: "#2932E5",
                },
              },
              "& .MuiSvgIcon-root": {
                fontSize: "24px",
                color: "#4A5565",
              },
            },
          }}
        >
          <Tab
            label="Dashboard"
            icon={<DashboardIcon />}
            iconPosition="start"
            disabled
          />
          <Tab
            label="History"
            icon={<HistoryIcon />}
            iconPosition="start"
            disabled
          />
          <Tab
            label="Alert Rule"
            icon={<NotificationsIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Form Card */}
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: "14px",
          boxShadow:
            "0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)",
          overflow: "hidden",
        }}
      >
        {/* Breadcrumb Section */}
        <Box
          sx={{
            borderBottom: "0.8px solid #e0e0e0",
            backgroundColor: "#fafafa",
            px: "20px",
            py: "8px",
          }}
        >
          <Breadcrumbs
            aria-label="breadcrumb"
            separator="›"
            sx={{
              mb: 0,
              "& .MuiBreadcrumbs-separator": {
                fontSize: "18px",
                color: "#4A5565",
                mx: 1,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <NotificationsIcon
                sx={{
                  fontSize: 20,
                  color: "#212121",
                }}
              />
              <Link
                underline="hover"
                color="inherit"
                href="#"
                onClick={handleCancel}
                sx={{
                  cursor: "pointer",
                  fontFamily: "Roboto, sans-serif",
                  fontWeight: 500,
                  fontSize: "16px",
                  lineHeight: "22.88px",
                  color: "#4A5565",
                }}
              >
                Alerts
              </Link>
            </Box>
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontWeight: 500,
                fontSize: "16px",
                lineHeight: "22.88px",
                color: "#212121",
              }}
            >
              {viewOnly ? "View Rule" : editingAlert ? "Edit Rule" : "New Rule"}
            </Typography>
          </Breadcrumbs>
        </Box>

        {/* Main Content — view-only uses read-only/disabled controls (no fieldset; clearer text) */}
        <Box
          sx={{
            p: "3px",
            border: "none",
            margin: 0,
            minWidth: 0,
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: "4px",
              flexDirection: "column",
              padding: "5px",
            }}
          >
            {/* Define Rule (left card) + Set Action / View Actions (right card) */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: "flex-start",
                gap: 1,
                width: "100%",
              }}
            >
              <Paper
                sx={{
                  flex: { xs: "1 1 auto", md: "0 1 540px" },
                  width: { xs: "100%", md: "auto" },
                  maxWidth: { md: 640 },
                  minWidth: { md: 380 },
                  p: 1.5,
                  borderRadius: "12px",
                  backgroundColor: "#fff",
                  border: "1px solid #e8eaef",
                  boxShadow: "0px 1px 3px rgba(0,0,0,0.08)",
                }}
              >
                {/* Define Rule Section */}
                <Box sx={{ mb: 0, width: "100%" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#2932E5",
                        fontSize: "20px",
                        fontWeight: 500,
                        letterSpacing: "-0.5px",
                      }}
                    >
                      Define Rule
                    </Typography>
                    <InfoOutlinedIcon
                      sx={{ fontSize: 18, color: "#455a64" }}
                      aria-hidden
                    />
                  </Box>

                  {/* Alert Name — info icon on row hover or field focus */}
                  <Box sx={DEFINE_RULE_ROW_WITH_INFO_SX}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      <Typography sx={{ ...DEFINE_RULE_LABEL_SX, mb: 0 }}>
                        Alert Name
                      </Typography>
                      <DefineRuleFieldInfo
                        title={ALERT_NAME_INFO_TOOLTIP}
                        ariaLabel="Alert name help"
                      />
                    </Box>
                    {/* <Tooltip
                      title={ALERT_NAME_INFO_TOOLTIP}
                      arrow
                      placement="top"
                      enterTouchDelay={0}
                    > */}
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Alert Name"
                      value={alertName}
                      onChange={(e) => setAlertName(e.target.value)}
                      InputProps={{ readOnly: viewOnly }}
                      sx={DEFINE_RULE_OUTLINED_FIELD_SX}
                    />
                    {/* </Tooltip> */}
                  </Box>

                  {/* Type of Alert */}
                  <Box sx={DEFINE_RULE_ROW_WITH_INFO_SX}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      <Typography sx={{ ...DEFINE_RULE_LABEL_SX, mb: 0 }}>
                        Alert Type
                      </Typography>
                      <DefineRuleFieldInfo
                        title={ALERT_TYPE_INFO_TOOLTIP}
                        ariaLabel="Alert type help"
                      />
                    </Box>
                    <FormControl
                      fullWidth
                      size="small"
                      error={Boolean(useCasesError)}
                    >
                      <Select
                        value={alertType}
                        onChange={(e) => setAlertType(e.target.value)}
                        displayEmpty
                        disabled={viewOnly || useCasesLoading}
                        MenuProps={{ disablePortal: true }}
                        renderValue={(selected) => {
                          if (useCasesLoading) {
                            return (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <CircularProgress size={18} />
                                <span>Loading types…</span>
                              </Box>
                            );
                          }
                          if (!selected) {
                            return (
                              <em style={{ color: "#a0a0a0", fontStyle: "normal" }}>
                                Select Alert Type
                              </em>
                            );
                          }
                          const opt = useCaseOptions.find((u) => u.id === selected);
                          if (opt) return opt.name;
                          return selected;
                        }}
                        sx={{...DEFINE_RULE_OUTLINED_FIELD_SX, borderRadius: "12px"}}
                      >
                        <MenuItem value="" disabled>
                          <em>Select Alert Type</em>
                        </MenuItem>
                        {alertType &&
                          !useCaseOptions.some((u) => u.id === alertType) && (
                            <MenuItem key="__legacy-alert-type__" value={alertType}>
                              {alertType}
                            </MenuItem>
                          )}
                        {useCaseOptions.map((uc) => (
                          <MenuItem key={uc.id} value={uc.id}>
                            {uc.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {useCasesError ? (
                        <FormHelperText>{useCasesError}</FormHelperText>
                      ) : null}
                    </FormControl>
                  </Box>

                  {/* Source (AI / IoT / System) */}
                  <Box sx={DEFINE_RULE_ROW_WITH_INFO_SX}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      <Typography sx={{ ...DEFINE_RULE_LABEL_SX, mb: 0, borderRadius: "12px"}}>
                        Source
                      </Typography>
                      <DefineRuleFieldInfo
                        title={SOURCE_INFO_TOOLTIP}
                        ariaLabel="Source help"
                      />
                    </Box>
                    <FormControl fullWidth size="small" sx={{ mb: 0 }}>
                      <Select
                        displayEmpty
                        value={sourceCategory}
                        onChange={handleSourceCategoryChange}
                        disabled={viewOnly}
                        MenuProps={{ disablePortal: true }}
                        renderValue={(selected) => {
                          if (!selected) {
                            return (
                              <em style={{ color: "#a0a0a0", fontStyle: "normal" }}>
                                Select AI
                              </em>
                            );
                          }
                          return selected;
                        }}
                        sx={{...DEFINE_RULE_OUTLINED_FIELD_SX, borderRadius: "12px"}}
                      >
                        <MenuItem value="">
                          <em>Select AI</em>
                        </MenuItem>
                        <MenuItem value="AI">AI</MenuItem>
                        <MenuItem value="IoT" disabled>
                          IoT
                        </MenuItem>
                        <MenuItem value="System" disabled>
                          System
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Sub Source */}
                  <Box sx={DEFINE_RULE_ROW_WITH_INFO_SX}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      <Typography sx={{ ...DEFINE_RULE_LABEL_SX, mb: 0, borderRadius: "12px"}}>
                        Sub Source
                      </Typography>
                      <DefineRuleFieldInfo
                        title={SUB_SOURCE_INFO_TOOLTIP}
                        ariaLabel="Sub source help"
                      />
                    </Box>

                    {sourceCategory === "AI" && (
                      <>
                        <Button
                          fullWidth
                          onClick={handleSourceDialogOpen}
                          sx={{
                            ...DEFINE_RULE_PICKER_BUTTON_SX,
                            color:
                              selectedSources.length === 0
                                ? "#757575"
                                : "#212121",
                          }}
                          endIcon={<SearchIcon />}
                        >
                          {getAiSubSourceButtonLabel()}
                        </Button>
                        <FormHelperText sx={{ mt: 0.5 }}>
                          Cameras and zones (ROIs): pick one or more cameras,
                          then select zones per camera. Multiple selections
                          allowed.
                        </FormHelperText>
                      </>
                    )}

                    {sourceCategory === "IoT" && (
                      <>
                        <Button
                          fullWidth
                          onClick={handleSourceDialogOpen}
                          sx={{
                            ...DEFINE_RULE_PICKER_BUTTON_SX,
                            color:
                              selectedSources.length === 0
                                ? "#757575"
                                : "#212121",
                          }}
                          endIcon={<SearchIcon />}
                        >
                          {getIotSubSourceButtonLabel()}
                        </Button>
                        <FormHelperText sx={{ mt: 0.5 }}>
                          IoT devices: search and select one or more in the
                          dialog. Same flow as AI sub-sources.
                        </FormHelperText>
                      </>
                    )}

                    {sourceCategory === "System" && (
                      <>
                        <Button
                          fullWidth
                          onClick={handleSourceDialogOpen}
                          sx={{
                            ...DEFINE_RULE_PICKER_BUTTON_SX,
                            color:
                              selectedSources.length === 0
                                ? "#757575"
                                : "#212121",
                                borderRadius: "12px",
                          }}
                          endIcon={<SearchIcon />}
                        >
                          {getSystemSubSourceButtonLabel()}
                        </Button>
                        <FormHelperText sx={{ mt: 0.5 }}>
                          System sources: search and select one or more in the
                          dialog. Same flow as AI sub-sources.
                        </FormHelperText>
                      </>
                    )}

                    {!sourceCategory && (
                      <FormHelperText>
                        Choose AI (cameras &amp; zones), then configure Sub
                        Source. IoT and System are temporarily unavailable.
                      </FormHelperText>
                    )}
                  </Box>

                  {/* Severity */}
                  <Box sx={DEFINE_RULE_ROW_WITH_INFO_SX}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      <Typography sx={{ ...DEFINE_RULE_LABEL_SX, mb: 0 , borderRadius: "12px"}}>
                        Severity
                      </Typography>
                      <DefineRuleFieldInfo
                        title={SEVERITY_INFO_TOOLTIP}
                        ariaLabel="Severity help"
                      />
                    </Box>
                    <FormControl fullWidth size="small">
                      <Select
                        value={severity}
                        onChange={(e) => setSeverity(e.target.value)}
                        displayEmpty
                        disabled={viewOnly}
                        MenuProps={{ disablePortal: true }}
                        sx={{...DEFINE_RULE_OUTLINED_FIELD_SX, borderRadius: "12px"}}
                      >
                        <MenuItem value="" disabled>
                          <em>Select Severity</em>
                        </MenuItem>
                        <MenuItem value="Critical">Critical</MenuItem>
                        <MenuItem value="High">High</MenuItem>
                        <MenuItem value="Medium">Medium</MenuItem>
                        <MenuItem value="Low">Low</MenuItem>
                      </Select>
                      {/* <FormHelperText>Alert priority level</FormHelperText> */}
                    </FormControl>
                  </Box>

                  {/* Schedule */}
                  <Box sx={DEFINE_RULE_ROW_WITH_INFO_SX}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      <Typography sx={{ ...DEFINE_RULE_LABEL_SX, mb: 0 }}>
                        Schedule
                      </Typography>
                      <DefineRuleFieldInfo
                        title={SCHEDULE_INFO_TOOLTIP}
                        ariaLabel="Schedule help"
                      />
                    </Box>
                    <Button
                      fullWidth
                      onClick={handleScheduleDialogOpen}
                      disabled={viewOnly}
                      sx={{
                        ...DEFINE_RULE_PICKER_BUTTON_SX,
                        color: !schedule ? "#757575" : "#212121",
                      }}
                      startIcon={
                        <CalendarIcon
                          sx={{
                            fontSize: 16,
                            color: "#212121",
                          }}
                        />
                      }
                    >
                      {getScheduleDisplay()}
                    </Button>
                   
                  </Box>

                  {/* Cooldown Interval */}
                  <Box sx={{ ...DEFINE_RULE_ROW_WITH_INFO_SX, mb: 0 , borderRadius: "12px"}}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Typography sx={{ ...DEFINE_RULE_LABEL_SX, mb: 0 }}>
                          Cooldown Interval
                        </Typography>
                        <DefineRuleFieldInfo
                          title={COOLDOWN_INFO_TOOLTIP}
                          ariaLabel="Cooldown interval help"
                        />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 700,
                          color: "#2932E5",
                          letterSpacing: "0.13px",
                        }}
                      >
                        {COOLDOWN_MIN} - {COOLDOWN_MAX} seconds
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      placeholder={COOLDOWN_DEFAULT.toString()}
                      value={countdownInterval}
                      InputProps={{ readOnly: viewOnly }}
                      onKeyDown={(e) => {
                        // Allow only digits and control keys
                        const allowedKeys = [
                          "Backspace",
                          "Delete",
                          "Tab",
                          "Escape",
                          "Enter",
                          "ArrowLeft",
                          "ArrowRight",
                          "ArrowUp",
                          "ArrowDown",
                          "Home",
                          "End",
                        ];
                        const isDigit = /^[0-9]$/.test(e.key);
                        const isAllowed = allowedKeys.includes(e.key);
                        if (!isDigit && !isAllowed) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "") {
                          setCountdownInterval("");
                          return;
                        }
                        const numValue = parseInt(value, 10);
                        if (isNaN(numValue)) return;
                        setCountdownInterval(value);
                      }}
                      error={
                        countdownInterval !== "" &&
                        !isNaN(parseInt(countdownInterval, 10)) &&
                        (parseInt(countdownInterval, 10) < COOLDOWN_MIN ||
                          parseInt(countdownInterval, 10) > COOLDOWN_MAX)
                      }
                      // helperText={
                      //   countdownInterval !== "" &&
                      //   !isNaN(parseInt(countdownInterval, 10)) &&
                      //   (parseInt(countdownInterval, 10) < COOLDOWN_MIN ||
                      //     parseInt(countdownInterval, 10) > COOLDOWN_MAX)
                      //     ? `Value must be between ${COOLDOWN_MIN} and ${COOLDOWN_MAX} seconds`
                      //     : "Delay before triggering alert (seconds)"
                      // }
                      inputProps={{
                        min: COOLDOWN_MIN,
                        max: COOLDOWN_MAX,
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                      sx={DEFINE_RULE_OUTLINED_FIELD_SX}
                    />
                  </Box>
                </Box>
              </Paper>

              <Paper
                sx={{
                  flex: "1 1 0",
                  minWidth: 0,
                  width: { xs: "100%", md: "auto" },
                  p: 0,
                  borderRadius: "12px",
                  backgroundColor: "#fff",
                  border: "1px solid #e8eaef",
                  boxShadow: "0px 1px 3px rgba(0,0,0,0.08)",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    borderBottom: "1px solid #e8eaef",
                    pr: 0.5,
                  }}
                >
                  {viewOnly ? (
                    <Box
                      sx={{
                        flex: 1,
                        minHeight: 48,
                        px: 2,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontWeight: 600,
                          fontSize: "15px",
                          color: "#2932E5",
                        }}
                      >
                        View Action
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <Tabs
                        value={rightActionTab}
                        onChange={(_, v) => setRightActionTab(v)}
                        sx={{
                          flex: 1,
                          minHeight: 48,
                          px: 1,
                          "& .MuiTabs-indicator": {
                            height: 3,
                            backgroundColor: "#2932E5",
                          },
                          "& .MuiTab-root": {
                            textTransform: "none",
                            fontFamily: "Roboto, sans-serif",
                            fontWeight: 500,
                            fontSize: "15px",
                            color: "#4A5565",
                            minHeight: 48,
                            "&.Mui-selected": { color: "#2932E5" },
                          },
                        }}
                      >
                        <Tab label="Set Action" />
                        <Tab label="View Action" />
                      </Tabs>
                      {/* {rightActionTab === 0 && (
                        <IconButton
                          aria-label="Add another action block"
                          onClick={handleAddActionCard}
                          size="small"
                          sx={{
                            color: "#2932E5",
                            border: "1px solid #2932E5",
                            borderRadius: "10px",
                            mr: 1,
                            flexShrink: 0,
                            "&:hover": {
                              backgroundColor: "rgba(41, 50, 229, 0.04)",
                            },
                          }}
                        >
                          <AddIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      )} */}
                    </>
                  )}
                </Box>

                <Box sx={{ p: 1 }}>
                  {!viewOnly && rightActionTab === 0 && (
                    <Box
                      sx={{
                        // backgroundColor: "#f2f2f2",
                        borderRadius: "12px",
                        border: "1px",
                        p: 2,
                      }}
                    >
                      {/* Action Cards */}
                      {actionCards.map((card, index) => (
                        <Box
                          key={card.id}
                          sx={{
                            mb: index < actionCards.length - 1 ? 2 : 0,
                            pb: index < actionCards.length - 1 ? 2 : 0,
                            borderBottom:
                              index < actionCards.length - 1
                                ? "1px solid #e0e0e0"
                                : "none",
                          }}
                        >
                          {/* Action Type Selection */}
                          <Box sx={{ mb: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                mb: 1,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 500,
                                  color: "#212121",
                                  letterSpacing: "0.15px",
                                }}
                              >
                                Action Type
                              </Typography>
                              <InfoIcon
                                sx={{
                                  fontSize: 16,
                                  color: "#212121",
                                }}
                              />
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <FormControl fullWidth size="small">
                                <Select
                                  value={card.type}
                                  onChange={(e) =>
                                    handleActionCardChange(
                                      card.id,
                                      "type",
                                      e.target.value,
                                    )
                                  }
                                  displayEmpty
                                  sx={{
                                    backgroundColor: "white",
                                    borderRadius: "10px",
                                    fontSize: "14px",
                                    "& fieldset": {
                                      borderColor: "#e0e0e0",
                                    },
                                    "& .MuiSelect-select": {
                                      padding: "12px 20px",
                                    },
                                    "& .MuiSelect-select em": {
                                      color: "#a0a0a0",
                                      fontStyle: "normal",
                                    },
                                  }}
                                >
                                  <MenuItem value="" disabled>
                                    <em>Select Action Type</em>
                                  </MenuItem>
                                  <MenuItem value="Email">Email</MenuItem>
                                  <MenuItem value="Telegram">Telegram</MenuItem>
                                  <MenuItem value="HTTP">HTTP</MenuItem>
                                  <MenuItem value="SMS">SMS</MenuItem>
                                  {/* <MenuItem value="Phone">Phone</MenuItem> */}
                                </Select>
                                <FormHelperText>
                                  Choose how to send the alert — the form below
                                  updates for each notification type.
                                </FormHelperText>
                              </FormControl>
                              {actionCards.length > 1 && (
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleRemoveActionCard(card.id)
                                  }
                                  sx={{
                                    color: "#d32f2f",
                                    padding: "8px",
                                    alignSelf: "flex-start",
                                    mt: "8px",
                                    "&:hover": {
                                      backgroundColor:
                                        "rgba(211, 47, 47, 0.08)",
                                    },
                                  }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </Box>

                          {/* Conditional Fields Based on Action Type — key remounts template when type changes */}
                          <Box
                            key={`action-template-${card.id}-${card.type || "none"}`}
                            sx={{ width: "100%" }}
                          >
                            {renderActionCardFields(card)}
                          </Box>

                          {/* Add to List Button */}
                          {card.type && (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                mt: 2,
                                flexShrink: 0,
                              }}
                            >
                              <Button
                                variant="contained"
                                onClick={() => handleAddActionToList(card)}
                                startIcon={
                                  <AddIcon
                                    sx={{
                                      fontSize: 18,
                                    }}
                                  />
                                }
                                sx={{
                                  backgroundColor: "white",
                                  border: "1px solid #2932E5",
                                  borderRadius: "10px",
                                  textTransform: "none",
                                  px: "14px",
                                  py: "5px",
                                  height: "36px",
                                  fontFamily: "Roboto, sans-serif",
                                  fontWeight: 500,
                                  fontSize: "14px",
                                  lineHeight: "24.5px",
                                  color: "#2932E5",
                                  boxShadow:
                                    "0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)",
                                  "&:hover": {
                                    backgroundColor:
                                      "rgba(210, 227, 239, 0.08)",
                                  },
                                }}
                              >
                                Add Action
                              </Button>
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {(viewOnly || rightActionTab === 1) && (
                    <Box
                      sx={{
                        backgroundColor: "#fff",
                        borderRadius: "12px",
                        border: "1px solid #e8eaef",
                        p: 2,
                        width: "100%",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontWeight: 600,
                          fontSize: "1rem",
                          color: "#212121",
                          mb: 2,
                        }}
                      >
                        Actions
                      </Typography>

                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Search Actions...."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon
                                sx={{
                                  fontSize: 20,
                                  color: "rgba(0,0,0,0.54)",
                                }}
                              />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          mb: 2,
                          "& .MuiInputBase-input::placeholder": {
                            color: "#a0a0a0",
                            opacity: 1,
                          },
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#fff",
                            borderRadius: "10px",
                            "& fieldset": {
                              borderColor: "rgba(0,0,0,0.12)",
                            },
                          },
                          "& .MuiInputBase-input": {
                            fontSize: "14px",
                            color: "#212121",
                            padding: "12px 16px 12px 8px",
                          },
                        }}
                      />

                      <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                          border: "1px solid #e8eaef",
                          borderRadius: "8px",
                          overflow: "hidden",
                        }}
                      >
                        <Table
                          stickyHeader
                          sx={{
                            "& .MuiTableHead-root .MuiTableCell-root": {
                              borderBottom: "1px solid #e8eaef",
                              padding: "10px 16px",
                              lineHeight: "20px",
                              textAlign: "left",
                            },
                            "& .MuiTableBody-root .MuiTableCell-root": {
                              padding: "12px 16px",
                              lineHeight: "20px",
                              textAlign: "left",
                              verticalAlign: "middle",
                            },
                          }}
                        >
                          <TableHead>
                            <TableRow>
                              <TableCell
                                sx={{
                                  fontFamily: "Roboto, sans-serif",
                                  fontWeight: 600,
                                  fontSize: "0.875rem",
                                  color: "#2932E5",
                                  backgroundColor: "#fafafa",
                                }}
                              >
                                Notified by
                              </TableCell>
                              <TableCell
                                sortDirection={
                                  viewActionsOrderBy === "notifiedTo"
                                    ? viewActionsOrder
                                    : false
                                }
                                sx={{
                                  fontFamily: "Roboto, sans-serif",
                                  fontWeight: 600,
                                  fontSize: "0.875rem",
                                  color: "#2932E5",
                                  backgroundColor: "#fafafa",
                                }}
                              >
                                <TableSortLabel
                                  active={viewActionsOrderBy === "notifiedTo"}
                                  direction={viewActionsOrder}
                                  onClick={() => {
                                    setViewActionsOrderBy("notifiedTo");
                                    setViewActionsOrder((o) =>
                                      o === "asc" ? "desc" : "asc",
                                    );
                                  }}
                                  sx={{
                                    "&.MuiTableSortLabel-root": {
                                      color: "#2932E5",
                                    },
                                    "& .MuiTableSortLabel-icon": {
                                      color: "#2932E5 !important",
                                    },
                                  }}
                                >
                                  Notified to
                                </TableSortLabel>
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontFamily: "Roboto, sans-serif",
                                  fontWeight: 600,
                                  fontSize: "0.875rem",
                                  color: "#2932E5",
                                  backgroundColor: "#fafafa",
                                }}
                              >
                                Message
                              </TableCell>
                              {!viewOnly && (
                                <TableCell
                                  width={56}
                                  align="right"
                                  sx={{
                                    backgroundColor: "#fafafa",
                                  }}
                                />
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {viewActionsSorted.length === 0 ? (
                              <TableRow>
                                <TableCell
                                  colSpan={viewOnly ? 3 : 4}
                                  align="center"
                                  sx={{
                                    py: 4,
                                    fontFamily: "Roboto, sans-serif",
                                    fontSize: "0.875rem",
                                    color: "#757575",
                                  }}
                                >
                                  No actions added yet
                                </TableCell>
                              </TableRow>
                            ) : (
                              viewActionsPaginated.map((action) => {
                                return (
                                  <TableRow
                                    key={action.id}
                                    hover
                                    sx={{
                                      "&:hover": {
                                        backgroundColor: "#f9fafb",
                                      },
                                    }}
                                  >
                                    <TableCell
                                      sx={{
                                        fontFamily: "Roboto, sans-serif",
                                        fontSize: "0.875rem",
                                        color: "#212121",
                                      }}
                                    >
                                      {action.type}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        fontFamily: "Roboto, sans-serif",
                                        fontSize: "0.875rem",
                                        maxWidth: "220px",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        color: "#212121",
                                      }}
                                    >
                                      {getNotifiedToLabel(action)}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        fontFamily: "Roboto, sans-serif",
                                        fontSize: "0.875rem",
                                        maxWidth: "280px",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        color: "#424242",
                                      }}
                                    >
                                      {getMessagePreview(action)}
                                    </TableCell>
                                    {!viewOnly && (
                                      <TableCell align="right">
                                        <IconButton
                                          size="small"
                                          aria-label="Row actions"
                                          onClick={(e) =>
                                            handleActionMenuOpen(e, action)
                                          }
                                          sx={{
                                            color: "rgba(0,0,0,0.54)",
                                          }}
                                        >
                                          <MoreVertIcon fontSize="small" />
                                        </IconButton>
                                      </TableCell>
                                    )}
                                  </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: 1,
                          mt: 1.5,
                          pt: 1,
                          borderTop: "1px solid #e8eaef",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            color: "#64748b",
                            fontSize: "0.875rem",
                            pl: 0.5,
                          }}
                        >
                          Total Items: {viewActionsSorted.length}
                        </Typography>
                        <TablePagination
                          component="div"
                          count={viewActionsSorted.length}
                          page={viewActionsPageSafe}
                          onPageChange={(_, newPage) =>
                            setViewActionsPage(newPage)
                          }
                          rowsPerPage={viewActionsRowsPerPage}
                          onRowsPerPageChange={(e) => {
                            setViewActionsRowsPerPage(
                              parseInt(e.target.value, 10),
                            );
                            setViewActionsPage(0);
                          }}
                          rowsPerPageOptions={[5, 10, 25, 50]}
                          labelRowsPerPage="Rows per page:"
                          sx={{
                            border: "none",
                            "& .MuiTablePagination-toolbar": {
                              minHeight: 40,
                              pr: 0,
                            },
                          }}
                        />
                      </Box>

                      <Menu
                        anchorEl={actionMenuAnchor}
                        open={Boolean(actionMenuAnchor)}
                        onClose={handleActionMenuClose}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "right",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}
                        PaperProps={{
                          sx: {
                            minWidth: 180,
                            boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
                            borderRadius: "8px",
                          },
                        }}
                      >
                        <MenuItem
                          onClick={handleEditAction}
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "14px",
                            gap: 1,
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <EditIcon fontSize="small" />
                          </ListItemIcon>
                          Edit
                        </MenuItem>
                        <MenuItem
                          onClick={handleDeleteAction}
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "14px",
                            color: "#d32f2f",
                            gap: 1,
                            "&:hover": {
                              backgroundColor: "rgba(211, 47, 47, 0.08)",
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36, color: "#d32f2f" }}>
                            <DeleteIcon fontSize="small" />
                          </ListItemIcon>
                          Delete
                        </MenuItem>
                      </Menu>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Box>
          </Box>
        </Box>

        {/* Footer Buttons */}
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            right: 0,
            left: "241px",

            p: 1,
            backgroundColor: "white",
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            zIndex: 1000,
          }}
        >
          {viewOnly ? (
            <Button
              onClick={handleCancel}
              variant="contained"
              sx={{
                backgroundColor: "#2932E5",
                borderRadius: "10px",
                textTransform: "none",
                px: "14px",
                py: "5px",
                height: "36px",
                fontFamily: "Roboto, sans-serif",
                fontWeight: 500,
                fontSize: "14px",
                color: "white",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#1f28c7",
                },
              }}
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                onClick={handleCancel}
                sx={{
                  color: "#212121",
                  fontSize: "16px",
                  fontWeight: 500,
                  textTransform: "none",
                  px: 3,
                  py: 1,
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                sx={{
                  backgroundColor: "#2932E5",
                  borderRadius: "10px",
                  textTransform: "none",
                  px: "14px",
                  py: "5px",
                  height: "36px",
                  fontFamily: "Roboto, sans-serif",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "24.5px",
                  color: "white",
                  boxShadow:
                    "0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)",
                  "&:hover": {
                    backgroundColor: "#1f28c7",
                  },
                }}
              >
                {editingAlert ? "Save Changes" : "Save New Rule"}
              </Button>
            </>
          )}
        </Box>

        {/* Source Selection Dialog — close only via X, footer Cancel, or footer Apply (not backdrop / Escape) */}
        <Dialog
          open={sourceDialogOpen}
          disableEscapeKeyDown
          onClose={(event, reason) => {
            if (reason === "backdropClick" || reason === "escapeKeyDown") {
              return;
            }
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pb: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 500,
                color: "#212121",
              }}
            >
              {sourceCategory === "AI"
                ? "Cameras & zones (AI)"
                : sourceCategory === "IoT"
                  ? "IoT devices"
                  : sourceCategory === "System"
                    ? "System sources"
                    : "Sub-sources"}
            </Typography>
            <IconButton onClick={handleSourceDialogClose} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {/* Search */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                mb: 2,
              }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder="Search sources..."
                value={sourceSearchQuery}
                onChange={(e) => setSourceSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{
                          fontSize: 20,
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiInputBase-input::placeholder": {
                    color: "#a0a0a0",
                    opacity: 1,
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              />
            </Box>

            <Divider
              sx={{
                mb: 2,
              }}
            />

            {sourceCategory === "AI" && (
              <Box
                sx={{
                  mb: 3,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#212121",
                    mb: 1,
                  }}
                >
                  Cameras
                </Typography>
                {aiCamerasLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      py: 2,
                    }}
                  >
                    <CircularProgress size={22} />
                    <Typography variant="body2" color="text.secondary">
                      Loading cameras…
                    </Typography>
                  </Box>
                ) : aiCamerasError ? (
                  <FormHelperText error sx={{ mx: 0 }}>
                    {aiCamerasError}
                  </FormHelperText>
                ) : (
                  <FormGroup>
                    {filteredAiCameras.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No cameras returned from the API.
                      </Typography>
                    ) : (
                      <>
                        <FormControlLabel
                          disabled={subSourceDialogReadOnly}
                          sx={{ mb: 1, display: "flex", alignItems: "center" }}
                          control={
                            <Checkbox
                              checked={allFilteredAiCamerasSelected}
                              onChange={handleAiCamerasSelectAllToggle}
                              sx={{
                                color: "#2932E5",
                                "&.Mui-checked": {
                                  color: "#2932E5",
                                },
                              }}
                            />
                          }
                          label={
                            <Typography
                              component="span"
                              sx={{
                                fontFamily: "Roboto, sans-serif",
                                fontSize: "0.875rem",
                                fontWeight: 600,
                                color: "#212121",
                              }}
                            >
                              All
                            </Typography>
                          }
                        />
                        {filteredAiCameras.map((source) => (
                        <Box
                          key={source.id}
                          sx={{
                            mb: 2,
                          }}
                        >
                          <FormControlLabel
                            disabled={subSourceDialogReadOnly}
                            control={
                              <Checkbox
                                checked={tempSelectedSources.includes(
                                  source.id,
                                )}
                                onChange={() => handleAiCameraToggle(source.id)}
                                sx={{
                                  color: "#2932E5",
                                  "&.Mui-checked": {
                                    color: "#2932E5",
                                  },
                                }}
                              />
                            }
                            label={source.name}
                          />
                          {tempSelectedSources.includes(source.id) && (
                            <Box
                              sx={{
                                ml: 4,
                                mt: 1,
                              }}
                            >
                              {aiCameraZonesLoading[source.id] ? (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      py: 0.5,
                                    }}
                                  >
                                    <CircularProgress size={20} />
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      Loading zones…
                                    </Typography>
                                  </Box>
                                ) : getCameraZonesList(source).length > 0 ? (
                                  <FormControl fullWidth size="small">
                                    <TextField
                                      size="small"
                                      fullWidth
                                      placeholder="Select zones"
                                      value={
                                        openRoiCameraId === source.id
                                          ? formatAiCameraZoneFieldLabel(
                                              source,
                                              roiDraftZones,
                                            )
                                          : formatAiCameraZoneFieldLabel(
                                              source,
                                              cameraROISelections[
                                                source.id
                                              ] || [],
                                            )
                                      }
                                      onClick={(e) => {
                                        if (subSourceDialogReadOnly) return;
                                        setRoiZoneMenuAnchorEl(
                                          e.currentTarget,
                                        );
                                        setOpenRoiCameraId(source.id);
                                        setRoiZoneMenuSearch("");
                                        setRoiDraftZones(
                                          (
                                            cameraROISelections[
                                              source.id
                                            ] || []
                                          ).map(normalizeZoneOptionId),
                                        );
                                      }}
                                      InputProps={{
                                        readOnly: true,
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            <ArrowDropDownIcon
                                              sx={{
                                                color: "action.active",
                                                mr: 0.25,
                                              }}
                                            />
                                          </InputAdornment>
                                        ),
                                      }}
                                      sx={{
                                        backgroundColor: "white",
                                        cursor: subSourceDialogReadOnly
                                          ? "default"
                                          : "pointer",
                                        fontSize: "14px",
                                        opacity: subSourceDialogReadOnly
                                          ? 0.92
                                          : 1,
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: "8px",
                                        },
                                      }}
                                    />
                                    <Popover
                                      open={
                                        openRoiCameraId === source.id &&
                                        Boolean(roiZoneMenuAnchorEl)
                                      }
                                      anchorEl={roiZoneMenuAnchorEl}
                                      onClose={() => {
                                        setRoiZoneMenuAnchorEl(null);
                                        setOpenRoiCameraId(null);
                                        setRoiZoneMenuSearch("");
                                      }}
                                      anchorOrigin={{
                                        vertical: "bottom",
                                        horizontal: "left",
                                      }}
                                      transformOrigin={{
                                        vertical: "top",
                                        horizontal: "left",
                                      }}
                                      disableRestoreFocus
                                      PaperProps={{
                                        elevation: 8,
                                        sx: {
                                          minWidth:
                                            roiZoneMenuAnchorEl?.clientWidth ??
                                            260,
                                          maxWidth: 440,
                                          maxHeight: 420,
                                          mt: 0.5,
                                          borderRadius: "8px",
                                          overflow: "hidden",
                                        },
                                      }}
                                    >
                                      <Box
                                        sx={{ px: 1.25, pt: 1, pb: 0.75 }}
                                        onMouseDown={(e) => e.stopPropagation()}
                                      >
                                        <TextField
                                          size="small"
                                          fullWidth
                                          placeholder="Search zones…"
                                          value={roiZoneMenuSearch}
                                          onChange={(e) =>
                                            setRoiZoneMenuSearch(
                                              e.target.value,
                                            )
                                          }
                                          onClick={(e) => e.stopPropagation()}
                                          InputProps={{
                                            startAdornment: (
                                              <InputAdornment position="start">
                                                <SearchIcon
                                                  sx={{
                                                    fontSize: 18,
                                                    color: "action.active",
                                                  }}
                                                />
                                              </InputAdornment>
                                            ),
                                          }}
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              borderRadius: "8px",
                                            },
                                          }}
                                        />
                                      </Box>
                                      <Divider />
                                      {(() => {
                                        const allZoneOpts =
                                          getCameraZonesList(source);
                                        const q = roiZoneMenuSearch
                                          .trim()
                                          .toLowerCase();
                                        const filteredZoneOpts = q
                                          ? allZoneOpts.filter((z) =>
                                              z.name
                                                .toLowerCase()
                                                .includes(q),
                                            )
                                          : allZoneOpts;
                                        const allIds = allZoneOpts.map((z) =>
                                          normalizeZoneOptionId(z.id),
                                        );
                                        const draftRaw =
                                          openRoiCameraId === source.id
                                            ? roiDraftZones
                                            : cameraROISelections[
                                                source.id
                                              ] || [];
                                        const draft =
                                          draftRaw.map(normalizeZoneOptionId);
                                        const allZonesSelected =
                                          allIds.length > 0 &&
                                          allIds.every((id) =>
                                            draft.includes(id),
                                          );
                                        return (
                                          <Box
                                            sx={{
                                              maxHeight: 260,
                                              overflowY: "auto",
                                            }}
                                          >
                                            <Box
                                              onClick={() => {
                                                setRoiDraftZones((prev) => {
                                                  const p = prev.map(
                                                    normalizeZoneOptionId,
                                                  );
                                                  const allOn =
                                                    allIds.length > 0 &&
                                                    allIds.every((id) =>
                                                      p.includes(id),
                                                    );
                                                  return allOn ? [] : [...allIds];
                                                });
                                              }}
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                px: 2,
                                                py: 1,
                                                cursor: "pointer",
                                                "&:hover": {
                                                  bgcolor: "action.hover",
                                                },
                                              }}
                                            >
                                              <Checkbox
                                                checked={allZonesSelected}
                                                tabIndex={-1}
                                                disableRipple
                                                sx={{
                                                  pointerEvents: "none",
                                                  color: "#2932E5",
                                                  "&.Mui-checked": {
                                                    color: "#2932E5",
                                                  },
                                                }}
                                              />
                                              <Typography
                                                component="span"
                                                sx={{
                                                  fontWeight: 600,
                                                  fontSize: "0.875rem",
                                                }}
                                              >
                                                All
                                              </Typography>
                                            </Box>
                                            {filteredZoneOpts.length === 0 ? (
                                              <Box sx={{ px: 2, py: 1.5 }}>
                                                <Typography
                                                  variant="body2"
                                                  color="text.secondary"
                                                >
                                                  <em>No matching zones</em>
                                                </Typography>
                                              </Box>
                                            ) : (
                                              filteredZoneOpts.map((z) => {
                                                const zid =
                                                  normalizeZoneOptionId(z.id);
                                                const checked =
                                                  draft.includes(zid);
                                                return (
                                                  <Box
                                                    key={zid}
                                                    onClick={() => {
                                                      setRoiDraftZones(
                                                        (prev) => {
                                                          const p = prev.map(
                                                            normalizeZoneOptionId,
                                                          );
                                                          return p.includes(zid)
                                                            ? p.filter(
                                                                (x) =>
                                                                  x !== zid,
                                                              )
                                                            : [...p, zid];
                                                        },
                                                      );
                                                    }}
                                                    sx={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: 1,
                                                      px: 2,
                                                      py: 1,
                                                      cursor: "pointer",
                                                      "&:hover": {
                                                        bgcolor: "action.hover",
                                                      },
                                                    }}
                                                  >
                                                    <Checkbox
                                                      checked={checked}
                                                      tabIndex={-1}
                                                      disableRipple
                                                      sx={{
                                                        pointerEvents: "none",
                                                        color: "#2932E5",
                                                        "&.Mui-checked": {
                                                          color: "#2932E5",
                                                        },
                                                      }}
                                                    />
                                                    <Typography
                                                      component="span"
                                                      variant="body2"
                                                    >
                                                      {z.name}
                                                    </Typography>
                                                  </Box>
                                                );
                                              })
                                            )}
                                          </Box>
                                        );
                                      })()}
                                      <Divider />
                                      <Box
                                        data-roi-zone-actions
                                        sx={{
                                          display: "flex",
                                          justifyContent: "flex-end",
                                          gap: 1,
                                          p: 1,
                                          bgcolor: "#fafafa",
                                        }}
                                      >
                                        <Button
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setRoiZoneMenuAnchorEl(null);
                                            setOpenRoiCameraId(null);
                                            setRoiZoneMenuSearch("");
                                          }}
                                          sx={{
                                            textTransform: "none",
                                            color: "#212121",
                                          }}
                                        >
                                          Cancel
                                        </Button>
                                        <Button
                                          size="small"
                                          variant="contained"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleCameraROIChange(
                                              source.id,
                                              roiDraftZones,
                                            );
                                            setRoiZoneMenuAnchorEl(null);
                                            setOpenRoiCameraId(null);
                                            setRoiZoneMenuSearch("");
                                          }}
                                          sx={{
                                            textTransform: "none",
                                            backgroundColor: "#2932E5",
                                            boxShadow: "none",
                                            "&:hover": {
                                              backgroundColor: "#1f28c7",
                                              boxShadow: "none",
                                            },
                                          }}
                                        >
                                          Apply
                                        </Button>
                                      </Box>
                                    </Popover>
                                  </FormControl>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    No zones for this camera.
                                  </Typography>
                                )}
                              </Box>
                            )}
                        </Box>
                        ))}
                      </>
                    )}
                  </FormGroup>
                )}
              </Box>
            )}

            {sourceCategory === "IoT" && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#212121",
                    mb: 1,
                  }}
                >
                  IoT devices
                </Typography>
                <FormGroup>
                  {filteredSources
                    .filter((source) => source.type === "iot")
                    .map((source) => (
                      <FormControlLabel
                        key={source.id}
                        disabled={subSourceDialogReadOnly}
                        control={
                          <Checkbox
                            checked={tempSelectedSources.includes(source.id)}
                            onChange={() =>
                              handleIotSystemTempToggle(source.id)
                            }
                            sx={{
                              color: "#2932E5",
                              "&.Mui-checked": { color: "#2932E5" },
                            }}
                          />
                        }
                        label={source.name}
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "14px",
                          mb: 1,
                        }}
                      />
                    ))}
                </FormGroup>
              </Box>
            )}

            {sourceCategory === "System" && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#212121",
                    mb: 1,
                  }}
                >
                  System sources
                </Typography>
                <FormGroup>
                  {filteredSources
                    .filter((source) => source.type === "system")
                    .map((source) => (
                      <FormControlLabel
                        key={source.id}
                        disabled={subSourceDialogReadOnly}
                        control={
                          <Checkbox
                            checked={tempSelectedSources.includes(source.id)}
                            onChange={() =>
                              handleIotSystemTempToggle(source.id)
                            }
                            sx={{
                              color: "#2932E5",
                              "&.Mui-checked": { color: "#2932E5" },
                            }}
                          />
                        }
                        label={source.name}
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "14px",
                          mb: 1,
                        }}
                      />
                    ))}
                </FormGroup>
              </Box>
            )}
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              pb: 2,
            }}
          >
            <Button
              onClick={handleSourceDialogClose}
              sx={{
                color: "#212121",
              }}
            >
              {subSourceDialogReadOnly ? "Close" : "Cancel"}
            </Button>
            {!subSourceDialogReadOnly ? (
              <Button
                onClick={handleSourceDialogSave}
                variant="contained"
                sx={{
                  backgroundColor: "#2932E5",
                  "&:hover": {
                    backgroundColor: "#1f28c7",
                  },
                }}
              >
                Apply
              </Button>
            ) : null}
          </DialogActions>
        </Dialog>

        {/* Schedule Dialog */}
        <WeeklyScheduleDialog
          open={scheduleDialogOpen}
          onClose={handleScheduleDialogClose}
          onSave={handleScheduleDialogSave}
          initialSchedule={(() => {
            // Determine which schedule format to use
            let initialSchedule = null;

            if (schedule && schedule._dialogFormat) {
              // Use stored dialog format if available (most accurate)
              initialSchedule = schedule._dialogFormat;
              console.log("Using stored _dialogFormat for initial schedule");
            } else if (
              schedule &&
              typeof schedule === "object" &&
              !schedule.type &&
              !schedule.schedule &&
              !schedule.days &&
              !schedule.start_time
            ) {
              // Already in dialog format (object with day names like Sunday, Monday)
              // Check if it has at least one day with hours
              const hasValidDays = Object.keys(schedule).some(
                (day) =>
                  schedule[day] &&
                  typeof schedule[day] === "object" &&
                  Object.keys(schedule[day]).length > 0,
              );
              if (hasValidDays) {
                initialSchedule = schedule;
                console.log("Using schedule directly as dialog format");
              }
            }

            // If still null, try to convert from API format
            if (!initialSchedule && schedule) {
              initialSchedule = convertApiScheduleToDialogFormat(schedule);
              console.log("Converted schedule from API format:", schedule);
            }

            // If still null or invalid, return null (dialog will default to all selected)
            console.log("Final initialSchedule for dialog:", initialSchedule);
            return initialSchedule;
          })()}
        />

        {/* Edit Action Dialog */}
        <Dialog
          open={editActionDialogOpen}
          onClose={() => {
            setEditActionDialogOpen(false);
            setEditingActionCard(null);
            setEditingTriggerId(null);
            setEditingActionId(null);
          }}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "12px",
            },
          }}
        >
          <DialogTitle
            sx={{
              margin: "0px",
              lineHeight: "1.6",
              letterSpacing: "0.0075em",
              padding: "16px 24px",
              flex: "0 0 auto",
              fontFamily: "Roboto, sans-serif",
              fontSize: "20px",
              color: "rgb(255, 133, 85)",
              fontWeight: 600,
            }}
          >
            Edit Action
          </DialogTitle>
          <DialogContent>
            {editingActionCard && (
              <Box>
                {/* Action Type Selection */}
                <Box sx={{ mb: 2 }}>
                  <FormControl fullWidth size="small">
                    <Select
                      value={editingActionCard.type || ""}
                      onChange={(e) =>
                        handleEditActionCardChange("type", e.target.value)
                      }
                      displayEmpty
                      sx={{
                        backgroundColor: "white",
                        borderRadius: "10px",
                        fontSize: "14px",
                        fontFamily: "Roboto, sans-serif",
                        "& fieldset": {
                          borderColor: "#e0e0e0",
                        },
                        "& .MuiSelect-select": {
                          padding: "12px 20px",
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Select Action Type</em>
                      </MenuItem>
                      <MenuItem
                        value="Email"
                        sx={{ fontFamily: "Roboto, sans-serif" }}
                      >
                        Email
                      </MenuItem>
                      <MenuItem
                        value="Telegram"
                        sx={{ fontFamily: "Roboto, sans-serif" }}
                      >
                        Telegram
                      </MenuItem>
                      <MenuItem
                        value="HTTP"
                        sx={{ fontFamily: "Roboto, sans-serif" }}
                      >
                        HTTP
                      </MenuItem>
                      <MenuItem
                        value="SMS"
                        sx={{ fontFamily: "Roboto, sans-serif" }}
                      >
                        SMS
                      </MenuItem>
                    </Select>
                    <Typography
                      sx={{
                        color: "rgba(0, 0, 0, 0.6)",
                        fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                        fontWeight: 400,
                        fontSize: "0.75rem",
                        lineHeight: 1.66,
                        letterSpacing: "0.03333em",
                        textAlign: "left",
                        margin: 0,
                        mt: 0.5,
                      }}
                    >
                      Action Type
                    </Typography>
                  </FormControl>
                </Box>

                {editingActionCard.type &&
                editingActionCard.type !== "Email" ? (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#212121",
                        mb: 1,
                        letterSpacing: "0.15px",
                        fontFamily: "Roboto, sans-serif",
                      }}
                    >
                      Notified to
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Names or teams (comma-separated)"
                      value={editingActionCard.notifiedTo ?? ""}
                      onChange={(e) =>
                        handleEditActionCardChange("notifiedTo", e.target.value)
                      }
                      helperText="Who is notified for this action (display and reporting)"
                      FormHelperTextProps={{ sx: { mt: 0.5 } }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "white",
                          borderRadius: "10px",
                          "& fieldset": { borderColor: "#e0e0e0" },
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "14px",
                          fontFamily: "Roboto, sans-serif",
                          padding: "12px 16px",
                        },
                      }}
                    />
                  </Box>
                ) : null}

                {/* Conditional Fields Based on Action Type */}
                {editingActionCard.type === "Email" && (
                  <>
                    <Box sx={GMAIL_COMPOSE_OUTER_SX}>
                      <Box sx={gmailComposeRowSx(true)}>
                        <Typography
                          component="span"
                          sx={GMAIL_COMPOSE_LABEL_SX}
                        >
                          Notified to
                        </Typography>
                        <TextField
                          variant="standard"
                          hiddenLabel
                          fullWidth
                          placeholder="Names or teams, comma-separated"
                          value={editingActionCard.notifiedTo ?? ""}
                          onChange={(e) =>
                            handleEditActionCardChange(
                              "notifiedTo",
                              e.target.value,
                            )
                          }
                          InputProps={{ disableUnderline: true }}
                          sx={gmailComposeTextFieldSx}
                        />
                      </Box>
                      <Box sx={gmailComposeRowSx(true)}>
                        <Typography
                          component="span"
                          sx={GMAIL_COMPOSE_LABEL_SX}
                        >
                          To
                        </Typography>
                        <TextField
                          variant="standard"
                          hiddenLabel
                          fullWidth
                          placeholder="Recipients"
                          value={editingActionCard.email || ""}
                          onChange={(e) =>
                            handleEditActionCardChange("email", e.target.value)
                          }
                          error={!!emailErrors.editDialog?.email}
                          InputProps={{ disableUnderline: true }}
                          sx={gmailComposeTextFieldSx}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.1,
                            flexShrink: 0,
                          }}
                        >
                          {!(
                            editDialogEmailCcVisible ||
                            emailErrors.editDialog?.cc
                          ) && (
                            <Link
                              component="button"
                              type="button"
                              underline="none"
                              onClick={(e) => {
                                e.preventDefault();
                                setEditDialogEmailCcVisible(true);
                                requestAnimationFrame(() => {
                                  requestAnimationFrame(() => {
                                    document
                                      .getElementById("email-cc-edit-dialog")
                                      ?.focus?.();
                                  });
                                });
                              }}
                              sx={gmailComposeLinkSx}
                            >
                              Cc
                            </Link>
                          )}
                          {!(
                            editDialogEmailBccVisible ||
                            emailErrors.editDialog?.bcc
                          ) && (
                            <Link
                              component="button"
                              type="button"
                              underline="none"
                              onClick={(e) => {
                                e.preventDefault();
                                setEditDialogEmailBccVisible(true);
                                requestAnimationFrame(() => {
                                  requestAnimationFrame(() => {
                                    document
                                      .getElementById("email-bcc-edit-dialog")
                                      ?.focus?.();
                                  });
                                });
                              }}
                              sx={gmailComposeLinkSx}
                            >
                              Bcc
                            </Link>
                          )}
                        </Box>
                      </Box>
                      {(editDialogEmailCcVisible ||
                        emailErrors.editDialog?.cc) && (
                        <Box sx={gmailComposeRowSx(true)}>
                          <Typography
                            component="span"
                            sx={GMAIL_COMPOSE_LABEL_SX}
                          >
                            Cc
                          </Typography>
                          <TextField
                            id="email-cc-edit-dialog"
                            variant="standard"
                            hiddenLabel
                            fullWidth
                            placeholder="Carbon copy"
                            value={editingActionCard.cc || ""}
                            onChange={(e) =>
                              handleEditActionCardChange("cc", e.target.value)
                            }
                            error={!!emailErrors.editDialog?.cc}
                            InputProps={{ disableUnderline: true }}
                            sx={gmailComposeTextFieldSx}
                          />
                        </Box>
                      )}
                      {(editDialogEmailBccVisible ||
                        emailErrors.editDialog?.bcc) && (
                        <Box sx={gmailComposeRowSx(true)}>
                          <Typography
                            component="span"
                            sx={GMAIL_COMPOSE_LABEL_SX}
                          >
                            Bcc
                          </Typography>
                          <TextField
                            id="email-bcc-edit-dialog"
                            variant="standard"
                            hiddenLabel
                            fullWidth
                            placeholder="Blind carbon copy"
                            value={editingActionCard.bcc || ""}
                            onChange={(e) =>
                              handleEditActionCardChange("bcc", e.target.value)
                            }
                            error={!!emailErrors.editDialog?.bcc}
                            InputProps={{ disableUnderline: true }}
                            sx={gmailComposeTextFieldSx}
                          />
                        </Box>
                      )}
                      <Box sx={gmailComposeRowSx(false)}>
                        <Typography
                          component="span"
                          sx={GMAIL_COMPOSE_LABEL_SX}
                        >
                          Subject
                        </Typography>
                        <TextField
                          variant="standard"
                          hiddenLabel
                          fullWidth
                          placeholder="Subject"
                          value={editingActionCard.subject || ""}
                          onChange={(e) =>
                            handleEditActionCardChange(
                              "subject",
                              e.target.value,
                            )
                          }
                          InputProps={{ disableUnderline: true }}
                          sx={gmailComposeTextFieldSx}
                        />
                      </Box>
                    </Box>
                    {(emailErrors.editDialog?.email ||
                      emailErrors.editDialog?.cc ||
                      emailErrors.editDialog?.bcc) && (
                      <Box sx={{ mb: 1.5, pl: 0.5 }}>
                        {emailErrors.editDialog?.email && (
                          <Typography
                            component="div"
                            variant="caption"
                            color="error"
                            sx={{ display: "block" }}
                          >
                            To: {emailErrors.editDialog.email}
                          </Typography>
                        )}
                        {emailErrors.editDialog?.cc && (
                          <Typography
                            component="div"
                            variant="caption"
                            color="error"
                            sx={{ display: "block" }}
                          >
                            Cc: {emailErrors.editDialog.cc}
                          </Typography>
                        )}
                        {emailErrors.editDialog?.bcc && (
                          <Typography
                            component="div"
                            variant="caption"
                            color="error"
                            sx={{ display: "block" }}
                          >
                            Bcc: {emailErrors.editDialog.bcc}
                          </Typography>
                        )}
                      </Box>
                    )}
                    <Box sx={{ ...actionMessagePlaceholderGridEditSx, mb: 2 }}>
                      <Box sx={actionMessageBodyColumnSx}>
                        <Box
                          contentEditable
                          suppressContentEditableWarning
                          data-email-editor-id="edit-dialog"
                          ref={(el) => {
                            if (!el) return;
                            const next = messageBodyToEditorHtml(
                              editingActionCard.messageBody,
                            );
                            if (el.innerHTML !== next) el.innerHTML = next;
                          }}
                          onInput={(e) => {
                            const htmlContent = e.target.innerHTML;
                            handleEditActionCardChange(
                              "messageBody",
                              htmlContent,
                            );
                            updateEditDialogFormattingState();
                          }}
                          onBlur={(e) => {
                            const htmlContent = e.target.innerHTML;
                            handleEditActionCardChange(
                              "messageBody",
                              htmlContent,
                            );
                          }}
                          onKeyUp={() => updateEditDialogFormattingState()}
                          onMouseUp={() => updateEditDialogFormattingState()}
                          sx={{
                            flex: 1,
                            minHeight: { xs: "120px", md: 0 },
                            overflowY: "auto",
                            fontSize: "14px",
                            color: "#202124",
                            fontFamily: "Roboto, sans-serif",
                            lineHeight: "1.5",
                            outline: "none",
                            padding: "12px 16px",
                            backgroundColor: "white",
                            borderRadius: "10px",
                            border: "1px solid #e0e0e0",
                            "&:empty:before": {
                              content: '"Compose your message."',
                              color: "#5f6368",
                              opacity: 1,
                            },
                            "&:focus": {
                              outline: "none",
                              borderColor: "#2932E5",
                            },
                          }}
                        />
                      <Typography
                        sx={{
                          flexShrink: 0,
                          mt: 0.5,
                          color: "rgba(0, 0, 0, 0.6)",
                          fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                          fontWeight: 400,
                          fontSize: "0.75rem",
                          lineHeight: 1.66,
                          letterSpacing: "0.03333em",
                          textAlign: "left",
                          margin: 0,
                        }}
                      >
                        Message Body
                      </Typography>
                      {/* Gmail-style Toolbar - Below Message Body */}
                      <Box
                        sx={{
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mt: 1,
                          pt: 1,
                          borderTop: "1px solid #e0e0e0",
                        }}
                      >
                        <IconButton
                          size="small"
                          title="Attach files"
                          sx={{
                            color: "#5f6368",
                            padding: "6px",
                            "&:hover": {
                              backgroundColor: "rgba(95, 99, 104, 0.1)",
                            },
                          }}
                          onClick={() => {
                            const input = document.createElement("input");
                            input.type = "file";
                            input.multiple = true;
                            input.onchange = (e) => {
                              const files = Array.from(e.target.files);
                              console.log("Files selected:", files);
                              // Handle file attachment logic here
                            };
                            input.click();
                          }}
                        >
                          <AttachFileIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          title="Bold"
                          sx={{
                            color: editDialogFormattingState.bold
                              ? "#1976d2"
                              : "#5f6368",
                            padding: "6px",
                            backgroundColor: editDialogFormattingState.bold
                              ? "rgba(25, 118, 210, 0.1)"
                              : "transparent",
                            "&:hover": {
                              backgroundColor: editDialogFormattingState.bold
                                ? "rgba(25, 118, 210, 0.15)"
                                : "rgba(95, 99, 104, 0.1)",
                            },
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            const editor = document.querySelector(
                              `[data-email-editor-id="edit-dialog"]`,
                            );
                            if (editor) {
                              editor.focus();
                              document.execCommand("bold", false, null);
                              setTimeout(() => {
                                const htmlContent = editor.innerHTML;
                                handleEditActionCardChange(
                                  "messageBody",
                                  htmlContent,
                                );
                                updateEditDialogFormattingState();
                              }, 0);
                            }
                          }}
                        >
                          <FormatBoldIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          title="Italic"
                          sx={{
                            color: editDialogFormattingState.italic
                              ? "#1976d2"
                              : "#5f6368",
                            padding: "6px",
                            backgroundColor: editDialogFormattingState.italic
                              ? "rgba(25, 118, 210, 0.1)"
                              : "transparent",
                            "&:hover": {
                              backgroundColor: editDialogFormattingState.italic
                                ? "rgba(25, 118, 210, 0.15)"
                                : "rgba(95, 99, 104, 0.1)",
                            },
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            const editor = document.querySelector(
                              `[data-email-editor-id="edit-dialog"]`,
                            );
                            if (editor) {
                              editor.focus();
                              document.execCommand("italic", false, null);
                              setTimeout(() => {
                                const htmlContent = editor.innerHTML;
                                handleEditActionCardChange(
                                  "messageBody",
                                  htmlContent,
                                );
                                updateEditDialogFormattingState();
                              }, 0);
                            }
                          }}
                        >
                          <FormatItalicIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          title="Underline"
                          sx={{
                            color: editDialogFormattingState.underline
                              ? "#1976d2"
                              : "#5f6368",
                            padding: "6px",
                            backgroundColor: editDialogFormattingState.underline
                              ? "rgba(25, 118, 210, 0.1)"
                              : "transparent",
                            "&:hover": {
                              backgroundColor:
                                editDialogFormattingState.underline
                                  ? "rgba(25, 118, 210, 0.15)"
                                  : "rgba(95, 99, 104, 0.1)",
                            },
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            const editor = document.querySelector(
                              `[data-email-editor-id="edit-dialog"]`,
                            );
                            if (editor) {
                              editor.focus();
                              document.execCommand("underline", false, null);
                              setTimeout(() => {
                                const htmlContent = editor.innerHTML;
                                handleEditActionCardChange(
                                  "messageBody",
                                  htmlContent,
                                );
                                updateEditDialogFormattingState();
                              }, 0);
                            }
                          }}
                        >
                          <FormatUnderlinedIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      </Box>
                      <Box sx={actionPlaceholderColumnEditSx}>
                        {renderMessagePlaceholderPicker({
                          title:
                            "Click a link below to insert a placeholder into your email:",
                          onPickToken: insertEditDialogEmailShortcode,
                          maxHeight: { xs: 240, md: 320 },
                          scroll: true,
                          stretchList: true,
                        })}
                      </Box>
                    </Box>
                  </>
                )}

                {editingActionCard.type === "Telegram" && (
                  <>
                    {/* Get Chat ID Link Section */}
                    <Box sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          backgroundColor: "white",
                          borderRadius: "10px",
                          border: "1px solid #e0e0e0",
                          p: 2,
                          mb: 2,
                          display: "flex",
                          flexDirection: "row",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: 1.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "#4a5565",
                            m: 0,
                            lineHeight: 1.5,
                          }}
                        >
                          Click the link to get your Chat ID from the Telegram
                          bot:
                        </Typography>
                        <Link
                          href="https://t.me/allvision_alert_bot?start=register"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            fontSize: "14px",
                            color: "#2932E5",
                            textDecoration: "none",
                            fontWeight: 500,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            wordBreak: "break-all",
                            "&:hover": {
                              textDecoration: "underline",
                              color: "#1f28c7",
                            },
                          }}
                        >
                          https://t.me/allvision_alert_bot?start=register
                        </Link>
                      </Box>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter chat IDs (comma-separated)"
                        value={editingActionCard.chatId || ""}
                        onChange={(e) =>
                          handleEditActionCardChange("chatId", e.target.value)
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "white",
                            borderRadius: "10px",
                            "& fieldset": {
                              borderColor: "#e0e0e0",
                            },
                          },
                          "& .MuiInputBase-input": {
                            fontSize: "14px",
                            fontFamily: "Roboto, sans-serif",
                            padding: "12px 16px",
                          },
                        }}
                      />
                      <Typography
                        sx={{
                          color: "rgba(0, 0, 0, 0.6)",
                          fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                          fontWeight: 400,
                          fontSize: "0.75rem",
                          lineHeight: 1.66,
                          letterSpacing: "0.03333em",
                          textAlign: "left",
                          margin: 0,
                        }}
                      >
                        Chat ID
                      </Typography>
                    </Box>
                    <Box sx={{ ...actionMessagePlaceholderGridEditSx, mb: 2 }}>
                      <Box sx={actionMessageBodyColumnSx}>
                        <Box
                          sx={{
                            border: "1px solid #e0e0e0",
                            borderRadius: "10px",
                            backgroundColor: "white",
                            overflow: "hidden",
                            flex: 1,
                            minHeight: { md: 0 },
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: 0.5,
                              p: 1,
                              borderBottom: "1px solid #e0e0e0",
                              backgroundColor: "#fafafa",
                            }}
                          >
                            <IconButton
                              size="small"
                              title="Attach files"
                              sx={{
                                color: "#5f6368",
                                padding: "6px",
                                "&:hover": {
                                  backgroundColor: "rgba(95, 99, 104, 0.1)",
                                },
                              }}
                              onClick={() => {
                                const input = document.createElement("input");
                                input.type = "file";
                                input.multiple = true;
                                input.onchange = (e) => {
                                  const files = Array.from(
                                    e.target.files || [],
                                  );
                                  if (files.length)
                                    console.log("Files selected:", files);
                                };
                                input.click();
                              }}
                            >
                              <AttachFileIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              title="Bold"
                              sx={{
                                color: editDialogFormattingState.bold
                                  ? "#1976d2"
                                  : "#5f6368",
                                padding: "6px",
                                backgroundColor: editDialogFormattingState.bold
                                  ? "rgba(25, 118, 210, 0.1)"
                                  : "transparent",
                                "&:hover": {
                                  backgroundColor:
                                    editDialogFormattingState.bold
                                      ? "rgba(25, 118, 210, 0.15)"
                                      : "rgba(95, 99, 104, 0.1)",
                                },
                              }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                const editor = document.querySelector(
                                  `[data-email-editor-id="edit-dialog"]`,
                                );
                                if (editor) {
                                  editor.focus();
                                  document.execCommand("bold", false, null);
                                  setTimeout(() => {
                                    handleEditActionCardChange(
                                      "messageBody",
                                      editor.innerHTML,
                                    );
                                    updateEditDialogFormattingState();
                                  }, 0);
                                }
                              }}
                            >
                              <FormatBoldIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              title="Italic"
                              sx={{
                                color: editDialogFormattingState.italic
                                  ? "#1976d2"
                                  : "#5f6368",
                                padding: "6px",
                                backgroundColor: editDialogFormattingState.italic
                                  ? "rgba(25, 118, 210, 0.1)"
                                  : "transparent",
                                "&:hover": {
                                  backgroundColor:
                                    editDialogFormattingState.italic
                                      ? "rgba(25, 118, 210, 0.15)"
                                      : "rgba(95, 99, 104, 0.1)",
                                },
                              }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                const editor = document.querySelector(
                                  `[data-email-editor-id="edit-dialog"]`,
                                );
                                if (editor) {
                                  editor.focus();
                                  document.execCommand("italic", false, null);
                                  setTimeout(() => {
                                    handleEditActionCardChange(
                                      "messageBody",
                                      editor.innerHTML,
                                    );
                                    updateEditDialogFormattingState();
                                  }, 0);
                                }
                              }}
                            >
                              <FormatItalicIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              title="Underline"
                              sx={{
                                color: editDialogFormattingState.underline
                                  ? "#1976d2"
                                  : "#5f6368",
                                padding: "6px",
                                backgroundColor:
                                  editDialogFormattingState.underline
                                    ? "rgba(25, 118, 210, 0.1)"
                                    : "transparent",
                                "&:hover": {
                                  backgroundColor:
                                    editDialogFormattingState.underline
                                      ? "rgba(25, 118, 210, 0.15)"
                                      : "rgba(95, 99, 104, 0.1)",
                                },
                              }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                const editor = document.querySelector(
                                  `[data-email-editor-id="edit-dialog"]`,
                                );
                                if (editor) {
                                  editor.focus();
                                  document.execCommand(
                                    "underline",
                                    false,
                                    null,
                                  );
                                  setTimeout(() => {
                                    handleEditActionCardChange(
                                      "messageBody",
                                      editor.innerHTML,
                                    );
                                    updateEditDialogFormattingState();
                                  }, 0);
                                }
                              }}
                            >
                              <FormatUnderlinedIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          <Box
                            contentEditable
                            suppressContentEditableWarning
                            data-email-editor-id="edit-dialog"
                            ref={(el) => {
                              if (!el) return;
                              const next = messageBodyToEditorHtml(
                                editingActionCard.messageBody,
                              );
                              if (el.innerHTML !== next) el.innerHTML = next;
                            }}
                            onInput={(e) => {
                              const htmlContent = e.target.innerHTML;
                              handleEditActionCardChange(
                                "messageBody",
                                htmlContent,
                              );
                              updateEditDialogFormattingState();
                            }}
                            onBlur={(e) => {
                              handleEditActionCardChange(
                                "messageBody",
                                e.target.innerHTML,
                              );
                            }}
                            onKeyUp={() => updateEditDialogFormattingState()}
                            onMouseUp={() => updateEditDialogFormattingState()}
                            sx={{
                              flex: 1,
                              minHeight: { xs: 160, md: 0 },
                              fontSize: "14px",
                              color: "#202124",
                              fontFamily: "Roboto, sans-serif",
                              lineHeight: 1.5,
                              outline: "none",
                              p: 2,
                              "&:empty:before": {
                                content: '"Enter your message."',
                                color: "#5f6368",
                                opacity: 1,
                              },
                            }}
                          />
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 0.5,
                            color: "rgba(0, 0, 0, 0.6)",
                          }}
                        >
                          Message to be sent via Telegram
                        </Typography>
                      </Box>
                      <Box sx={actionPlaceholderColumnEditSx}>
                        {renderMessagePlaceholderPicker({
                          title:
                            "Click a link below to insert a placeholder into your message:",
                          onPickToken: insertEditDialogEmailShortcode,
                          maxHeight: { xs: 240, md: 320 },
                          scroll: true,
                          stretchList: true,
                        })}
                      </Box>
                    </Box>
                  </>
                )}

                {editingActionCard.type === "SMS" && (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <FormControl fullWidth size="small">
                        <Select
                          value={editingActionCard.countryCode || "+1"}
                          onChange={(e) =>
                            handleEditActionCardChange(
                              "countryCode",
                              e.target.value,
                            )
                          }
                          sx={{
                            backgroundColor: "white",
                            borderRadius: "10px",
                            fontFamily: "Roboto, sans-serif",
                            "& fieldset": {
                              borderColor: "#e0e0e0",
                            },
                          }}
                        >
                          <MenuItem
                            value="+1"
                            sx={{ fontFamily: "Roboto, sans-serif" }}
                          >
                            +1 (US/Canada)
                          </MenuItem>
                          <MenuItem
                            value="+91"
                            sx={{ fontFamily: "Roboto, sans-serif" }}
                          >
                            +91 (India)
                          </MenuItem>
                          <MenuItem
                            value="+44"
                            sx={{ fontFamily: "Roboto, sans-serif" }}
                          >
                            +44 (UK)
                          </MenuItem>
                          <MenuItem
                            value="+86"
                            sx={{ fontFamily: "Roboto, sans-serif" }}
                          >
                            +86 (China)
                          </MenuItem>
                          <MenuItem
                            value="+81"
                            sx={{ fontFamily: "Roboto, sans-serif" }}
                          >
                            +81 (Japan)
                          </MenuItem>
                          <MenuItem
                            value="+49"
                            sx={{ fontFamily: "Roboto, sans-serif" }}
                          >
                            +49 (Germany)
                          </MenuItem>
                          <MenuItem
                            value="+33"
                            sx={{ fontFamily: "Roboto, sans-serif" }}
                          >
                            +33 (France)
                          </MenuItem>
                          <MenuItem
                            value="+39"
                            sx={{ fontFamily: "Roboto, sans-serif" }}
                          >
                            +39 (Italy)
                          </MenuItem>
                          <MenuItem
                            value="+34"
                            sx={{ fontFamily: "Roboto, sans-serif" }}
                          >
                            +34 (Spain)
                          </MenuItem>
                          <MenuItem
                            value="+61"
                            sx={{ fontFamily: "Roboto, sans-serif" }}
                          >
                            +61 (Australia)
                          </MenuItem>
                          <MenuItem
                            value="+55"
                            sx={{ fontFamily: "Roboto, sans-serif" }}
                          >
                            +55 (Brazil)
                          </MenuItem>
                          <MenuItem
                            value="+7"
                            sx={{ fontFamily: "Roboto, sans-serif" }}
                          >
                            +7 (Russia)
                          </MenuItem>
                          <MenuItem
                            value="+82"
                            sx={{ fontFamily: "Roboto, sans-serif" }}
                          >
                            +82 (South Korea)
                          </MenuItem>
                          <MenuItem
                            value="+65"
                            sx={{ fontFamily: "Roboto, sans-serif" }}
                          >
                            +65 (Singapore)
                          </MenuItem>
                          <MenuItem
                            value="+971"
                            sx={{ fontFamily: "Roboto, sans-serif" }}
                          >
                            +971 (UAE)
                          </MenuItem>
                        </Select>
                      </FormControl>
                      <Typography
                        sx={{
                          color: "rgba(0, 0, 0, 0.6)",
                          fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                          fontWeight: 400,
                          fontSize: "0.75rem",
                          lineHeight: 1.66,
                          letterSpacing: "0.03333em",
                          textAlign: "left",
                          margin: 0,
                        }}
                      >
                        Country Code
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter phone numbers (comma-separated)"
                        value={editingActionCard.chatId || ""}
                        onChange={(e) =>
                          handleEditActionCardChange("chatId", e.target.value)
                        }
                        error={!!smsErrors.editDialog?.phoneNumber}
                        helperText={
                          smsErrors.editDialog?.phoneNumber ||
                          "Enter phone numbers without country code"
                        }
                        FormHelperTextProps={{
                          sx: {
                            color: smsErrors.editDialog?.phoneNumber
                              ? "#d32f2f"
                              : "rgba(0, 0, 0, 0.6)",
                          },
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "white",
                            borderRadius: "10px",
                            "& fieldset": {
                              borderColor: smsErrors.editDialog?.phoneNumber
                                ? "#d32f2f"
                                : "#e0e0e0",
                            },
                          },
                          "& .MuiInputBase-input": {
                            fontSize: "14px",
                            fontFamily: "Roboto, sans-serif",
                            padding: "12px 16px",
                          },
                        }}
                      />
                      <Typography
                        sx={{
                          color: "rgba(0, 0, 0, 0.6)",
                          fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                          fontWeight: 400,
                          fontSize: "0.75rem",
                          lineHeight: 1.66,
                          letterSpacing: "0.03333em",
                          textAlign: "left",
                          margin: 0,
                        }}
                      >
                        Phone Number
                      </Typography>
                    </Box>
                    <Box sx={{ ...actionMessagePlaceholderGridEditSx, mb: 2 }}>
                      <Box sx={actionMessageBodyColumnSx}>
                        <Box
                          sx={{
                            border:
                              smsErrors.editDialog?.messageBody ||
                              plainTextLengthFromMessageHtml(
                                editingActionCard.messageBody || "",
                              ) > 160
                                ? "1px solid #d32f2f"
                                : "1px solid #e0e0e0",
                            borderRadius: "10px",
                            backgroundColor: "white",
                            overflow: "hidden",
                            flex: 1,
                            minHeight: { md: 0 },
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: 0.5,
                              p: 1,
                              borderBottom: "1px solid #e0e0e0",
                              backgroundColor: "#fafafa",
                            }}
                          >
                            <IconButton
                              size="small"
                              title="Attach files"
                              sx={{
                                color: "#5f6368",
                                padding: "6px",
                                "&:hover": {
                                  backgroundColor: "rgba(95, 99, 104, 0.1)",
                                },
                              }}
                              onClick={() => {
                                const input = document.createElement("input");
                                input.type = "file";
                                input.multiple = true;
                                input.onchange = (e) => {
                                  const files = Array.from(
                                    e.target.files || [],
                                  );
                                  if (files.length)
                                    console.log("Files selected:", files);
                                };
                                input.click();
                              }}
                            >
                              <AttachFileIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              title="Bold"
                              sx={{
                                color: editDialogFormattingState.bold
                                  ? "#1976d2"
                                  : "#5f6368",
                                padding: "6px",
                                backgroundColor: editDialogFormattingState.bold
                                  ? "rgba(25, 118, 210, 0.1)"
                                  : "transparent",
                                "&:hover": {
                                  backgroundColor:
                                    editDialogFormattingState.bold
                                      ? "rgba(25, 118, 210, 0.15)"
                                      : "rgba(95, 99, 104, 0.1)",
                                },
                              }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                const editor = document.querySelector(
                                  `[data-email-editor-id="edit-dialog"]`,
                                );
                                if (editor) {
                                  editor.focus();
                                  document.execCommand("bold", false, null);
                                  setTimeout(() => {
                                    handleEditActionCardChange(
                                      "messageBody",
                                      editor.innerHTML,
                                    );
                                    updateEditDialogFormattingState();
                                  }, 0);
                                }
                              }}
                            >
                              <FormatBoldIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              title="Italic"
                              sx={{
                                color: editDialogFormattingState.italic
                                  ? "#1976d2"
                                  : "#5f6368",
                                padding: "6px",
                                backgroundColor: editDialogFormattingState.italic
                                  ? "rgba(25, 118, 210, 0.1)"
                                  : "transparent",
                                "&:hover": {
                                  backgroundColor:
                                    editDialogFormattingState.italic
                                      ? "rgba(25, 118, 210, 0.15)"
                                      : "rgba(95, 99, 104, 0.1)",
                                },
                              }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                const editor = document.querySelector(
                                  `[data-email-editor-id="edit-dialog"]`,
                                );
                                if (editor) {
                                  editor.focus();
                                  document.execCommand("italic", false, null);
                                  setTimeout(() => {
                                    handleEditActionCardChange(
                                      "messageBody",
                                      editor.innerHTML,
                                    );
                                    updateEditDialogFormattingState();
                                  }, 0);
                                }
                              }}
                            >
                              <FormatItalicIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              title="Underline"
                              sx={{
                                color: editDialogFormattingState.underline
                                  ? "#1976d2"
                                  : "#5f6368",
                                padding: "6px",
                                backgroundColor:
                                  editDialogFormattingState.underline
                                    ? "rgba(25, 118, 210, 0.1)"
                                    : "transparent",
                                "&:hover": {
                                  backgroundColor:
                                    editDialogFormattingState.underline
                                      ? "rgba(25, 118, 210, 0.15)"
                                      : "rgba(95, 99, 104, 0.1)",
                                },
                              }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                const editor = document.querySelector(
                                  `[data-email-editor-id="edit-dialog"]`,
                                );
                                if (editor) {
                                  editor.focus();
                                  document.execCommand(
                                    "underline",
                                    false,
                                    null,
                                  );
                                  setTimeout(() => {
                                    handleEditActionCardChange(
                                      "messageBody",
                                      editor.innerHTML,
                                    );
                                    updateEditDialogFormattingState();
                                  }, 0);
                                }
                              }}
                            >
                              <FormatUnderlinedIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          <Box
                            contentEditable
                            suppressContentEditableWarning
                            data-email-editor-id="edit-dialog"
                            ref={(el) => {
                              if (!el) return;
                              const next = messageBodyToEditorHtml(
                                editingActionCard.messageBody,
                              );
                              if (el.innerHTML !== next) el.innerHTML = next;
                            }}
                            onInput={(e) => {
                              const htmlContent = e.target.innerHTML;
                              handleEditActionCardChange(
                                "messageBody",
                                htmlContent,
                              );
                              updateEditDialogFormattingState();
                            }}
                            onBlur={(e) => {
                              handleEditActionCardChange(
                                "messageBody",
                                e.target.innerHTML,
                              );
                            }}
                            onKeyUp={() => updateEditDialogFormattingState()}
                            onMouseUp={() => updateEditDialogFormattingState()}
                            sx={{
                              flex: 1,
                              minHeight: { xs: 160, md: 0 },
                              fontSize: "14px",
                              color: "#202124",
                              fontFamily: "Roboto, sans-serif",
                              lineHeight: 1.5,
                              outline: "none",
                              p: 2,
                              "&:empty:before": {
                                content: '"Enter SMS message."',
                                color: "#5f6368",
                                opacity: 1,
                              },
                            }}
                          />
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 0.5,
                            color:
                              smsErrors.editDialog?.messageBody ||
                              plainTextLengthFromMessageHtml(
                                editingActionCard.messageBody || "",
                              ) > 160
                                ? "#d32f2f"
                                : "rgba(0, 0, 0, 0.6)",
                          }}
                        >
                          {smsErrors.editDialog?.messageBody ||
                            (plainTextLengthFromMessageHtml(
                              editingActionCard.messageBody || "",
                            ) > 160
                              ? "Message body must be 160 characters or less"
                              : `${plainTextLengthFromMessageHtml(
                                  editingActionCard.messageBody || "",
                                )}/160 characters`)}
                        </Typography>
                      </Box>
                      <Box sx={actionPlaceholderColumnEditSx}>
                        {renderMessagePlaceholderPicker({
                          title:
                            "Click a link below to insert a placeholder into your message:",
                          onPickToken: insertEditDialogEmailShortcode,
                          maxHeight: { xs: 240, md: 320 },
                          scroll: true,
                          stretchList: true,
                        })}
                      </Box>
                    </Box>
                  </>
                )}

                {editingActionCard.type === "HTTP" && (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="https://example.com/webhook"
                        value={editingActionCard.chatId || ""}
                        onChange={(e) =>
                          handleEditActionCardChange("chatId", e.target.value)
                        }
                        error={!!httpErrors.editDialog?.url}
                        helperText={
                          httpErrors.editDialog?.url ||
                          "Enter a valid webhook URL (e.g., https://example.com/webhook)"
                        }
                        FormHelperTextProps={{
                          sx: {
                            color: httpErrors.editDialog?.url
                              ? "#d32f2f"
                              : "rgba(0, 0, 0, 0.6)",
                          },
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "white",
                            borderRadius: "10px",
                            "& fieldset": {
                              borderColor: httpErrors.editDialog?.url
                                ? "#d32f2f"
                                : "#e0e0e0",
                            },
                          },
                          "& .MuiInputBase-input": {
                            fontSize: "14px",
                            fontFamily: "Roboto, sans-serif",
                            padding: "12px 16px",
                          },
                        }}
                      />
                      <Typography
                        sx={{
                          color: "rgba(0, 0, 0, 0.6)",
                          fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                          fontWeight: 400,
                          fontSize: "0.75rem",
                          lineHeight: 1.66,
                          letterSpacing: "0.03333em",
                          textAlign: "left",
                          margin: 0,
                        }}
                      >
                        Endpoint URL
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 400,
                          fontFamily: "Roboto, sans-serif",
                          lineHeight: "1.5",
                          letterSpacing: "0.00938em",
                          color: "rgba(74, 85,101)",
                          mb: 1,
                        }}
                      >
                        HTTP Method
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={editingActionCard.httpMethod || "POST"}
                          onChange={(e) =>
                            handleEditActionCardChange(
                              "httpMethod",
                              e.target.value,
                            )
                          }
                          sx={{
                            backgroundColor: "white",
                            borderRadius: "10px",
                            fontFamily: "Roboto, sans-serif",
                            "& fieldset": {
                              borderColor: "#e0e0e0",
                            },
                          }}
                        >
                          <MenuItem
                            value="POST"
                            sx={{ fontFamily: "Roboto, sans-serif" }}
                          >
                            POST
                          </MenuItem>
                          <MenuItem
                            value="GET"
                            sx={{ fontFamily: "Roboto, sans-serif" }}
                          >
                            GET
                          </MenuItem>
                          <MenuItem
                            value="PUT"
                            sx={{ fontFamily: "Roboto, sans-serif" }}
                          >
                            PUT
                          </MenuItem>
                          <MenuItem
                            value="DELETE"
                            sx={{ fontFamily: "Roboto, sans-serif" }}
                          >
                            DELETE
                          </MenuItem>
                          <MenuItem
                            value="PATCH"
                            sx={{ fontFamily: "Roboto, sans-serif" }}
                          >
                            PATCH
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 400,
                          fontFamily: "Roboto, sans-serif",
                          lineHeight: "1.5",
                          letterSpacing: "0.00938em",
                          color: "rgba(74, 85,101)",
                          mb: 1,
                        }}
                      >
                        Message Body
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="Enter request body"
                        value={editingActionCard.messageBody || ""}
                        onChange={(e) =>
                          handleEditActionCardChange(
                            "messageBody",
                            e.target.value,
                          )
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "white",
                            borderRadius: "10px",
                            "& fieldset": {
                              borderColor: "#e0e0e0",
                            },
                          },
                          "& .MuiInputBase-input": {
                            fontSize: "14px",
                            fontFamily: "Roboto, sans-serif",
                            padding: "12px 16px",
                          },
                        }}
                      />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 400,
                          fontFamily: "Roboto, sans-serif",
                          lineHeight: "1.5",
                          letterSpacing: "0.00938em",
                          color: "rgba(74, 85,101)",
                          mb: 1,
                        }}
                      >
                        Header Key
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="X-Custom-Header"
                        value={editingActionCard.httpHeaderKey || ""}
                        onChange={(e) =>
                          handleEditActionCardChange(
                            "httpHeaderKey",
                            e.target.value,
                          )
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "white",
                            borderRadius: "10px",
                            "& fieldset": {
                              borderColor: "#e0e0e0",
                            },
                          },
                          "& .MuiInputBase-input": {
                            fontSize: "14px",
                            fontFamily: "Roboto, sans-serif",
                            padding: "12px 16px",
                          },
                        }}
                      />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 400,
                          fontFamily: "Roboto, sans-serif",
                          lineHeight: "1.5",
                          letterSpacing: "0.00938em",
                          color: "rgba(74, 85,101)",
                          mb: 1,
                        }}
                      >
                        Header Value
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Header value"
                        value={editingActionCard.httpHeaderValue || ""}
                        onChange={(e) =>
                          handleEditActionCardChange(
                            "httpHeaderValue",
                            e.target.value,
                          )
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "white",
                            borderRadius: "10px",
                            "& fieldset": {
                              borderColor: "#e0e0e0",
                            },
                          },
                          "& .MuiInputBase-input": {
                            fontSize: "14px",
                            fontFamily: "Roboto, sans-serif",
                            padding: "12px 16px",
                          },
                        }}
                      />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 400,
                          fontFamily: "Roboto, sans-serif",
                          lineHeight: "1.5",
                          letterSpacing: "0.00938em",
                          color: "rgba(74, 85,101)",
                          mb: 1,
                        }}
                      >
                        Param Key
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Query parameter key"
                        value={editingActionCard.httpParamKey || ""}
                        onChange={(e) =>
                          handleEditActionCardChange(
                            "httpParamKey",
                            e.target.value,
                          )
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "white",
                            borderRadius: "10px",
                            "& fieldset": {
                              borderColor: "#e0e0e0",
                            },
                          },
                          "& .MuiInputBase-input": {
                            fontSize: "14px",
                            fontFamily: "Roboto, sans-serif",
                            padding: "12px 16px",
                          },
                        }}
                      />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 400,
                          fontFamily: "Roboto, sans-serif",
                          lineHeight: "1.5",
                          letterSpacing: "0.00938em",
                          color: "rgba(74, 85,101)",
                          mb: 1,
                        }}
                      >
                        Param Value
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Query parameter value"
                        value={editingActionCard.httpParamValue || ""}
                        onChange={(e) =>
                          handleEditActionCardChange(
                            "httpParamValue",
                            e.target.value,
                          )
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "white",
                            borderRadius: "10px",
                            "& fieldset": {
                              borderColor: "#e0e0e0",
                            },
                          },
                          "& .MuiInputBase-input": {
                            fontSize: "14px",
                            fontFamily: "Roboto, sans-serif",
                            padding: "12px 16px",
                          },
                        }}
                      />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 400,
                          fontFamily: "Roboto, sans-serif",
                          lineHeight: "1.5",
                          letterSpacing: "0.00938em",
                          color: "rgba(74, 85,101)",
                          mb: 1,
                        }}
                      >
                        Authorization Type
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={editingActionCard.authType || "No Auth"}
                          onChange={(e) =>
                            handleEditActionCardChange(
                              "authType",
                              e.target.value,
                            )
                          }
                          sx={{
                            backgroundColor: "white",
                            borderRadius: "10px",
                            fontFamily: "Roboto, sans-serif",
                            "& fieldset": {
                              borderColor: "#e0e0e0",
                            },
                          }}
                        >
                          <MenuItem
                            value="No Auth"
                            sx={{ fontFamily: "Roboto, sans-serif" }}
                          >
                            No Auth
                          </MenuItem>
                          <MenuItem
                            value="Bearer"
                            sx={{ fontFamily: "Roboto, sans-serif" }}
                          >
                            Bearer Token
                          </MenuItem>
                          <MenuItem
                            value="Basic"
                            sx={{ fontFamily: "Roboto, sans-serif" }}
                          >
                            Basic Auth
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    {editingActionCard.authType === "Bearer" && (
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: 400,
                            fontFamily: "Roboto, sans-serif",
                            lineHeight: "1.5",
                            letterSpacing: "0.00938em",
                            color: "rgba(74, 85,101)",
                            mb: 1,
                          }}
                        >
                          Token
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="password"
                          placeholder="Enter Bearer token"
                          value={
                            editingActionCard.authToken ||
                            editingActionCard.authAccount ||
                            ""
                          }
                          onChange={(e) =>
                            handleEditActionCardChange(
                              "authToken",
                              e.target.value,
                            )
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "white",
                              borderRadius: "10px",
                              "& fieldset": {
                                borderColor: "#e0e0e0",
                              },
                            },
                            "& .MuiInputBase-input": {
                              fontSize: "14px",
                              fontFamily: "Roboto, sans-serif",
                              padding: "12px 16px",
                            },
                          }}
                        />
                      </Box>
                    )}
                    {editingActionCard.authType === "Basic" && (
                      <>
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 400,
                              fontFamily: "Roboto, sans-serif",
                              lineHeight: "1.5",
                              letterSpacing: "0.00938em",
                              color: "rgba(74, 85,101)",
                              mb: 1,
                            }}
                          >
                            Account
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Username"
                            value={editingActionCard.authAccount || ""}
                            onChange={(e) =>
                              handleEditActionCardChange(
                                "authAccount",
                                e.target.value,
                              )
                            }
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: "white",
                                borderRadius: "10px",
                                "& fieldset": {
                                  borderColor: "#e0e0e0",
                                },
                              },
                              "& .MuiInputBase-input": {
                                fontSize: "14px",
                                fontFamily: "Roboto, sans-serif",
                                padding: "12px 16px",
                              },
                            }}
                          />
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 400,
                              fontFamily: "Roboto, sans-serif",
                              lineHeight: "1.5",
                              letterSpacing: "0.00938em",
                              color: "rgba(74, 85,101)",
                              mb: 1,
                            }}
                          >
                            Password
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            type="password"
                            placeholder="Password"
                            value={editingActionCard.authPassword || ""}
                            onChange={(e) =>
                              handleEditActionCardChange(
                                "authPassword",
                                e.target.value,
                              )
                            }
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: "white",
                                borderRadius: "10px",
                                "& fieldset": {
                                  borderColor: "#e0e0e0",
                                },
                              },
                              "& .MuiInputBase-input": {
                                fontSize: "14px",
                                fontFamily: "Roboto, sans-serif",
                                padding: "12px 16px",
                              },
                            }}
                          />
                        </Box>
                      </>
                    )}
                  </>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions
            sx={{
              padding: "16px 24px",
              gap: 1,
            }}
          >
            <Button
              onClick={() => {
                setEditActionDialogOpen(false);
                setEditingActionCard(null);
                setEditingTriggerId(null);
                setEditingActionId(null);
              }}
              sx={{
                fontFamily: "Roboto, sans-serif",
                textTransform: "none",
                color: "#4A5565",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEditedAction}
              variant="contained"
              sx={{
                fontFamily: "Roboto, sans-serif",
                backgroundColor: "#2932E5",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1f28c7",
                },
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmDialogOpen}
          onClose={() => {
            setDeleteConfirmDialogOpen(false);
            setActionToDelete(null);
          }}
          PaperProps={{
            sx: {
              backgroundColor: "rgb(255, 255, 255)",
              color: "rgba(0, 0, 0, 0.87)",
              boxShadow:
                "rgba(0, 0, 0, 0.2) 0px 11px 15px -7px, rgba(0, 0, 0, 0.14) 0px 24px 38px 3px, rgba(0, 0, 0, 0.12) 0px 9px 46px 8px",
              position: "relative",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              maxHeight: "calc(100% - 64px)",
              maxWidth: "600px",
              width: "calc(100% - 64px)",
              transition: "box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1)",
              borderRadius: "4px",
              margin: "32px",
            },
          }}
        >
          <DialogTitle
            sx={{
              fontFamily: "Roboto, sans-serif",
              margin: "0px",
              color: "#D32F2F",
              fontWeight: 500,
              fontSize: "1.25rem",
              lineHeight: "1.6",
              letterSpacing: "0.0075em",
              padding: "16px 24px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            Confirm Delete
          </DialogTitle>
          <DialogContent
            sx={{
              fontFamily: "Roboto, sans-serif",
              margin: "0px 0px 16px",
              fontWeight: 400,
              fontSize: "1rem",
              lineHeight: "1.5",
              letterSpacing: "0.00938em",
              color: "rgba(0, 0, 0, 0.6)",
            }}
          >
            Are you sure you want to delete action "
            {actionToDelete?.type || actionToDelete?.payload || "this action"}"?
          </DialogContent>
          <DialogActions
            sx={{
              padding: "16px 24px",
              gap: 1,
            }}
          >
            <Button
              onClick={() => {
                setDeleteConfirmDialogOpen(false);
                setActionToDelete(null);
              }}
              sx={{
                fontFamily: "Roboto, sans-serif",
                textTransform: "none",
                color: "#4A5565",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDeleteAction}
              variant="contained"
              sx={{
                fontFamily: "Roboto, sans-serif",
                backgroundColor: "#d32f2f",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#c62828",
                },
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
